/**
 * O escritório 2D — o mundo navegável, desenhado com Phaser em pixel-art.
 *
 * Vive fora do React de propósito. Um jogo a 60fps que provocasse um render do
 * React a cada frame seria a receita para engasgar a aba inteira; por isso o
 * Phaser possui o canvas e o loop, e o React só o monta e o desmonta. A ponte
 * com o resto da app é de sentido único e sem estado de render: leio o store
 * imperativamente (`useStore.getState()`) dentro do `update`, nunca por hook.
 *
 * O aspecto é o de um jogo 2D de 16 bits (à Gather), mas com uma verdadeira
 * PLANTA DE EMPRESA: sete divisões (dois escritórios, sala de reunião, copa,
 * sala de descanso, recepção) ligadas por um corredor central. Cada sala tem o
 * seu chão, a sua mobília e a sua LUZ. Nada disto vem de ficheiros de imagem:
 * cada tile e cada sprite nasce desenhado por código em `officeArt.ts`
 * (clean-room, zero assets de terceiros, zero bytes no bundle). A geometria e a
 * colisão são o contrato partilhado de `space.ts` — aqui só se veste e se ilumina.
 *
 * Áudio por sala (o que o botão "Escritório" desbloqueia): à medida que o avatar
 * anda, `roomAt` diz em que divisão está; ao MUDAR de sala a app entra na call do
 * cargo dessa sala (Vendas, TI, Reunião…) e sai ao sair. O corredor é zona neutra.
 *
 * Divisão de trabalho com o servidor (a mesma de `space.ts`):
 *  - o JOGADOR LOCAL move-se aqui, a 60fps, com colisão fina contra `isWalkable`;
 *  - os REMOTOS chegam a 10Hz e são interpolados entre pontos;
 *  - o servidor só prende aos limites do mapa (Fase 1, sem anti-cheat).
 *
 * O Phaser é pesado (~1MB); por isso é importado dinamicamente — só entra no
 * bundle de quem abre mesmo o escritório, nunca no arranque do chat.
 */

import {
  BUILDING,
  COLS,
  canEnter,
  canLockRoom,
  doorTilesOf,
  EXTERIOR_DOOR_SPANS,
  EXTERIOR_DOORS,
  EXTERIOR_OPENINGS,
  type ExteriorDoorSpan,
  type Facing,
  findPath,
  floorAt,
  type HuddleBody,
  type HuddleGroup,
  huddleGroups,
  huddleOf,
  inLake,
  interactivePropAt,
  isOutside,
  isUnderground,
  isUpstairs,
  isVerticalWallRun,
  isWalkable,
  knockableRoomAt,
  LAKE,
  lockableRoomAt,
  MAP_HEIGHT,
  MAP_WIDTH,
  onGardenPath,
  Permission,
  POSITION_SYNC_MS,
  PROP_SPECS,
  PROPS,
  type PropAction,
  type PropTarget,
  pathToPerson,
  placeName,
  ROOM_LABELS,
  ROOMS,
  ROWS,
  type Room,
  roomAt,
  SAY_RANGE,
  SAY_TTL_MS,
  type Seat,
  type SeatBody,
  type Snowflake,
  SPAWN,
  SURFACE_ROWS,
  seatAnchor,
  seatAt,
  seatBlocks,
  seatKey,
  seatOwners,
  TELEPORTS,
  type Teleport,
  TILE,
  TREES,
  type TreeSize,
  teleportCenter,
  teleportDestination,
  teleportNear,
  trocaDeAndar,
  UPSTAIRS_OFFSET_ROWS,
  UPSTAIRS_TOP_ROW,
} from "@greenvoice/shared";
import type PhaserT from "phaser";
import { type PointerEvent as ReactPointerEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { corpoNoMapa, presenceRoomId, roomMoveSpot } from "../hooks/useOfficePresence";
import { connectionLabel, connectMuted, engineVoice, useVoiceActions, useVoiceSnapshot } from "../hooks/useVoice";
import { api } from "../lib/api";
import { useIsTouch } from "../lib/media";
import { loadOfficePrefs, saveOfficePrefs } from "../lib/officePrefs";
import { voiceEngine } from "../lib/voice";
import { can, channelsOf, ownVoiceMarks, selfPermissions } from "../store/selectors";
import { gatewayClient, useStore } from "../store/store";
import { Arcade } from "./Arcade";
import { ChannelSidebar, useSidebarCollapsed } from "./ChannelSidebar";
import { GameTable } from "./GameTable";
import { IconChevron, IconClose, IconHeadphones, IconLock, IconMembers, IconShrink } from "./Icons";
import { OfficeChat } from "./OfficeChat";
import { OfficeMinimap, type Rota } from "./OfficeMinimap";
import { HuddleBar, KnockPanel, WhoPanel } from "./OfficePanels";
import { type Direccao, OfficeTouchControls } from "./OfficeTouchControls";
import {
  BUSH,
  BUSH_SMALL,
  DOOR_EXT_SLIDE,
  DOOR_FRAME,
  DOOR_HATCH_SLIDE,
  DOOR_SIGN,
  DOOR_SLIDE,
  EXT_DOOR_GAP,
  ensureWorldTextures,
  floorKey,
  GRASS,
  GRASS_ALT,
  HATCH_GAP,
  HEDGE,
  isLayeredSeat,
  LAKE_ART,
  LEAF,
  pathKey,
  propKey,
  propSliceKey,
  propSlices,
  ROOF,
  ROOF_AC,
  ROOF_ALT,
  ROOF_HELI,
  ROOF_SOLAR,
  seatPropKey,
  TREE,
  TREE_BIG,
  TREE_SMALL,
  WALL_DOOR,
  WALL_FACE,
  WALL_LAMP,
  WALL_TOP,
  WALL_TOP_SLIM,
  WALL_WINDOW,
  WATER_FRAMES,
} from "./officeArt";
import {
  type Avatar,
  bandaCamara,
  CAMERA_ZOOM,
  COLLIDE_MARGIN,
  ensureGlow,
  GLOW_COOL,
  GLOW_GREEN,
  GLOW_RED,
  GLOW_WARM,
  makeAvatar,
  marksFor,
  mudouDePersonagem,
  pokeShake,
  type Remote,
  reconcileRemotes,
  type Seating,
  SPEED,
  setMarks,
  setSeated,
  stepAnim,
  ZOOM_MAX,
  ZOOM_MIN,
  ZOOM_STEP,
} from "./officeAvatars";
import { corpoLembrado, lembrarCorpo } from "./officeBody";
import {
  alvoAFrente,
  BolasDeNeve,
  bolasDaCena,
  DISTANCIA_SEM_ALVO,
  type EstadoDaBola,
  pontoAFrente,
} from "./officeBolaDeNeve";
import { donaAoVoltar, type EcraDaCall, foraDaCall, planoDeFecho, planoDeSala, saiuDeEcraCheio } from "./officeCall";
import { preloadOfficeChar } from "./officeChar";
import { preloadOfficeCharFemale } from "./officeCharFemale";
import { aoAr, Clima, climaDaCena } from "./officeClima";
import {
  ARBUSTO_ALTO,
  ARBUSTO_BAGAS,
  ARBUSTO_ESPINHOSO,
  ARBUSTO_FLORIDO,
  ARBUSTO_LARGO,
  ARBUSTO_RASTEIRO,
  ARBUSTO_REDONDO,
  ARBUSTO_SEBE,
  ARBUSTO_VARIEGADO,
  arbusto,
  ensureFloraTextures,
  preloadOfficeFlora,
} from "./officeFlora";
import {
  ensureFurnitureTextures,
  HELICOPTERO,
  HELICOPTERO_CORPO,
  PATO,
  preloadOfficeFurniture,
} from "./officeFurniture";
import { ceoPresente, Helicoptero, helicopteroDaCena } from "./officeHeli";
import { destinoMaisProximo } from "./officeMap";
import { pathMask } from "./officePath";
import { MEDIDA_DA_PECA, PECAS_DO_TELHADO } from "./officeRoof";
import { PLANTA_CASA, SOMBRA_CASA } from "./officeShadow";
import {
  createHuddleLayer,
  createSayLayer,
  createTvBadge,
  type HuddleLayer,
  type HuddleRing,
  type SayItem,
  type SayLayer,
  type TvBadge,
} from "./officeSocial";
import { guardarArteDaCena, servirArteEmCache } from "./officeTextureCache";
import { VidaDoJardim, vidaDaCena } from "./officeVida";
import { saiaDaParede } from "./officeWallSkirt";
import { PontoModal } from "./PontoModal";
import { TOPBAR_SLOT_ID } from "./TopBar";
import { UserCard } from "./UserCard";
import { UserPanel } from "./UserPanel";
import { VoiceBar } from "./VoiceBar";
import { VoiceStage } from "./VoiceStage";
import { Whiteboard } from "./Whiteboard";

type Key = PhaserT.Input.Keyboard.Key;

/** O que o aviso da tecla E diz em cima de cada móvel. */
const PROP_LABELS: Record<PropAction, string> = {
  whiteboard: "Abrir o quadro branco",
  game: "Jogar 4 em Linha",
  rush: "Jogar Green Rush",
  clock: "Bater ponto",
};
/** De quanto em quanto tempo se refaz o grafo das conversas de corredor. 5Hz
 *  chega: as pessoas andam a ~4,7 tiles/s e o raio do grupo é de 2,8 tiles. */
const HUDDLE_SCAN_MS = 200;

/**
 * Quanto é que a bancada monta na base da parede que a encosta, em pixels.
 *
 * 10 de 32: cobre o rodapé e deixa a parede a ler-se como parede. Metade já
 * parecia uma bancada meio enterrada, e menos de 8 não se distingue do encosto
 * ao pixel que ela já tinha — que era precisamente a queixa.
 */
const MONTA_NA_PAREDE = 10;

/** Profundidades de fundo (o resto usa o y da base para o y-sort 2.5D). */
const DEPTH_FLOOR = -10000;
const DEPTH_RUG = -9000;
const DEPTH_AMBIENT = -8500; // escurece chão+tapete; os candeeiros (ADD) acendem por cima
const DEPTH_CEILING_LIGHT = -8000;
const DEPTH_ROOM_LABEL = -6000;

/**
 * De quem é cada lugar, neste instante. Escrito uma vez por frame pelo loop, lido
 * pelo módulo dos avatares — um objecto só, em vez de um `Map` novo a 60fps.
 *
 * Vive fora da cena porque a resposta tem de ser a MESMA para o boneco local e
 * para os remotos: é o mesmo cálculo, sobre a mesma lista de corpos, e é isso que
 * garante que ninguém se vê sentado num lugar que, para o outro, já era dele.
 */
let seatOwnership: ReadonlyMap<string, string> = new Map();

/**
 * Os assentos deste escritório, para o módulo de avatares saber sentar os
 * remotos. A REGRA de sentar vive lá; a geometria que responde "há aqui um
 * lugar?" vive aqui, e é ela que entra por este adaptador.
 */
const SEATING: Seating = {
  at: seatAt,
  anchor: seatAnchor,
  depth: seatedDepth,
  ownerOf: (seat) => seatOwnership.get(seatKey(seat)),
};

/** Profundidade de quem está sentado: à frente do móvel em que se sentou. */
function seatedDepth(seat: Seat): number {
  return (seat.row + 1) * TILE + 1;
}

/**
 * Profundidade do ENCOSTO: um degrau acima de quem lá está sentado.
 *
 * É o que faz a cadeira envolver o corpo em vez de ficar um cartaz atrás dele —
 * o assento fica por baixo (`baseY`), a pessoa no meio (`seatedDepth`) e o
 * encosto por cima. Fica ainda ABAIXO das etiquetas e da luz do tecto, por isso
 * não tapa nomes nem sombreados de sala.
 */
function seatBackrestDepth(row: number): number {
  return (row + 1) * TILE + 2;
}

/**
 * BALOIÇO — o vento na vegetação.
 *
 * As plantas do v1 estavam PARADAS: as árvores largavam folhas em partículas, o
 * que dava a ilusão de vida à distância, mas o tronco e a copa não mexiam um
 * pixel, e um vaso dentro de uma sala não tem sequer partículas. Vegetação
 * imóvel num mundo onde a porta corre e o LED pulsa lê-se como cenário colado.
 *
 * Todas estas imagens estão ancoradas em `setOrigin(0.5, 1)`, ou seja, o pivô da
 * rotação é o PÉ da planta — é isso que faz um ângulo minúsculo ler-se como
 * vento a dobrar o caule, e não como o sprite inteiro a torcer-se.
 *
 * A `seed` desfasa os ciclos a partir da POSIÇÃO (nada de `Math.random`): duas
 * plantas lado a lado a baloiçar em uníssono denunciam a animação, e um mundo
 * determinístico é o mesmo mundo em todos os clientes.
 */
function baloico(scene: PhaserT.Scene, img: PhaserT.GameObjects.Image, seed: number, amp: number, dur: number): void {
  scene.tweens.add({
    targets: img,
    rotation: { from: -amp, to: amp },
    duration: dur,
    yoyo: true,
    repeat: -1,
    ease: "Sine.easeInOut",
    delay: (Math.abs(Math.round(seed)) % 7) * 190,
  });
}

// ── Porta exterior de correr ────────────────────────────────
//
// As quatro portas do prédio para o jardim são de correr, iguais em gesto às das
// salas: duas meias-folhas que se afastam para dentro da ombreira quando alguém
// chega. Antes rodavam pela dobradiça (tween de `scaleX`), o que é o desenho
// errado para uma porta ao ar livre — a rodar, ou invadia o corredor ou batia no
// jardim, e o encolher da folha lia-se como a folha a desaparecer, não a abrir.
//
// A diferença para as portas das salas é a MÁSCARA. Estas ficam em paredes-capa,
// que se desenham de cima e são OPACAS, por isso a folha tem de estar por cima da
// pedra (`depth + 1`) para se ver — e, sem recorte, ao correr para o lado ia
// pintar madeira por cima da ombreira. A máscara de geometria é o vão: dentro dela
// a folha vê-se, fora dela desaparece, que é exactamente "recolher para dentro
// da parede".
//
// A ESCALA é a das portas das salas, e isso foi uma correcção: cabiam num tile só,
// com um vão de 20px de largura, e ao lado de um vão de sala com 64 pareciam uma
// gateira («a porta está pequena, faça ela de um tamanho igual às portas das
// salas»). Agora cada uma são DOIS tiles, como as de dentro.

/**
 * Uma porta de duas folhas que correm para os lados por aproximação: as folhas,
 * o centro do vão em pixels do mundo (para o `update` medir a distância ao
 * avatar) e o curso partilhado. O `open` guarda o estado, para o tween só
 * disparar na transição e não a 60fps.
 *
 * O `run` e o `range` são POR PORTA e não constantes de módulo porque o mesmo
 * mecanismo serve duas peças com gestos diferentes: as portas do edifício, que
 * cedem a quem passa no corredor, e a escotilha do bunker, que abre para um SALTO
 * de andar — e por isso pede mais folga, senão pisa-se a boca antes de a tampa
 * acabar de correr.
 */
interface ExteriorDoor {
  /** Centro do vão em pixels do mundo — é aqui que se mede a proximidade. */
  cx: number;
  cy: number;
  left: PhaserT.GameObjects.Image;
  right: PhaserT.GameObjects.Image;
  /** Posição de cada folha fechada NO EIXO do curso — some-se/subtrai-se a estas. */
  leftShut: number;
  rightShut: number;
  /** Curso de cada folha em pixels: a sua largura inteira (some por completo). */
  run: number;
  /** A que distância do centro do vão a porta reage ao avatar, em pixels. */
  range: number;
  /** Curso 0..1 partilhado pelas duas folhas — um só tween move o par. */
  travel: { t: number };
  open: boolean;
  tween?: PhaserT.Tweens.Tween;
}

/**
 * Põe cada folha no sítio para o curso actual: afastam-se uma da outra.
 *
 * Só no eixo X. Tanto as portas exteriores como a escotilha vivem em vãos
 * HORIZONTAIS, e é por isso que as folhas correm sempre para os lados.
 */
function placeExtLeaves(door: ExteriorDoor): void {
  const gone = door.travel.t * door.run;
  door.left.x = door.leftShut - gone;
  door.right.x = door.rightShut + gone;
}

/** Corre o par de folhas. Um tween sobre o curso, para nunca dessincronizarem. */
function setExtDoorOpen(scene: PhaserT.Scene, door: ExteriorDoor, open: boolean): void {
  if (door.open === open) return;
  door.open = open;
  door.tween?.stop();
  door.tween = scene.tweens.add({
    targets: door.travel,
    t: open ? 1 : 0,
    duration: open ? 240 : 320, // fecha mais devagar do que abre, como as de verdade
    ease: open ? "Cubic.easeOut" : "Cubic.easeInOut",
    onUpdate: () => placeExtLeaves(door),
  });
}

/** Pixel onde acaba o jardim e começa a terra — a fronteira entre as duas faixas. */
const UNDERGROUND_TOP = SURFACE_ROWS * TILE;
/** Pixel onde começa o 1.º andar. */
const UPSTAIRS_TOP = UPSTAIRS_TOP_ROW * TILE;
/** Quanto sobe um corpo (e a câmara) ao passar do rés-do-chão ao 1.º andar. */
const ANDAR_PX = UPSTAIRS_OFFSET_ROWS * TILE;
/** A troca de andar na escada: o andar de baixo desvanece-se e o de cima aparece. */
const FADE_DO_ANDAR_MS = 260;

/** A faixa do mapa a que pertence um Y: superfície, subsolo ou 1.º andar. */
function faixaDe(y: number): { x: number; y: number; w: number; h: number } {
  if (y >= UPSTAIRS_TOP) return { x: 0, y: UPSTAIRS_TOP, w: MAP_WIDTH, h: MAP_HEIGHT - UPSTAIRS_TOP };
  if (y >= UNDERGROUND_TOP) return { x: 0, y: UNDERGROUND_TOP, w: MAP_WIDTH, h: UPSTAIRS_TOP - UNDERGROUND_TOP };
  return { x: 0, y: 0, w: MAP_WIDTH, h: UNDERGROUND_TOP };
}

/** A linha de parede da fachada norte por cima desta sala, ou `null` se a sala não encosta à fachada. */
function naFachada(room: Room): number | null {
  if (room.row === BUILDING.row + 1) return BUILDING.row;
  if (room.row === BUILDING.row + 1 + UPSTAIRS_OFFSET_ROWS) return BUILDING.row + UPSTAIRS_OFFSET_ROWS;
  return null;
}

/**
 * Prende a câmara à faixa onde o avatar está: superfície, subsolo ou 1.º andar.
 *
 * Sem isto, os limites cobriam o mapa TODO e, encostado à sebe sul, via-se meio
 * ecrã de terra preta com o bunker lá ao fundo — o subsolo deixava de ser um
 * sítio a que se DESCE para passar a ser um anexo visível do jardim.
 *
 * A caixa que se entrega à câmara não é a faixa em bruto: passa pelo
 * `bandaCamara`, que a alarga quando o campo de visão já é maior do que ela —
 * sem isso o Phaser encosta o mundo à esquerda em vez de o centrar. Como
 * depende do ZOOM, que anima, isto reaplica-se a cada frame e não só ao mudar
 * de faixa.
 */
function applyCameraBand(cam: PhaserT.Cameras.Scene2D.Camera, y: number): void {
  const b = bandaCamara(faixaDe(y), { w: cam.displayWidth, h: cam.displayHeight });
  cam.setBounds(b.x, b.y, b.w, b.h);
}

/** Há chão pisável encostado a este tile (8 vizinhos)? Rocha só se vê à beira. */
function touchesFloor(col: number, row: number): boolean {
  for (let dr = -1; dr <= 1; dr += 1) {
    for (let dc = -1; dc <= 1; dc += 1) {
      if (dr === 0 && dc === 0) continue;
      if (floorAt(col + dc, row + dr) != null) return true;
    }
  }
  return false;
}

/**
 * A escotilha do bunker, montada por cima da sua própria arte de chão.
 *
 * O batente (aro de betão, calhas de aço e a boca já pintada escura) vem no prop
 * `bunkerHatch`, desenhado plano como um tapete. Aqui só se acrescenta o que se
 * MEXE: as duas meias-tampas de aço que correm para os lados quando alguém se
 * aproxima. É a mesma peça de teatro das portas exteriores, à escala do vão de
 * 56px — e é por isso que a escotilha "se abre" em vez de estar eternamente
 * escancarada, que era o que uma tampa pintada no batente daria.
 *
 * A profundidade é `DEPTH_RUG + 3`: acima do batente (que é `DEPTH_RUG + 2`) e
 * muito abaixo do avatar (y-sorted a ~592), portanto passa-se POR CIMA da tampa
 * fechada — como se passa por cima de um alçapão no chão.
 */
function makeHatchDoor(scene: PhaserT.Scene, x0: number, y0: number, depth: number): ExteriorDoor {
  const gapX = x0 + HATCH_GAP.x;
  const gapY = y0 + HATCH_GAP.y;
  const midY = gapY + HATCH_GAP.h / 2;
  const half = HATCH_GAP.w / 2;
  const leftShut = gapX + half / 2;
  const rightShut = gapX + half + half / 2;
  const mask = scene.make
    .graphics({}, false) // fora da display list: só serve de recorte
    .fillStyle(0xffffff)
    .fillRect(gapX, gapY, HATCH_GAP.w, HATCH_GAP.h)
    .createGeometryMask();
  const left = scene.add.image(leftShut, midY, DOOR_HATCH_SLIDE).setDepth(depth).setMask(mask);
  const right = scene.add
    .image(rightShut, midY, DOOR_HATCH_SLIDE)
    .setDepth(depth)
    .setFlipX(true) // espelhada → os dois puxadores encontram-se ao meio
    .setMask(mask);
  return {
    cx: gapX + half,
    cy: midY,
    left,
    right,
    leftShut,
    rightShut,
    run: half,
    // Mais folgado do que numa porta de parede: a escotilha ocupa 3 tiles e o salto
    // dispara ao pisar a boca. Com o alcance das portas normais, chegava-se ao vão
    // antes de a tampa acabar de correr e caía-se por uma escotilha ainda fechada.
    range: TILE * 2.6,
    travel: { t: 0 },
    open: false,
  };
}

/**
 * Uma porta exterior do edifício: batente, folhas e o recorte do vão.
 *
 * Monta-se a partir de um `ExteriorDoorSpan` porque cada porta são DOIS tiles,
 * lado a lado numa parede HORIZONTAL — a única onde há portas desde que as dos
 * flancos saíram. Par em colunas, folhas a correr para os lados, exactamente como
 * as das salas.
 *
 * O batente é MEIO vão (ombreira na ponta esquerda, aberto até à borda direita),
 * por isso o segundo tile é a mesma textura com `setFlipX` e o par fecha um vão
 * simétrico de 56px.
 *
 * A capa de pedra desenha-se por baixo do batente porque o varrimento das paredes
 * salta estes dois tiles (são vãos pisáveis) e o batente, que é arte com recortes,
 * não tapa o tile todo — sem a capa ficava um buraco no mapa à volta da ombreira.
 */
function makeExtDoor(scene: PhaserT.Scene, span: ExteriorDoorSpan): ExteriorDoor {
  const x0 = span.col * TILE;
  const y0 = span.row * TILE;
  const gap = EXT_DOOR_GAP;
  const half = gap.w / 2; // curso de cada meia-folha: some por completo
  const start = x0 + gap.x;
  const aShut = start + half / 2; // centro de cada meia-folha fechada
  const bShut = start + half + half / 2;
  const across = y0 + gap.y + gap.h / 2;
  for (const [i, c] of [span.col, span.col + 1].entries()) {
    scene.add
      .image(c * TILE, span.row * TILE, WALL_TOP)
      .setOrigin(0, 0)
      .setDepth(span.row * TILE);
    scene.add
      .image(c * TILE + TILE / 2, y0 + TILE / 2, WALL_DOOR)
      .setFlipX(i === 1) // a 2ª metade é a 1ª espelhada → ombreiras nas duas pontas
      .setDepth(span.row * TILE + 0.5); // meio ponto acima da capa que acabou de se pôr
  }
  const mask = scene.make
    .graphics({}, false) // fora da display list: só serve de recorte
    .fillStyle(0xffffff)
    .fillRect(start, y0 + gap.y, gap.w, gap.h)
    .createGeometryMask();
  // As folhas vivem por cima da parede-capa, que é opaca; a máscara é que as faz
  // desaparecer para dentro dela ao correr.
  const leafDepth = span.row * TILE + 1;
  const left = scene.add.image(aShut, across, DOOR_EXT_SLIDE).setDepth(leafDepth).setMask(mask);
  const right = scene.add
    .image(bShut, across, DOOR_EXT_SLIDE)
    .setFlipX(true) // espelhada → os dois puxadores encontram-se ao meio
    .setDepth(leafDepth)
    .setMask(mask);
  return {
    cx: start + half,
    cy: across,
    left,
    right,
    leftShut: aShut,
    rightShut: bShut,
    run: half,
    // O MESMO alcance das portas das salas — «com as mesmas funcionalidades».
    range: DOOR_OPEN_RANGE,
    travel: { t: 0 },
    open: false,
  };
}

// ── Porta de correr automática ──────────────────────────────
//
// Cada sala com nome tem UMA porta dupla no seu vão: duas folhas que abrem sozinhas
// quando alguém se aproxima e voltam a fechar quando essa pessoa se afasta. Trancar
// (E) muda o critério de abertura, não o desenho: a porta continua ali, deixa de
// ceder a quem vem de fora e passa a exibir o cadeado.
//
// A abertura é calculada LOCALMENTE em cada cliente a partir das posições que ele
// já recebe — não há mensagem nenhuma de "porta abriu". Duas pessoas no mesmo
// corredor vêem o mesmo porque partem dos mesmos dados e da mesma regra.

/** Alcance a que a porta cede a quem chega — abre-se antes de lá esbarrar. */
const DOOR_OPEN_RANGE = TILE * 2.4;

interface SlidingDoor {
  roomId: string;
  /** Centro do vão (fronteira entre os dois tiles) — a distância mede-se aqui. */
  cx: number;
  cy: number;
  /** Os dois tiles do vão, para saber quem está EM CIMA da soleira. */
  row: number;
  colLeft: number;
  colRight: number;
  left: PhaserT.GameObjects.Image;
  right: PhaserT.GameObjects.Image;
  sign: PhaserT.GameObjects.Image;
  /**
   * Luz de estado do ferrolho — `null` numa porta que NÃO tranca.
   *
   * O tipo diz a regra: a luz é o mostrador de um ferrolho, e a Copa, a Recepção
   * e a Sala de Descanso não têm nenhum (`noBolt`, regra 0). Uma luz verde
   * permanente numa porta dessas não estava a informar nada — estava a prometer
   * um estado que nunca ia mudar.
   */
  led: PhaserT.GameObjects.Rectangle | null;
  /** Halo vermelho sobre a calha — o sinal que se lê do fundo do corredor. */
  glow: PhaserT.GameObjects.Image | null;
  /** Curso 0..1 partilhado pelas duas folhas — um só tween move o par. */
  travel: { t: number };
  open: boolean;
  locked: boolean;
  signShown: boolean;
  slideTween?: PhaserT.Tweens.Tween;
  signTween?: PhaserT.Tweens.Tween;
  /** Pulsar do halo enquanto trancada (só existe enquanto está vermelha). */
  pulseTween?: PhaserT.Tweens.Tween;
}

/** Põe cada folha no sítio para o curso actual: afastam-se uma da outra. */
function placeLeaves(door: SlidingDoor): void {
  const gone = door.travel.t * TILE; // curso total: um tile para cada lado
  door.left.x = door.colLeft * TILE + TILE / 2 - gone;
  door.right.x = door.colRight * TILE + TILE / 2 + gone;
}

/** Corre as duas folhas. Um tween sobre o curso, para o par nunca dessincronizar. */
function setSlideOpen(scene: PhaserT.Scene, door: SlidingDoor, open: boolean): void {
  if (door.open === open) return;
  door.open = open;
  door.slideTween?.stop();
  door.slideTween = scene.tweens.add({
    targets: door.travel,
    t: open ? 1 : 0,
    duration: open ? 240 : 320, // fecha mais devagar do que abre, como as de verdade
    ease: open ? "Cubic.easeOut" : "Cubic.easeInOut",
    onUpdate: () => placeLeaves(door),
  });
}

/** Acende/apaga o cadeado (só faz sentido com a porta fechada). */
function setSignShown(scene: PhaserT.Scene, door: SlidingDoor, shown: boolean): void {
  if (door.signShown === shown) return;
  door.signShown = shown;
  door.signTween?.stop();
  if (shown) door.sign.setVisible(true);
  door.signTween = scene.tweens.add({
    targets: door.sign,
    alpha: shown ? 1 : 0,
    scaleY: shown ? 1 : 0.7,
    duration: shown ? 200 : 140,
    ease: shown ? "Back.easeOut" : "Cubic.easeIn",
    onComplete: () => {
      if (!shown) door.sign.setVisible(false);
    },
  });
}

/**
 * Verde quando a sala está aberta, VERMELHO quando trancada — e vermelho a
 * sério: o LED muda de cor e acende-se um halo que respira sobre a calha. Um
 * quadradinho de 5px não se via do corredor; um sinal luminoso vê-se. É o único
 * feedback de que a tranca pegou mesmo, por isso não pode ser subtil.
 */
function setDoorLocked(scene: PhaserT.Scene, door: SlidingDoor, locked: boolean): void {
  // Sem ferrolho não há estado para mostrar nem para guardar: a porta continua a
  // abrir a quem chega e o servidor recusa trancá-la (regra 0). Sair AQUI, antes
  // do `door.locked`, é o que impede uma porta de ficar trancada em silêncio.
  if (!door.led || !door.glow) return;
  if (door.locked === locked) return;
  door.locked = locked;
  const colour = locked ? 0xef4444 : 0x39d353;
  door.led.fillColor = colour;
  door.led.setStrokeStyle(4, colour, 0.35);
  door.pulseTween?.stop();
  if (!locked) {
    door.glow.setVisible(false).setAlpha(0);
    return;
  }
  door.glow.setVisible(true).setAlpha(0.75);
  door.pulseTween = scene.tweens.add({
    targets: door.glow,
    alpha: 0.3,
    duration: 900,
    yoyo: true,
    repeat: -1,
    ease: "Sine.easeInOut",
  });
}

/**
 * Monta a porta de cada sala com nome a partir dos seus dois vãos contíguos (a
 * contiguidade é contrato testado em `space.test.ts`).
 *
 * A profundidade é o truque todo: a moldura fica ATRÁS da folha, e a folha um
 * ponto ATRÁS da parede-face vizinha (que se desenha em `(row+1)*TILE`). Assim,
 * ao abrir, a folha desliza para o tile do lado e desaparece POR DENTRO da parede
 * — que é exactamente o que uma porta de correr faz.
 *
 * O `Phaser` vem por PARÂMETRO, como em `drawWorld`, e tem de vir: os tipos do
 * Phaser declaram um `namespace Phaser` GLOBAL, por isso `Phaser.BlendModes` passa
 * o typecheck em qualquer sítio — mas o bundle ESM (o que o Vite carrega) nunca põe
 * nada em `window`. Usá-lo sem o receber compila, constrói, e só rebenta em runtime
 * com `ReferenceError` a meio do `create()` — que é um escritório todo preto.
 */
function drawSlidingDoors(scene: PhaserT.Scene, Phaser: typeof PhaserT): SlidingDoor[] {
  ensureGlow(scene, GLOW_RED, "239,68,68");
  const out: SlidingDoor[] = [];
  for (const room of ROOMS) {
    if (room.name === "") continue;
    const tiles = [...doorTilesOf(room.id)].sort((a, b) => a[0] - b[0]);
    if (tiles.length !== 2) continue;
    const [left, right] = tiles as [readonly [number, number], readonly [number, number]];
    const row = left[1];
    const base = (row + 1) * TILE; // pé do vão: tudo aqui é ancorado por baixo
    const mid = right[0] * TILE; // fronteira entre os dois tiles = meio do vão

    for (const [col] of tiles) {
      scene.add
        .image(col * TILE + TILE / 2, base, DOOR_FRAME)
        .setOrigin(0.5, 1)
        .setDepth(base - 2);
    }
    const leafLeft = scene.add
      .image(left[0] * TILE + TILE / 2, base, DOOR_SLIDE)
      .setOrigin(0.5, 1)
      .setDepth(base - 1);
    const leafRight = scene.add
      .image(right[0] * TILE + TILE / 2, base, DOOR_SLIDE)
      .setOrigin(0.5, 1)
      .setDepth(base - 1)
      .setFlipX(true); // espelhada → o puxador de cada folha fica no bordo do meio

    // Cadeado a meia altura do vão, à frente das folhas fechadas.
    const sign = scene.add
      .image(mid, base - TILE / 2, DOOR_SIGN)
      .setDepth(base - 0.5)
      .setVisible(false)
      .setAlpha(0);

    // LED de estado, montado na calha, com halo por cima. O halo nasce apagado e
    // só acende (a pulsar) quando a sala tranca — ver `setDoorLocked`.
    //
    // E só existe onde HÁ ferrolho. A pergunta é `canLockRoom` — a mesma função
    // que o servidor usa para recusar a tranca —, não uma segunda lista de nomes
    // de salas escrita aqui: uma lista à parte fica desalinhada na primeira vez
    // que alguém marcar (ou desmarcar) um `noBolt` na planta, e o sintoma seria
    // uma luz a mentir. Numa porta sem ferrolho o verde era permanente: não
    // sinalizava "aberta", só estava aceso.
    const ledY = base - TILE + 9;
    const temFerrolho = canLockRoom(room.id);
    const led = temFerrolho
      ? scene.add
          .rectangle(mid, ledY, 10, 6, 0x39d353)
          .setStrokeStyle(4, 0x39d353, 0.35)
          .setDepth(base - 0.4)
      : null;
    const glow = temFerrolho
      ? scene.add
          .image(mid, ledY, GLOW_RED)
          .setBlendMode(Phaser.BlendModes.ADD)
          .setDepth(base - 0.42)
          .setVisible(false)
          .setAlpha(0)
      : null;
    glow?.setDisplaySize(TILE * 3.2, TILE * 2.2);

    const door: SlidingDoor = {
      roomId: room.id,
      cx: mid,
      cy: row * TILE + TILE / 2,
      row,
      colLeft: left[0],
      colRight: right[0],
      left: leafLeft,
      right: leafRight,
      sign,
      led,
      glow,
      travel: { t: 0 },
      open: false,
      locked: false,
      signShown: false,
    };
    out.push(door);
  }
  return out;
}

/** Está em cima da soleira desta porta? Aí ainda conta como estando na sala. */
function onDoorway(door: SlidingDoor, x: number, y: number): boolean {
  if (Math.floor(y / TILE) !== door.row) return false;
  const col = Math.floor(x / TILE);
  return col === door.colLeft || col === door.colRight;
}

/**
 * Alguém, aqui e agora, faz esta porta abrir?
 *
 * Aberta, chega chegar perto — é uma porta automática de escritório. Trancada, só
 * cede a quem já está DENTRO (regra 2: quem está lá dentro sai sempre), e num
 * alcance curto, para trancar se ver mesmo: a porta bate e só volta a mexer-se
 * para deixar sair alguém. Quem vem do corredor fica à porta.
 */
function doorShouldOpen(door: SlidingDoor, bodies: { x: number; y: number }[]): boolean {
  for (const body of bodies) {
    if (!door.locked) {
      if (Math.hypot(body.x - door.cx, body.y - door.cy) <= DOOR_OPEN_RANGE) return true;
      continue;
    }
    // TRANCADA: um só critério, e é PISAR a soleira. Nada de raio nenhum —
    // qualquer raio à volta do vão abria a porta a quem apenas se aproximava (e
    // era isso mesmo que se via: trancava-se, ficava-se ali ao pé, e a porta
    // continuava escancarada). Pisar a soleira só o consegue quem está DENTRO:
    // o `canEnter` proíbe um estranho de sequer entrar no vão de uma sala
    // trancada (contrato testado em `space.test.ts`). Logo: fechada para quem
    // chega, e quem está lá dentro sai sempre — regra 2 intacta.
    if (onDoorway(door, body.x, body.y)) return true;
  }
  return false;
}

/**
 * O bosquete que esconde a escotilha do bunker.
 *
 * > "coloque umas moitas no bunker, só pra deixar ele um pouco escondido"
 *
 * O "um pouco" é a especificação inteira. A escotilha é um TELEPORTE: escondida a
 * sério, deixa de existir para quem não sabe que está ali. Por isso estas seis
 * peças partem a SILHUETA — o rectângulo de betão limpo no meio da relva, que é o
 * que denuncia a peça a vinte metros — e não tocam num pixel do VÃO por onde as
 * folhas de aço deslizam (`HATCH_GAP`). Medido: 42% do betão coberto, 0% do vão.
 * É `officeBunker.test.ts` que mede as duas coisas, lendo esta lista.
 *
 * Esconder aqui é barato, e essa é a armadilha: a escotilha é um `FLAT_PROP` a
 * `DEPTH_RUG + 2`, portanto QUALQUER peça do jardim — ordenada pela linha, com
 * profundidade positiva — lhe passa à frente, mesmo estando a norte dela. Sem o
 * y-sort a travar nada, o que limita a composição é o gosto e não o motor; daí o
 * número (seis) e a distribuição estarem escritos e medidos, não sorteados.
 *
 * A geometria é apertada e é ela que explica os decimais:
 *   - a escotilha está em col 40 row 3, 3×2 → betão em x 1280..1376, y 96..160;
 *   - o vão fica em x 1300..1356, y 116..148 — é o buraco intocável;
 *   - a NASCENTE não há relva: a col 43 é a sebe da borda do mundo. O bosquete só
 *     pode fechar por norte, poente e sul, e nada passa de x 1376;
 *   - quem sobe do bunker aterra em [41,5], com os pés a y≈176; as duas rasteiras
 *     assentam a 174,4, logo ninguém aparece atrás de folhagem ao chegar.
 *
 * `w`/`h` são a medida do PNG em pixels de ecrã e vivem AQUI, ao lado do `col`/
 * `row`, porque é com elas que o teste faz a aritmética das caixas: numa tabela à
 * parte, mover uma moita passava a ser verificado contra o tamanho de outra. Como
 * em `officeFlora.ts`, o número está escrito à mão de propósito — é um CONTRATO
 * com o ficheiro de arte, não um valor que possa escorregar sozinho.
 *
 * As espécies escolhem-se pela mesma regra do resto do jardim (ver o `scenery`):
 * a `ALTO` é a única erecta das nove (26×50) e é ela o fundo por onde a tampa
 * espreita; as duas `RASTEIRO` repetem-se DE PROPÓSITO, porque formam uma bainha
 * contínua a sul e com 25 px de altura passam rentes ao vão sem lhe tocar; a
 * `FLORIDO` fica com o canto nordeste, onde a escolha óbvia — `REDONDO` — ficaria
 * a dois tiles da `REDONDO` que já lá está (col 41, row 1,6), lado a lado com o
 * seu próprio clone. A `VARIEGADO`, a sul, não toca no betão: é a que impede o
 * conjunto de se ler como cinco moitas em roda de um alçapão — o contrário de
 * escondido.
 */
const MOITAS_BUNKER: { variante: string; recurso: string; col: number; row: number; w: number; h: number }[] = [
  { variante: ARBUSTO_ALTO, recurso: BUSH_SMALL, col: 40.35, row: 3.4, w: 26, h: 50 }, // fundo NO, a peça alta
  { variante: ARBUSTO_FLORIDO, recurso: BUSH_SMALL, col: 42.4, row: 3.4, w: 37, h: 35 }, // canto NE, contra a sebe
  { variante: ARBUSTO_ESPINHOSO, recurso: BUSH, col: 39.75, row: 4.75, w: 53, h: 49 }, // canto SO, a poente do vão
  { variante: ARBUSTO_RASTEIRO, recurso: BUSH_SMALL, col: 40.4, row: 5.45, w: 52, h: 25 }, // bainha sul, poente
  { variante: ARBUSTO_RASTEIRO, recurso: BUSH_SMALL, col: 42.1, row: 5.45, w: 52, h: 25 }, // bainha sul, nascente
  { variante: ARBUSTO_VARIEGADO, recurso: BUSH, col: 40.2, row: 6.7, w: 60, h: 43 }, // solta, a quebrar a roda
];

/**
 * Desenha o cenário estático uma vez: o chão de cada sala, as paredes em 2.5D
 * (face iluminada virada ao observador, capa de topo nas restantes), a mobília
 * do contrato partilhado ancorada pela base (y-sort), as etiquetas das salas e a
 * ILUMINAÇÃO — candeeiros de teto quentes por sala e o brilho frio dos ecrãs.
 * A colisão continua a ser a de `space.ts`; aqui só se veste e se ilumina.
 *
 * Devolve as portas exteriores de correr (as duas folhas + o vão) para o `update`
 * as abrir por proximidade do avatar.
 */
function drawWorld(scene: PhaserT.Scene, Phaser: typeof PhaserT): ExteriorDoor[] {
  // Primeiro a arte do pacote — vegetação e os dois móveis grandes —, DEPOIS o
  // resto: quem regista a chave primeiro fica com ela, e é assim que a árvore
  // desenhada dá lugar à desenhada à mão do campus sem uma linha de `officeArt`
  // mudar. Ao contrário, não acontecia nada — ver o cabeçalho de `officeFlora.ts`.
  ensureFloraTextures(scene);
  ensureFurnitureTextures(scene);
  ensureWorldTextures(scene);
  ensureGlow(scene, GLOW_WARM, "255,214,150");
  ensureGlow(scene, GLOW_COOL, "150,205,255");
  ensureGlow(scene, GLOW_GREEN, "110,240,150");

  // ── Chão: uma tábua por tile, com o tipo da sala, xadrez subtil, no fundo.
  // Fora do prédio, o mesmo passo pinta JARDIM (relva, ou água do lago) — é o que
  // tira ao mundo o ar de "prisão" e dá exterior ao prédio.
  //
  // O lago tem DOIS caminhos e a escolha faz-se aqui, uma vez, antes do ciclo:
  //
  //  • `lagoPintado` — a imagem única do lago chegou (448×224, a elipse inteira num
  //    só recorte). Então as tiles de água pintam-se de RELVA como qualquer outro
  //    tile do jardim e a imagem assenta por cima: a margem irregular da pintura
  //    precisa de ver relva por baixo, senão a orla do lago recortava-se contra o
  //    vazio. Sem tiles de água não há timer de onda — a ondulação passa a ser a
  //    que o pintor deixou lá, mais os nenúfares que continuam a baloiçar.
  //
  //  • sem imagem (o PNG faltou e o `officeFurniture.ts` apanhou o 404) — volta o
  //    lago em tiles: as 4 frames de onda com uma fase por tile, que um timer cicla
  //    lá em baixo. É o plano B, e é por isso que este ramo não desaparece.
  //
  // Uma só decisão, no topo, em vez de um `exists()` por tile: são ~2900 tiles e a
  // resposta é a mesma para todas.
  const lagoPintado = scene.textures.exists(LAKE_ART);
  const waterTiles: { img: PhaserT.GameObjects.Image; phase: number }[] = [];
  for (let r = 0; r < ROWS; r += 1) {
    for (let c = 0; c < COLS; c += 1) {
      const kind = floorAt(c, r);
      if (!kind) {
        if (isOutside(c, r)) {
          if (inLake(c, r) && !lagoPintado) {
            const phase = (c * 3 + r * 5) % WATER_FRAMES.length;
            const img = scene.add
              .image(c * TILE, r * TILE, WATER_FRAMES[phase] ?? WATER_FRAMES[0]!)
              .setOrigin(0, 0)
              .setDepth(DEPTH_FLOOR);
            waterTiles.push({ img, phase });
          } else {
            scene.add
              .image(c * TILE, r * TILE, (c + r) % 2 === 0 ? GRASS_ALT : GRASS)
              .setOrigin(0, 0)
              .setDepth(DEPTH_FLOOR);
            // O carreiro de pedra vai POR CIMA da relva, não no lugar dela — e é
            // dessa sobreposição que vem a orla: o tile recorta-se nos lados que
            // dão para o jardim e é o verde de baixo que aparece no recorte. Qual
            // dos 16 recortes usar sai dos quatro vizinhos (`pathMask`); no meio
            // do caminho a máscara é 0 e o tile fica cheio de lado a lado.
            // Um tile acima do chão (ainda muito abaixo dos tapetes, -9000).
            if (onGardenPath(c, r))
              scene.add
                .image(c * TILE, r * TILE, pathKey(pathMask(onGardenPath, c, r), (c + r) % 2 === 0))
                .setOrigin(0, 0)
                .setDepth(DEPTH_FLOOR + 1);
          }
        }
        continue; // parede interior — tratada em baixo
      }
      scene.add
        .image(c * TILE, r * TILE, floorKey(kind, (c + r) % 2 === 0))
        .setOrigin(0, 0)
        .setDepth(DEPTH_FLOOR);
    }
  }

  // ── E o lago em si, um só objecto por cima da relva. O canto sai da MESMA elipse
  // que a colisão usa (`LAKE`), não de dois números escritos à mão: o dia em que o
  // lago mudar de sítio, muda em `space.ts` e a imagem segue-o sozinha.
  //
  // Profundidade `DEPTH_RUG`: acima do chão, abaixo dos nenúfares (`DEPTH_RUG + 1`)
  // e muito abaixo de tudo o que é y-sorted — a casa flutuante, as pedras da margem
  // e a gente passam à frente da água como devem.
  if (lagoPintado) {
    scene.add
      .image((LAKE.cx - LAKE.rx) * TILE, (LAKE.cy - LAKE.ry) * TILE, LAKE_ART)
      .setOrigin(0, 0)
      .setDepth(DEPTH_RUG);
  }

  // ── Sebe-vedação: a moldura-borda do mapa (que a colisão mantém sólida) veste-se
  // de sebe, para o mundo terminar numa cerca viva de folhagem e não num corte seco
  // de relva. Ancorada pela base → sobe com altura, y-sorted como a mobília.
  //
  // O limite é `SURFACE_ROWS`, NÃO `ROWS`: desde que o bunker acrescentou uma faixa
  // subterrânea por baixo, `ROWS - 1` é a última linha do SUBSOLO, não a do jardim.
  // Usá-lo aqui deixava a sebe sul aberta (linha 51 sem cerca) e plantava um cordão
  // de arbustos catorze linhas abaixo do chão. É a mesma correcção que `buildWalls`
  // já leva em `space.ts` — a fronteira do mundo à superfície é `SURFACE_ROWS - 1`.
  for (let r = 0; r < SURFACE_ROWS; r += 1) {
    for (let c = 0; c < COLS; c += 1) {
      const border = r === 0 || c === 0 || r === SURFACE_ROWS - 1 || c === COLS - 1;
      if (!border || inLake(c, r)) continue;
      const y = (r + 1) * TILE;
      scene.add
        .image(c * TILE + TILE / 2, y, HEDGE)
        .setOrigin(0.5, 1)
        .setDepth(y);
    }
  }

  // ── Ambiente: escurece só o INTERIOR do prédio para os poços de luz das salas
  // saltarem à vista (chão fundo, luz quente em cima). Restrito ao rectângulo do
  // prédio → o jardim lá fora fica com a sua luz de dia, não uma cave. Fica sob os
  // candeeiros (ADD) e sob a mobília, que continuam vivos.
  scene.add
    .rectangle(BUILDING.col * TILE, BUILDING.row * TILE, BUILDING.w * TILE, BUILDING.h * TILE, 0x0a1220, 0.32)
    .setOrigin(0, 0)
    .setDepth(DEPTH_AMBIENT);

  // ── E o subsolo inteiro, muito mais escuro. Uma cave iluminada como um escritório
  // não é uma cave: é o contraste com o jardim lá em cima que vende os dois metros
  // de terra por cima da cabeça. Cobre a faixa toda (e não só as salas) porque a
  // rocha entre elas também não vê o sol.
  scene.add
    .rectangle(0, UNDERGROUND_TOP, MAP_WIDTH, UPSTAIRS_TOP - UNDERGROUND_TOP, 0x03060c, 0.46)
    .setOrigin(0, 0)
    .setDepth(DEPTH_AMBIENT);

  // ── O TELHADO, na faixa do 1.º andar.
  //
  // Quem sobe continua a ver o jardim à volta (é a câmara de baixo que o mostra),
  // mas não as salas nem as pessoas do rés-do-chão: o telhado cobre a planta
  // inteira do prédio, excepto onde há sala por cima. Por isso é OPACO e ocupa o
  // rectângulo do prédio tile a tile — a tela, o beiral à volta, a sombra que o
  // prédio deita no relvado, e os painéis e máquinas lá em cima.
  const topoDoTelhado = (BUILDING.row + UPSTAIRS_OFFSET_ROWS) * TILE;
  const esqDoTelhado = BUILDING.col * TILE;
  const largDoTelhado = BUILDING.w * TILE;
  const altDoTelhado = BUILDING.h * TILE;
  for (let r = BUILDING.row; r < BUILDING.row + BUILDING.h; r += 1) {
    for (let c = BUILDING.col; c < BUILDING.col + BUILDING.w; c += 1) {
      if (floorAt(c, r + UPSTAIRS_OFFSET_ROWS) != null) continue;
      scene.add
        .image(c * TILE, (r + UPSTAIRS_OFFSET_ROWS) * TILE, (c + r) % 2 === 0 ? ROOF_ALT : ROOF)
        .setOrigin(0, 0)
        .setDepth(DEPTH_FLOOR);
    }
  }
  const BEIRAL = 6;
  for (const [x, y, w, h] of [
    [esqDoTelhado, topoDoTelhado, largDoTelhado, BEIRAL],
    [esqDoTelhado, topoDoTelhado + altDoTelhado - BEIRAL, largDoTelhado, BEIRAL],
    [esqDoTelhado, topoDoTelhado, BEIRAL, altDoTelhado],
    [esqDoTelhado + largDoTelhado - BEIRAL, topoDoTelhado, BEIRAL, altDoTelhado],
  ] as const) {
    scene.add
      .rectangle(x, y, w, h, 0xbfc3c7)
      .setOrigin(0, 0)
      .setDepth(DEPTH_FLOOR + 2);
  }
  // A sombra do prédio no jardim (a sul e a nascente) e a do bloco do CEO no telhado.
  scene.add
    .rectangle(esqDoTelhado + 10, topoDoTelhado + altDoTelhado, largDoTelhado, 12, 0x000000, 0.24)
    .setOrigin(0, 0)
    .setDepth(DEPTH_FLOOR + 2);
  scene.add
    .rectangle(esqDoTelhado + largDoTelhado, topoDoTelhado + 10, 12, altDoTelhado, 0x000000, 0.24)
    .setOrigin(0, 0)
    .setDepth(DEPTH_FLOOR + 2);
  const salasDeCima = ROOMS.filter((room) => isUpstairs(room.row));
  if (salasDeCima.length > 0) {
    const col0 = Math.min(...salasDeCima.map((room) => room.col));
    const col1 = Math.max(...salasDeCima.map((room) => room.col + room.w));
    const row0 = Math.min(...salasDeCima.map((room) => room.row));
    const row1 = Math.max(...salasDeCima.map((room) => room.row + room.h));
    // A parede do bloco ocupa a coluna a seguir às salas; a sombra cai logo depois.
    scene.add
      .rectangle((col1 + 1) * TILE, (row0 - 1) * TILE + 8, 14, (row1 - row0 + 2) * TILE - 8, 0x000000, 0.22)
      .setOrigin(0, 0)
      .setDepth(DEPTH_FLOOR + 2);
    // E a mesma luz ambiente do rés-do-chão, só por cima das salas de cima.
    scene.add
      .rectangle(col0 * TILE, row0 * TILE, (col1 - col0) * TILE, (row1 - row0) * TILE, 0x0a1220, 0.32)
      .setOrigin(0, 0)
      .setDepth(DEPTH_AMBIENT);
  }
  const TEXTURA_DA_PECA = { solar: ROOF_SOLAR, ac: ROOF_AC, heliponto: ROOF_HELI } as const;
  for (const peca of PECAS_DO_TELHADO) {
    scene.add
      .image(peca.col * TILE, (peca.row + UPSTAIRS_OFFSET_ROWS) * TILE, TEXTURA_DA_PECA[peca.tipo])
      .setOrigin(0, 0)
      .setDepth(DEPTH_FLOOR + 3);
    // O helicóptero pousa ao centro do heliponto, com a sombra por baixo. Se o
    // PNG não carregou, fica só o heliponto.
    if (peca.tipo === "heliponto") {
      const { w, h } = MEDIDA_DA_PECA.heliponto;
      const cx = (peca.col + w / 2) * TILE;
      const cy = (peca.row + UPSTAIRS_OFFSET_ROWS + h / 2) * TILE;
      // Um pouco acima e à direita do meio: a cauda sobe para a esquerda, e ao
      // centro exacto o corpo ficava descaído para baixo do H.
      if (scene.textures.exists(HELICOPTERO_CORPO)) {
        // Com as pás a rodar e a descolar quando o CEO sai (ver `officeHeli`).
        new Helicoptero(scene, cx + 12, cy - 10, HELICOPTERO_CORPO, DEPTH_FLOOR + 5, { dx: 20, dy: 40, w: 126, h: 30 });
      } else if (scene.textures.exists(HELICOPTERO)) {
        scene.add.ellipse(cx + 32, cy + 30, 126, 30, 0x000000, 0.3).setDepth(DEPTH_FLOOR + 4);
        scene.add
          .image(cx + 12, cy - 10, HELICOPTERO)
          .setOrigin(0.5, 0.5)
          .setDepth(DEPTH_FLOOR + 5);
      }
    }
  }

  // ── Candeeiros de teto: um poço quente amplo + um núcleo mais quente no centro
  // de cada sala, para a luz ter foco (hotspot) sobre o chão escurecido.
  //
  // No betão do bunker a luz é FRIA. Não é decoração: é a leitura de que se mudou
  // de sítio. Lá em cima há janelas e lâmpadas de tungsténio; aqui em baixo há
  // fluorescente sobre betão, e o azul faz o subsolo parecer subsolo mesmo antes de
  // se ler o rótulo da sala.
  for (const room of ROOMS) {
    if (room.floor === "asphalt") continue; // parque exterior — luz de dia, não teto
    const cold = room.floor === "concrete";
    const key = cold ? GLOW_COOL : GLOW_WARM;
    const cx = (room.col + room.w / 2) * TILE;
    const cy = (room.row + room.h / 2) * TILE;
    const pool = scene.add
      .image(cx, cy, key)
      .setDepth(DEPTH_CEILING_LIGHT)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(room.floor === "lounge" ? 0.2 : 0.28);
    pool.setDisplaySize(room.w * TILE * 1.15, room.h * TILE * 1.15);
    const core = scene.add
      .image(cx, cy, key)
      .setDepth(DEPTH_CEILING_LIGHT)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(0.16);
    core.setDisplaySize(room.w * TILE * 0.55, room.h * TILE * 0.55);
  }

  // ── Janelas: no topo da fachada norte (a linha de parede logo acima de cada
  // sala encostada à fachada) umas colunas viram VIDRO com céu, em vez de parede
  // cega — o pedido "coloque janelas no prédio". Ficam em colunas fixas da sala.
  const windowTiles = new Set<string>();
  for (const room of ROOMS) {
    const fachada = naFachada(room); // só as salas coladas à fachada — em baixo e no 1.º andar
    if (fachada === null) continue;
    for (const dc of [2, 4, 6]) windowTiles.add(`${room.col + dc},${fachada}`);
  }

  // As portas exteriores: vãos na parede do prédio que dão para o jardim. Ficam em
  // paredes-cap (não viradas ao observador), por isso desenham-se como capa-porta
  // plana e não se tratam nem como parede cega nem como chão. Cada uma ocupa dois
  // tiles — o `EXTERIOR_DOORS` é a lista achatada desses tiles, para os saltar no
  // varrimento das paredes; os `SPANS` é que sabem o par e o eixo em que abre.
  const exteriorDoorSet = new Set(EXTERIOR_DOORS.map(([c, r]) => `${c},${r}`));
  const doors: ExteriorDoor[] = EXTERIOR_DOOR_SPANS.map((span) => makeExtDoor(scene, span));
  // As aberturas SEM porta não têm nada a montar — nem batente, nem folhas, nem
  // alcance: só se salta a pedra e mostra-se o que está por baixo dela.
  const exteriorOpeningSet = new Set(EXTERIOR_OPENINGS.map(([c, r]) => `${c},${r}`));

  // ── Paredes 2.5D. Uma parede virada ao observador (chão logo abaixo) mostra a
  // FACE, com altura; as restantes mostram a CAPA de topo, vista de cima. Os tiles
  // FORA do prédio já são jardim (relva/água) — nunca parede cinzenta até à borda.
  /**
   * Meia tile de piso de cada lado de um tramo VERTICAL de parede, com o piso do
   * vizinho respectivo — dentro à esquerda, jardim à direita (ou o contrário).
   *
   * Serve dois donos com a mesma necessidade e razões diferentes: a capa fina
   * tem margens translúcidas e sem chão por baixo abriria o fundo preto da cena;
   * a abertura sem porta não tem capa nenhuma, e sem isto era o mesmo buraco
   * preto — só que do tamanho da tile toda. Uma tile inteira de um só piso não
   * serve a nenhum dos dois: um tramo vertical separa duas coisas diferentes
   * (madeira e relva, sala e jardim) e dava um degrau falso a meio.
   *
   * QUAL textura vai de cada lado é decisão do `officeWallSkirt` — inclusive o
   * caso que faltava aqui, o vizinho ser parede maciça (dava `null` e a metade
   * ficava por pintar: era esse o vão preto). Aqui só se desenha.
   *
   * O `setCrop` é em coordenadas da TEXTURA e não mexe na posição do sprite.
   */
  const pintaMeiosPisos = (c: number, r: number): void => {
    for (const { key, x } of saiaDaParede(c, r)) {
      scene.add
        .image(c * TILE, r * TILE, key)
        .setOrigin(0, 0)
        .setDepth(DEPTH_FLOOR)
        .setCrop(x, 0, TILE / 2, TILE);
    }
  };

  for (let r = 0; r < ROWS; r += 1) {
    for (let c = 0; c < COLS; c += 1) {
      // Os tiles de vão já foram montados pelo `makeExtDoor` (batente + folhas):
      // aqui só se saltam, para a parede-capa não os tapar. Vão pisável — nem
      // parede cega nem chão.
      if (exteriorDoorSet.has(`${c},${r}`)) continue;
      // A abertura sem porta é o mesmo salto, sem nada montado a substituir a
      // pedra: pinta-se o chão que ela tapava e segue-se. É literalmente um
      // buraco na parede, e é isso que se pediu.
      if (exteriorOpeningSet.has(`${c},${r}`)) {
        pintaMeiosPisos(c, r);
        continue;
      }
      if (isOutside(c, r)) continue; // jardim — não é parede do prédio
      if (floorAt(c, r) != null) continue; // não é parede
      // No subsolo não há "fora do prédio" para travar isto (`isOutside` é falso em
      // toda a faixa), por isso sem guarda a rocha vinha pintada de ponta a ponta —
      // uma laje cinzenta de 44×14 tiles à volta de duas salas. Só se desenha pedra
      // ENCOSTADA a chão de bunker; o resto fica preto, que é o que a terra é quando
      // se corta um mundo 2.5D ao meio: vazio, não betão.
      // No 1.º andar é igual: a parede só existe à volta das salas de cima — o
      // resto é telhado, já pintado lá atrás.
      if ((isUnderground(r) || isUpstairs(r)) && !touchesFloor(c, r)) continue;
      const facing = floorAt(c, r + 1) != null; // há chão pisável em baixo
      if (facing) {
        const tex = windowTiles.has(`${c},${r}`) ? WALL_WINDOW : WALL_FACE;
        scene.add
          .image(c * TILE + TILE / 2, (r + 1) * TILE, tex)
          .setOrigin(0.5, 1)
          .setDepth((r + 1) * TILE);
      } else {
        // A capa fina só nos tramos VERTICAIS — a regra vive no `shared`, ao lado
        // do mapa que ela lê, e tem testes. Aqui só se escolhe a textura.
        const slim = isVerticalWallRun(c, r);
        // E, se for fina, pinta-se CHÃO por baixo dela. A capa fina só afina de
        // verdade porque as margens são translúcidas (ver `drawWallTopSlim`): com
        // margem opaca, a barra continua a ler-se com os 32px do tile por muito
        // que a pedra encolha. Translúcido sem nada por baixo abria o fundo preto
        // da cena — logo o chão tem de vir cá.
        if (slim) pintaMeiosPisos(c, r);
        scene.add
          .image(c * TILE, r * TILE, slim ? WALL_TOP_SLIM : WALL_TOP)
          .setOrigin(0, 0)
          .setDepth(r * TILE);
      }
    }
  }

  // ── Candeeiros de parede. Apliques quentes montados na FACE da parede de topo
  // de cada sala com nome (braço de ferro + lampião âmbar), com auréola quente
  // aditiva a lamber o chão. O bug antigo: o candeeiro caía atrás de uma estante /
  // quadro / TV encostados à parede e SUMIA. A correção: mapear as colunas tapadas
  // por mobília nas duas filas do topo (e as das janelas) e montar só em VÃOS
  // LIVRES e espaçados. Puramente visual — não toca na geometria de space.ts.
  for (const room of ROOMS) {
    if (room.name === "") continue;
    const wallRow = room.row - 1; // a linha de parede logo acima do chão da sala
    const wy = room.row * TILE; // pé da face = topo do chão

    // Colunas tapadas por mobília encostada ao topo (filas room.row e room.row+1).
    const blocked = new Set<number>();
    for (const prop of PROPS) {
      const spec = PROP_SPECS[prop.kind];
      if (prop.row > room.row + 1 || prop.row + spec.h <= room.row) continue;
      for (let dx = 0; dx < spec.w; dx += 1) {
        const pc = prop.col + dx;
        if (pc >= room.col && pc < room.col + room.w) blocked.add(pc);
      }
    }
    // As colunas de janela (só nas salas da fachada) também não levam candeeiro.
    const windowCols = new Set<number>();
    if (naFachada(room) !== null) for (const dc of [2, 4, 6]) windowCols.add(room.col + dc);

    // Candidatos por ordem de preferência (flancos → miolo); ficam os 2 primeiros
    // que estejam livres, sejam mesmo parede-face, e não colem um ao outro.
    const candidates = [
      room.col + 1,
      room.col + room.w - 2,
      room.col + 2,
      room.col + room.w - 3,
      room.col + Math.floor(room.w / 2),
      room.col + 3,
      room.col + room.w - 4,
    ];
    const picked: number[] = [];
    for (const col of candidates) {
      if (picked.length >= 2) break;
      if (picked.includes(col) || blocked.has(col) || windowCols.has(col)) continue;
      if (picked.some((p) => Math.abs(p - col) < 2)) continue; // não encostar 2 candeeiros
      // só onde há MESMO parede-face (parede em cima, chão da sala em baixo)
      if (floorAt(col, wallRow) != null || floorAt(col, room.row) == null) continue;
      picked.push(col);
    }

    for (const col of picked) {
      const lx = col * TILE + TILE / 2;
      scene.add
        .image(lx, wy - 4, WALL_LAMP)
        .setOrigin(0.5, 1)
        .setDepth(wy + 2);
      const pool = scene.add
        .image(lx, wy + TILE * 1.1, GLOW_WARM)
        .setDepth(DEPTH_CEILING_LIGHT)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setAlpha(0.32);
      pool.setDisplaySize(TILE * 3.2, TILE * 3.4);
    }
  }

  // ── Mobília. Cada prop ancorado pela base ao fundo da sua pegada → sobe e
  // sobrepõe-se pelo y-sort. Os tapetes ficam planos, colados ao chão.
  // Props PLANOS (assentes no chão, top-down): o tapete e as riscas do parque colam
  // ao chão em DEPTH_RUG; os carros ficam logo acima das riscas, mas sob a mobília
  // e os bonecos. Nenhum leva sombra-elipse nem se ergue — desenham-se de origem 0,0.
  // A escotilha do bunker entra aqui pela mesma razão do tapete: é uma peça de
  // CHÃO, com a arte do tamanho exacto da pegada. Erguida como um móvel, ganhava
  // uma sombra-elipse de contacto a flutuar por baixo de uma tampa que está
  // assente no relvado — uma sombra debaixo do chão.
  //
  // Os quatro carros entram pela mesma porta, e pela mesma razão: são vistos de
  // CIMA. Erguidos como mobília, ganhavam uma sombra-elipse a sair-lhes de baixo
  // do pára-choques traseiro — uma sombra de peça vista de lado por baixo de uma
  // peça vista de cima, que é o mais depressa que se desmancha a ilusão de que o
  // parque tem chão.
  // A escada e o poço também: são degraus vistos de cima, com o corrimão rente.
  const FLAT_PROPS = new Set([
    "rug",
    "parkingLines",
    "carSedan",
    "carSport",
    "carSuper",
    "carSuv",
    "bunkerHatch",
    "stairs",
    "stairwell",
  ]);
  // Mobília que é VEGETAÇÃO: apanha o mesmo vento do jardim, mais contido porque
  // está dentro de casa.
  //
  // O canteiro esteve aqui e SAIU, e fica de fora mesmo tendo a arte do pacote
  // voltado: o `baloico` roda a peça INTEIRA, e três quartos do canteiro são a
  // caixa de pedra — via-se alvenaria a oscilar meio grau, que é metade da razão
  // por que se lia torto. Vaso e planta balançam por serem folhagem de alto a
  // baixo; obra de pedra não balança.
  const SWAY_PROPS = new Set(["plant", "plantTall"]);
  // As casas do stand, e SÓ elas, são normalizadas à largura da pegada.
  //
  // A razão é o plano B. Quando o PNG carrega, a peça já vem com a largura exacta
  // da pegada e a conta dá 1 — não faz nada. Quando o PNG falha, entra o desenho
  // à mão do `officeArt`, e esse sai sempre a 2× do tamanho pedido em `texture()`,
  // sem forma de pedir outro: uma casa que a pegada diz ter 224 px chegava com
  // 160 e ficava uma maqueta a meio de um lugar vazio. Aqui volta ao tamanho que
  // o mundo já reservou para ela. Fica restrito a estas seis chaves de propósito
  // — há mobília cuja arte é MAIS ESTREITA do que a pegada de propósito (a
  // consola de seis monitores, por exemplo), e essa não pode ser esticada.
  const PARK_HOME_PROPS = new Set([
    "mobileHome",
    "mobileHomeFlip",
    "mobileHomeSide",
    "mobileHomeSideFlip",
    "mobileHomeArch",
    "mobileHomeArchFlip",
  ]);
  // A sombra de contacto das casas mudou-se para o `officeShadow`, com a
  // medição de cada arte, a regra que dela tira os números e os testes que a
  // seguram. Aqui ficou só o desenho. Viveu neste ficheiro como constante local
  // e foi por isso que se partiu duas vezes em silêncio — nenhum teste chega a
  // uma constante dentro de um componente.
  for (const prop of PROPS) {
    const spec = PROP_SPECS[prop.kind];
    if (FLAT_PROPS.has(prop.kind)) {
      const depth = prop.kind === "rug" || prop.kind === "parkingLines" ? DEPTH_RUG : DEPTH_RUG + 2;
      scene.add
        .image(prop.col * TILE, prop.row * TILE, propKey(prop.kind))
        .setOrigin(0, 0)
        .setDepth(depth);
      // A escotilha é a única peça plana com partes móveis: por cima do batente
      // montam-se as duas tampas de aço, e entra na MESMA lista das portas
      // exteriores para o `update` lhe medir a proximidade com o mesmo código.
      if (prop.kind === "bunkerHatch") {
        doors.push(makeHatchDoor(scene, prop.col * TILE, prop.row * TILE, depth + 1));
      }
      continue;
    }
    const baseX = (prop.col + spec.w / 2) * TILE;
    const baseY = (prop.row + spec.h) * TILE;

    // ── Peça FUNDA (o sofá de perfil): uma imagem por LINHA de chão que ocupa.
    //
    // O y-sort desta cena dá uma profundidade por imagem e tira-a do `baseY`, ou
    // seja da linha da FRENTE. Numa peça de três linhas isso é uma armadilha: o
    // móvel inteiro ficava à frente de quem se sentasse nas duas linhas de trás,
    // e duas das três pessoas desapareciam dentro do sofá em que se sentaram.
    // Fatiado, cada pedaço leva a profundidade da SUA linha e o problema deixa
    // de existir — não é um remendo, é o mesmo y-sort a ser-lhe finalmente
    // aplicado à medida certa. A arte já vem preparada (ver `drawCouchSide`).
    const fatias = propSlices(prop.kind);
    if (fatias > 0) {
      // A mancha que assenta a peça no chão é UMA só, por baixo de todas as
      // fatias (daí a profundidade sair da linha de TRÁS). Uma por fatia dava
      // três manchas soltas com chão limpo pelo meio, como se o sofá tivesse
      // três pares de pernas — e onde se sobrepusessem ficava o dobro do escuro.
      scene.add
        .ellipse(baseX, (prop.row + spec.h / 2) * TILE, spec.w * TILE * 1.3, spec.h * TILE * 0.9, 0x05070b, 0.3)
        .setDepth((prop.row + 1) * TILE - 0.5);
      for (let i = 0; i < fatias; i++) {
        const fatiaY = (prop.row + i + 1) * TILE;
        scene.add.image(baseX, fatiaY, propSliceKey(prop.kind, i)).setOrigin(0.5, 1).setDepth(fatiaY);
      }
      continue;
    }

    // ── Encosto do lugar à mesa.
    // `space.ts` já chega o CORPO à secretária e centra-o com ela (`seatAnchor`).
    // A CADEIRA tem de fazer exactamente o mesmo movimento, ou a pessoa senta-se
    // ao lado do próprio móvel onde está a cadeira. Lê-se o delta da MESMA função
    // — não se repete a regra aqui — para os dois nunca poderem divergir.
    const lugar = isLayeredSeat(prop.kind) ? seatAt((prop.col + 0.5) * TILE, (prop.row + 0.5) * TILE) : null;
    const encosto = lugar ? seatAnchor(lugar) : null;
    // O deslocamento entra só na POSIÇÃO desenhada. A profundidade continua a sair
    // de `baseY` limpo: uns pixels a mais aqui punham o assento à frente de quem
    // lá está sentado (`seatedDepth`), e o boneco desaparecia dentro da cadeira.
    // ── A bancada MONTA na base da parede.
    //
    // Ela já acaba na última coluna de chão da sala: a pegada toca a parede ao
    // pixel, e mais longe não vai sem sair da sala. Mas a parede interior é
    // clara e lê-se como chão, e uma bancada que acaba nessa aresta parece ter
    // uma folga que não tem. Numa cozinha a sério o tampo encosta E cobre o
    // rodapé — o que faltava era a arte tapar essa faixa, não a peça andar.
    //
    // Só a ARTE se desloca. A pegada, a colisão e o y-sort ficam onde estavam,
    // que é o que permite fazer isto sem tocar em `space.ts`: quem passa
    // continua a contornar exactamente os quatro tiles de sempre.
    //
    // A direcção sai do MAPA e não de um sinal escrito à mão. Um `+10` fixo
    // funcionava aqui e enfiava a bancada dentro da sala no dia em que alguém a
    // encostasse à parede esquerda — olha-se para o tile a seguir à peça e monta-se
    // para o lado onde o chão acaba. Sem parede de nenhum lado (uma bancada no
    // meio da sala), não se mexe nada.
    const encostaNaParede =
      prop.kind === "counter"
        ? (floorAt(prop.col + spec.w, prop.row) === null ? MONTA_NA_PAREDE : 0) -
          (floorAt(prop.col - 1, prop.row) === null ? MONTA_NA_PAREDE : 0)
        : 0;

    const drawX = baseX + encostaNaParede + (encosto && lugar ? encosto.x - (lugar.col + 0.5) * TILE : 0);
    const drawY = baseY + (encosto && lugar ? encosto.y - (lugar.row + 0.5) * TILE : 0);

    // ── A casa do lago não assenta em chão nenhum: flutua. Sombra, oscilação e
    // ondas não chegavam para o dizer, e a razão é anterior a tudo isso: o casco
    // estava INTEIRO por cima da água, quilha e tudo. Nenhum barco se vê assim —
    // um corpo que mostra o fundo todo está pousado ou está no ar, nunca dentro
    // de água. Enquanto a silhueta completa estivesse à vista, qualquer marca por
    // baixo só podia ler-se como sombra projectada, ou seja, como voo.
    //
    // A primeira tentativa recortou o casco abaixo da linha de água (`setCrop`).
    // Escondia os pixels certos e continuava a não convencer: a água não chegava
    // a TOCAR na casa. Faltava-lhe massa por cima — o casco acabava numa aresta
    // e o lago começava do outro lado dela, dois planos encostados em vez de um
    // corpo metido dentro do outro.
    //
    // O que resolve é somar água POR CIMA. A casa desce `CALADO` px e uma LÂMINA
    // do próprio lago volta a ser desenhada à frente dela: o que fica abaixo da
    // linha desaparece por ficar debaixo de água, não por ter sido cortado fora.
    // A lâmina é um segundo recorte da MESMA imagem do lago, colocada no MESMO
    // sítio do mundo — logo alinha ao pixel com a água de baixo e não há costura
    // nem tom para acertar. E como a linha de água está PARADA e é a casa que
    // sobe e desce, ao subir mostra mais casco e ao descer afunda-se: é esse
    // desencontro entre um corpo que se move e uma superfície que não, que se lê
    // como boiar.
    if (prop.kind === "floatingHouse") {
      // 8 px de 98 de arte, e já foram 20. A 20 a água entrava pela VARANDA da
      // frente — a peça mais baixa da silhueta e a única que uma casa flutuante a
      // sério tem sempre seca; daí para baixo foi tudo a pedido, até a água só
      // encostar ao pontão. Baixar o calado é a correcção certa (e não descer a
      // lâmina): a linha de água é `drawY`, o mesmo sítio do mundo onde o recorte
      // do lago já cai, e é desse alinhamento ao pixel que vem o não haver costura.
      // Mexer nela para tapar menos casco pagava-se em seam; mexer no calado sai
      // de graça, porque só a casa e o seu balanço o lêem.
      //
      // E o `BALANCO` deixou de ser um `- 3` solto dentro do tween. É o número que
      // manda no piso do calado: a casa levanta-se `BALANCO` px no topo do ciclo,
      // portanto um calado que não o exceda com folga tira o casco inteiro da água
      // uma vez por ciclo — e uma casa que emerge periodicamente lê-se como casa a
      // saltar, não a boiar. Com 8 e 2 sobram 6 px molhados no pior instante.
      const CALADO = 8;
      const BALANCO = 2;
      const linhaDeAgua = drawY;
      const casa = scene.add
        .image(drawX, drawY + CALADO, propKey(prop.kind))
        .setOrigin(0.5, 1)
        .setDepth(baseY);
      // Só vertical, `BALANCO` px. Rodar (o balanço que uma casa destas teria a sério)
      // obriga o Phaser a reamostrar a imagem, e a nitidez que o PNG foi buscar
      // fora do browser perdia-se em cada fotograma.
      scene.tweens.add({
        targets: casa,
        y: drawY + CALADO - BALANCO,
        duration: 2600,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      });

      // A lâmina, à frente do casco (`baseY + 1`) e larga o suficiente para as
      // ondas caberem dentro dela. Os limites saem do footprint e do `LAKE`, não
      // de números escritos à mão — o dia em que o lago ou a casa mudarem de
      // sítio, a lâmina segue-os. 48 px de fundo chegam para tapar o calado e
      // param muito antes da margem, onde o PNG do lago já esmorece.
      const meiaLargura = spec.w * TILE * 0.5 + TILE;
      const laminaX = drawX - meiaLargura;
      const LAMINA_H = 48;
      if (lagoPintado)
        scene.add
          .image((LAKE.cx - LAKE.rx) * TILE, (LAKE.cy - LAKE.ry) * TILE, LAKE_ART)
          .setOrigin(0, 0)
          .setDepth(baseY + 1)
          .setCrop(
            laminaX - (LAKE.cx - LAKE.rx) * TILE,
            linhaDeAgua - (LAKE.cy - LAKE.ry) * TILE,
            meiaLargura * 2,
            LAMINA_H,
          );
      // Plano B (o PNG faltou): a mesma frame de onda dos tiles, alinhada ao grid
      // — `laminaX` e `linhaDeAgua` caem em múltiplos de `TILE`, portanto a fase
      // do padrão continua a bater com a água em volta.
      else
        scene.add
          .tileSprite(laminaX, linhaDeAgua, meiaLargura * 2, LAMINA_H, WATER_FRAMES[0]!)
          .setOrigin(0, 0)
          .setDepth(baseY + 1);

      // ── O reflexo. É este o sinal que fecha a questão, e não a lâmina: um
      // corpo POUSADO em cima de uma superfície não se reflecte nela — só um
      // corpo METIDO lá dentro é que devolve imagem. A mesma arte, virada ao
      // contrário a partir da linha de água, esmagada ao terço (a água não é um
      // espelho: encurta o que copia) e quase transparente, para ser reflexo e
      // não uma segunda casa ao contrário. Cresce para BAIXO, o que é a razão
      // prática de ser seguro — nunca toca no telhado, que é o que qualquer
      // recorte por cima arriscava cortar.
      const REFLEXO = 0.42;
      const reflexo = scene.add
        .image(drawX, linhaDeAgua + 2, propKey(prop.kind))
        .setOrigin(0.5, 0)
        .setFlipY(true)
        .setScale(1, REFLEXO)
        .setAlpha(0.16)
        .setDepth(baseY + 2);
      // Em fase com o balanço e ao contrário dele: quando a casa sobe, o reflexo
      // estica e desmaia (mais água entre os dois), quando desce, junta-se a ela.
      scene.tweens.add({
        targets: reflexo,
        scaleY: REFLEXO * 1.12,
        alpha: 0.1,
        duration: 2600,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      });

      // ── Tudo o que é perturbação da SUPERFÍCIE fica à frente da lâmina, e todo
      // abaixo da linha de água: é água mexida, não pintura em cima do casco.
      //
      // Aqui esteve uma elipse escura (`0x0a2d44` a 34%) por baixo do casco, a
      // fazer de sombra na água. Saiu, e não por gosto: **um corpo dentro de água
      // não projecta sombra na superfície em que está metido** — projectaria no
      // FUNDO, que não se vê. Uma mancha escura à superfície, logo debaixo do
      // casco, é exactamente a leitura que este ramo inteiro passou o tempo a
      // combater: lê-se como sombra de um corpo que PAIRA. Tinha o efeito ao
      // contrário do pretendido, e por cima escondia a água que devia estar a
      // mexer-se. O que assenta a casa dentro do lago é a lâmina (que lhe come o
      // casco) e o reflexo (que só um corpo submerso devolve) — não uma sombra.
      //
      // E os anéis deixaram de nascer DEBAIXO da casa. Nasciam a 0,8× da largura
      // do casco, ou seja, dentro da pegada dela: só se via a parte que escapava
      // por diante, e essa lia-se como poça por baixo. Passam a nascer no próprio
      // CONTORNO (1,0×) e só crescem para fora, que é o que a água deslocada faz
      // — sai do corpo, nunca se junta debaixo dele. Três, desencontrados a um
      // terço do ciclo, para nunca haver um instante de água parada.
      const ANEL_W = spec.w * TILE;
      const ANEL_H = TILE * 0.44;
      const ANEL_MAX = 1.3;
      for (let i = 0; i < 3; i += 1) {
        // O centro a +10 e a altura a 0,44 tile não são arbitrários: no ponto
        // mais aberto do anel o arco de cima fica em `10 - (14,08/2)·1,3 ≈ +0,9`,
        // ainda ABAIXO da linha de água. Um anel que a passasse desenhava um fio
        // claro por cima do casco — está à frente da lâmina, não seria cortado.
        const onda = scene.add
          .ellipse(drawX, linhaDeAgua + 10, ANEL_W, ANEL_H)
          .setStrokeStyle(1, 0xd6efff, 0.5)
          .setDepth(baseY + 3);
        scene.tweens.add({
          targets: onda,
          scaleX: ANEL_MAX,
          scaleY: ANEL_MAX,
          alpha: 0,
          duration: 3000,
          delay: i * 1000,
          repeat: -1,
          ease: "Sine.easeOut",
        });
      }
      // E a linha de contacto: dois fios de espuma encostados ao casco, em fase
      // com o balanço (a casa desce, desloca mais água, a espuma abre). Sem eles
      // a lâmina corta o casco a direito e volta a ler-se como aresta.
      for (let i = 0; i < 2; i += 1) {
        const espuma = scene.add
          .ellipse(
            drawX + (i === 0 ? -1 : 1) * TILE * 0.7,
            linhaDeAgua + 3 + i * 3,
            spec.w * TILE * (0.4 + i * 0.12),
            4,
            0xe8f6ff,
            0.5,
          )
          .setDepth(baseY + 5);
        scene.tweens.add({
          targets: espuma,
          alpha: 0.18,
          scaleX: 0.84,
          duration: 2600,
          delay: i * 900,
          yoyo: true,
          repeat: -1,
          ease: "Sine.easeInOut",
        });
      }
      continue;
    }

    // Sombra de contacto: a mancha escura que assenta o prop no chão (2.5D).
    // Fica logo abaixo dele e sobre o chão escurecido → dá volume e peso.
    //
    // A forma por omissão — sem subir, 0,84 da pegada, ao direito e centrada — só
    // está certa para mobília que pousa a direito na linha de baixo da sua arte,
    // que é toda menos as casas. Para essas manda o `officeShadow`, e ele dá duas
    // formas: as casas de CAIXA levam a própria PLANTA (`PLANTA_CASA`, o polígono
    // medido no pé da arte) e as restantes uma elipse afinada (`SOMBRA_CASA`).
    const planta = PLANTA_CASA[prop.kind];
    const pe = SOMBRA_CASA[prop.kind];
    // O `larg` das casas conta-se na ARTE, como o `sobe`, o `dx` e o `ang` — que
    // sempre foram px de arte. Em nove das dez entradas os dois números são o
    // MESMO (a arte foi desenhada a 32 px por tile: 160/160, 288/288, 224/224…),
    // por isso a tabela acima não mexeu um dígito. A décima é a `homeCapsule`:
    // 224 px de arte numa pegada de 6 tiles = 192, e nascia 14% estreita de graça
    // sem nada no ficheiro a dizer porquê. Contada na arte deixa de depender da
    // pegada — que é uma caixa de COLISÃO e pode mudar por outros motivos.
    // As casas do parque levam `setScale` para a pegada (ver `PARK_HOME_PROPS`
    // logo abaixo), portanto para essas a pegada É a largura no ecrã.
    const larguraNoEcra = PARK_HOME_PROPS.has(prop.kind)
      ? spec.w * TILE
      : (scene.textures.exists(propKey(prop.kind))
          ? scene.textures.get(propKey(prop.kind)).getSourceImage().width
          : 0) || spec.w * TILE;
    // A planta já vem em px desta mesma origem — x do meio da arte, y da linha de
    // baixo dela —, por isso vai tal e qual. O `setOrigin(0, 0)` é o que a faz
    // valer: o Phaser desenha cada ponto de um `Shape` em `posição + ponto −
    // displayOrigin`, e o `displayOrigin` por omissão é MEIA caixa envolvente, o
    // que atirava a sombra meia casa para trás e para cima. Não leva `ang`: a
    // inclinação do contacto são as próprias arestas medidas.
    const sombra = planta
      ? scene.add.polygon(drawX, drawY, planta, 0x05070b, 0.3).setOrigin(0, 0)
      : scene.add.ellipse(
          drawX + (pe?.dx ?? 0),
          drawY - 2 - (pe?.sobe ?? 0),
          pe ? larguraNoEcra * pe.larg : spec.w * TILE * 0.84,
          TILE * (pe?.alt ?? 0.52),
          0x05070b,
          0.3,
        );
    sombra.setDepth(baseY - 0.5);
    if (!planta && pe?.ang) sombra.setRotation(Phaser.Math.DegToRad(pe.ang));

    if (isLayeredSeat(prop.kind)) {
      // Cadeira: entra na cena aberta em duas metades, com o lugar para o corpo no
      // meio. A direcção é a MESMA que `space.ts` dá a quem ali se senta (olhar
      // para a mesa) — daí ir buscá-la ao lugar em vez de a inventar aqui, senão o
      // encosto podia ficar de um lado e as costas do outro.
      const facing = lugar?.facing ?? "down";
      scene.add
        .image(drawX, drawY, seatPropKey(prop.kind, facing, "base"))
        .setOrigin(0.5, 1)
        .setDepth(baseY);
      scene.add
        .image(drawX, drawY, seatPropKey(prop.kind, facing, "front"))
        .setOrigin(0.5, 1)
        .setDepth(seatBackrestDepth(prop.row));
      continue;
    }

    const corpo = scene.add.image(drawX, drawY, propKey(prop.kind)).setOrigin(0.5, 1).setDepth(baseY);
    if (PARK_HOME_PROPS.has(prop.kind) && corpo.width > 0) corpo.setScale((spec.w * TILE) / corpo.width);
    if (SWAY_PROPS.has(prop.kind)) baloico(scene, corpo, prop.col * 3 + prop.row * 7, 0.012, 3800);

    // O relógio de pé é o sítio de bater o ponto — e diz-se, por cima dele,
    // colado ao topo da arte. Dourado como o mostrador, para se ler como parte
    // da peça e não como mais um nome de sala.
    if (prop.kind === "grandfatherClock") {
      scene.add
        .text(drawX, drawY - corpo.displayHeight - 3, "PONTO", {
          fontFamily: "Inter, system-ui, sans-serif",
          fontSize: "10px",
          fontStyle: "bold",
          color: "#f2d98a",
          stroke: "#10161d",
          strokeThickness: 3,
        })
        .setOrigin(0.5, 1)
        .setResolution(3)
        .setDepth(baseY + 1);
    }

    // Ecrãs acesos. O CRT do posto brilha VERDE (fósforo) e acende no ecrã o NOME
    // DO CARGO — a mesma sala que dispara a call da equipa (Vendas, TI). A TV
    // brilha frio. Aditivo → ilumina o escritório escurecido.
    if (prop.kind === "deskPC" || prop.kind === "salesDesk") {
      // As duas secretárias acendem o MESMO fósforo, e nenhuma delas o acende no
      // mesmo sítio — os ecrãs estão onde o pacote os desenhou, e é do PNG que
      // saem estes números (nunca estimados; se a arte mudar, medem-se de novo).
      //
      //   VENDAS  — arte 64×46, painel único na coluna do meio, corrida contígua
      //             de verde em y 2..16 → centro a 9 do topo, `baseY - 46 + 9`.
      //   TI      — arte 96×54 (era 64×56 até 2026-08-26). São TRÊS monitores, e
      //             o grande está x 22..55 de 96, ou seja o centro do vidro cai
      //             10 px à ESQUERDA do meio do móvel; a banda dos três ecrãs
      //             ocupa x 3..69 e y 0..24 → centro vertical em `baseY - 44`.
      //             O antigo `baseY - 23` era o meio de um CRT que já não existe:
      //             nesta mesa cairia no teclado, tal como caía no de Vendas.
      const vendas = prop.kind === "salesDesk";
      const glow = scene.add
        .image(baseX - (vendas ? 0 : 10), baseY - (vendas ? 37 : 44), GLOW_GREEN)
        .setDepth(baseY + 1)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setAlpha(0.34);
      // O painel de Vendas mede 23×15; o brilho leva ~1,4× disso para ler como
      // emissão e não como um autocolante. Na TI a mancha acesa é a fila inteira
      // de monitores, por isso é larga (2,2 tiles ≈ os 66 px de x 3..69) e baixa
      // (0,9 ≈ os 24 px em que os três cabem) — mais alta transbordava a arte.
      glow.setDisplaySize(vendas ? TILE : TILE * 2.2, TILE * (vendas ? 0.66 : 0.9));
      const deskRoom = roomAt(baseX, (prop.row + 0.5) * TILE);
      // O nome do cargo vai DENTRO do ecrã grande, sobre o diagrama de rede, que
      // é a peça que se olha. O de Vendas já traz um painel de gestão pintado, por
      // isso a etiqueta sobe para cima do monitor (topo da arte = `baseY - 46`) em
      // vez de tapar a única parte da peça que se olha.
      scene.add
        .text(baseX - (vendas ? 0 : 10), baseY - (vendas ? 52 : 44), (deskRoom?.name ?? ">_").toUpperCase(), {
          fontFamily: "ui-monospace, 'Courier New', monospace",
          fontSize: "8px",
          color: "#7dfcae",
          stroke: "#04120a",
          strokeThickness: 2,
        })
        .setOrigin(0.5, 0.5)
        .setResolution(3)
        .setDepth(baseY + 0.5);
    } else if (prop.kind === "deskDual") {
      // A bancada de TI acende DOIS ecrãs, e os dois não estão simétricos: o
      // pacote desenhou-os com os centros em x=69,5 e x=96,5 do ficheiro, o que
      // dá -20 e +34 px do meio do móvel depois do recorte e da escala 2×
      // (ver `officeFurniture.ts`). Um brilho único ao centro ficava entre os
      // dois monitores, a iluminar a madeira. Estes valores foram MEDIDOS no
      // PNG, não estimados — se a arte mudar, medem-se outra vez.
      //
      // Sem nome de sala por cima: os três postos individuais logo acima já o
      // escrevem, e três etiquetas iguais na mesma sala eram ruído.
      for (const dx of [-20, 34]) {
        const glow = scene.add
          .image(baseX + dx, prop.row * TILE + TILE * 0.5, GLOW_GREEN)
          .setDepth(baseY + 1)
          .setBlendMode(Phaser.BlendModes.ADD)
          .setAlpha(0.28);
        glow.setDisplaySize(TILE * 1.8, TILE * 1.3);
      }
    } else if (prop.kind === "tv") {
      const glow = scene.add
        .image(baseX, prop.row * TILE + TILE * 0.3, GLOW_COOL)
        .setDepth(baseY + 1)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setAlpha(0.3);
      glow.setDisplaySize(TILE * (spec.w + 1), TILE * 1.6);
    } else if (prop.kind === "streetLamp") {
      // Poço de luz quente no alcatrão + brilho na própria luminária (3.5 tiles acima).
      const pool = scene.add
        .image(baseX, baseY - TILE * 0.4, GLOW_WARM)
        .setDepth(DEPTH_RUG + 1)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setAlpha(0.26);
      pool.setDisplaySize(TILE * 3.4, TILE * 2.2);
      const head = scene.add
        .image(baseX, baseY - TILE * 3.0, GLOW_WARM)
        .setDepth(baseY + 1)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setAlpha(0.42);
      head.setDisplaySize(TILE * 1.5, TILE * 1.5);
    }
  }

  // Patos, pássaros e a luz da hora real (ver `officeVida`).
  new VidaDoJardim(scene, GLOW_WARM, Phaser.BlendModes.ADD, PATO);

  // ── Jardim: vida à volta do prédio (o pedido "faça um ambiente em torno da área,
  // com plantas, gramas, talvez algum lago com pedras"). Arbustos e flores nas
  // margens de relva, y-sorted como a mobília.
  //
  // Estas posições são puramente visuais — um arbusto é rente ao chão e passa-se
  // por cima dele. As das ÁRVORES já não: desde *"o tronco das árvores deve ter
  // física"* vivem em `TREES` (`space.ts`), porque o tronco entra na grelha de
  // colisão e essa é partilhada. Entram nesta lista aqui em baixo, para levarem o
  // mesmo y-sort, o mesmo vento e as mesmas folhas de sempre.
  //
  // Os arbustos são NOVE espécies para dezasseis moitas, e a escolha de qual vai
  // a cada canteiro está escrita peça a peça de propósito: onde o mapa é
  // simétrico o par leva a MESMA espécie (as duas pontas da faixa do topo, os
  // dois lados do lago a norte, as duas encostas altas), e nas faixas de relva
  // estreitas — as de três colunas, a poente e a nascente — só entram as
  // variantes estreitas. A sebe larga tem 83 px de folhagem para 96 de faixa;
  // posta lá, encostava folhas ao alcatrão. Um sorteio não sabe nada disto.
  const arb = (variante: string, recurso: string) => arbusto(scene, variante, recurso);
  const scenery: { key: string; col: number; row: number }[] = [
    // Faixa de relva ESQUERDA (cols 0-2) — arbustos entre as coníferas do `TREES`.
    { key: arb(ARBUSTO_ESPINHOSO, BUSH), col: 1.7, row: 11 },
    { key: arb(ARBUSTO_BAGAS, BUSH), col: 1.6, row: 27 },
    { key: arb(ARBUSTO_ALTO, BUSH_SMALL), col: 2.2, row: 5.5 },
    // Faixa de relva DIREITA (agora larga, cols 34-42).
    { key: arb(ARBUSTO_VARIEGADO, BUSH), col: 35.4, row: 14 },
    { key: arb(ARBUSTO_BAGAS, BUSH), col: 41.0, row: 9 },
    { key: arb(ARBUSTO_ALTO, BUSH_SMALL), col: 36.0, row: 5.5 },
    // Esta emoldura a `homeModular` (cols 35-40, pé na linha 30) em vez de a
    // atravessar. Estava DENTRO dela, a tapar-lhe a frente. A causa é de ordenação,
    // não de gosto — a vegetação desenha em `row × 32` e entra na cena DEPOIS dos
    // props, portanto com profundidade igual OU maior ganha sempre a planta. A
    // régua para não repetir: à frente de uma casa, uma peça de cenário tem de
    // ficar abaixo do pé dela (`row + h`); ao lado, fora das colunas da pegada. Não
    // toca no rectângulo x[1120,1312] y[844,960] que a casa ocupa.
    { key: arb(ARBUSTO_SEBE, BUSH), col: 40.6, row: 32.2 }, // sudeste
    // RELVADO SUL alargado (rows 24-32) — anilha de vegetação a RODEAR o lago (a água
    // ocupa o meio, cols ~11-22, rows ~26-31). Norte, oeste, este e sul do lago.
    { key: arb(ARBUSTO_LARGO, BUSH), col: 7.5, row: 24.6 },
    { key: arb(ARBUSTO_LARGO, BUSH), col: 27.2, row: 24.4 }, // NE do lago, já do lado de lá do carreiro
    { key: arb(ARBUSTO_RASTEIRO, BUSH_SMALL), col: 8.0, row: 29 },
    { key: arb(ARBUSTO_VARIEGADO, BUSH), col: 29.0, row: 31.6 }, // à frente da cúpula, abaixo do pé
    // A SUL do lago não vai nada. Estas três peças foram postas "entre a água e a
    // borda" quando o lago acabava na linha 31,1; ao passar a imagem pintada ele
    // cresceu para baixo (ry 2,6 → 3,5) e engoliu-as — ficaram plantas de terra
    // dentro de água. A anilha fecha pelo norte, oeste e este, que é onde há relva.
    // Faixa do TOPO (rows 0-2) — arbustos baixos a espreitar por cima do telhado.
    { key: arb(ARBUSTO_REDONDO, BUSH_SMALL), col: 2.4, row: 1.6 },
    { key: arb(ARBUSTO_REDONDO, BUSH_SMALL), col: 41.0, row: 1.6 },
    { key: arb(ARBUSTO_FLORIDO, BUSH_SMALL), col: 12.0, row: 1.6 },
    // ESCOTILHA DO BUNKER (col 40, row 3) — seis moitas a partir-lhe a silhueta
    // sem tapar o vão por onde as folhas abrem. A composição e a medida de cada
    // peça estão em `MOITAS_BUNKER`; aqui entra pela porta normal do jardim, com
    // o mesmo y-sort e o mesmo vento das outras.
    ...MOITAS_BUNKER.map((m) => ({ key: arb(m.variante, m.recurso), col: m.col, row: m.row })),
    // PARQUE DE MOBILE HOMES (sul, rows 35-51) — enchimento entre as coníferas dos
    // quatro cantos do lote (essas estão no `TREES`).
    { key: arb(ARBUSTO_ESPINHOSO, BUSH), col: 1.8, row: 44 }, // enchimento relva oeste
    // Estava em 41,0 e ficava atrás da casa de arco — com a pegada dela em 7×4 a
    // arte passou a chegar à coluna 42, e como a casa é y-sorted mais abaixo (a
    // profundidade sai da linha do chão, 48, contra a 44 da sebe) tapava-a por
    // inteiro. 42,4 põe-na para lá da fachada, na faixa de relva que sobra.
    { key: arb(ARBUSTO_BAGAS, BUSH), col: 42.4, row: 44 }, // enchimento relva este
  ];
  // As árvores entram aqui vindas do `space.ts`, que é onde passaram a viver por
  // causa da colisão do tronco. O `alta` deixou de ser adivinhado pela textura (um
  // `Set` de chaves) e passa a vir da lista de onde a peça veio: árvore é o que
  // está em `TREES`, e mais nada. À frente dos arbustos porque em profundidade
  // igual ganha quem entra na cena depois — e uma copa por cima de uma moita é a
  // ordem certa (a única linha empatada é a 14, e lá o par continua na mesma ordem).
  const ARVORE: Record<TreeSize, string> = { small: TREE_SMALL, medium: TREE, big: TREE_BIG };
  const jardim = [
    ...TREES.map((t) => ({ key: ARVORE[t.size], col: t.col, row: t.row, alta: true })),
    ...scenery.map((s) => ({ ...s, alta: false })),
  ];
  const plantas: PhaserT.GameObjects.Image[] = [];
  for (const s of jardim) {
    const px = s.col * TILE;
    const py = s.row * TILE;
    const img = scene.add.image(px, py, s.key).setOrigin(0.5, 1).setDepth(py);
    plantas.push(img);
    // Vento. A árvore, que é alta e tem copa, dobra mais e mais depressa do que o
    // arbusto rente ao chão — a mesma diferença que se vê num jardim a sério.
    const alta = s.alta;
    baloico(scene, img, s.col * 3 + s.row * 7, alta ? 0.018 : 0.01, alta ? 3400 : 4200);
    if (!alta) continue;
    // A pegada sai da textura que ESTÁ lá, não de uma tabela escrita à mão: a
    // árvore do pacote (`officeFlora`) mede outra coisa que a conífera, e as
    // folhas têm de nascer na copa desta e não na da anterior.
    const size = { w: img.width, h: img.height };
    // Partículas de folhas: nascem na copa, planam para baixo com deriva e rodam
    // enquanto desvanecem. Puramente decorativo, determinístico (sem RNG externo).
    scene.add
      .particles(px, py, LEAF, {
        x: { min: -size.w * 0.34, max: size.w * 0.34 },
        y: { min: -size.h * 0.85, max: -size.h * 0.42 },
        lifespan: { min: 4200, max: 6400 },
        speedX: { min: -13, max: 13 },
        speedY: { min: 5, max: 16 },
        gravityY: 7,
        scale: { min: 0.7, max: 1.15 },
        rotate: { start: 0, end: 300 },
        alpha: { start: 0.95, end: 0 },
        frequency: 1700,
        quantity: 1,
      })
      .setDepth(py + 2);
  }

  // Chuva, neve, nevoeiro, ventania e pirilampos (ver `officeClima`).
  new Clima(scene, plantas, LEAF, LAKE_ART);

  // ── Lago: a água e mais nada. Houve aqui um cordão de pedras na relva
  // encostada à água — saía errado por construção: o `inLake` é a ELIPSE
  // inscrita na caixa da imagem, portanto os tiles vizinhos da elipse junto aos
  // cantos da caixa caem em relva rasa, longe da água que se vê. O resultado era
  // um arco de pedras a flutuar no jardim sem margem nenhuma por baixo. A margem
  // do lago já vem pintada na própria arte — não precisa de adorno.
  //
  // Houve também cinco nenúfares, retirados a pedido. Eram eles que carregavam o
  // movimento da superfície: com o lago pintado numa só imagem, o `waterTiles`
  // lá em cima nasce VAZIO e o timer de frames logo a seguir nunca chega a
  // correr. Sem os nenúfares a água é um decalque parado — foi a troca escolhida,
  // e fica escrito aqui para quem vier procurar a animação que não encontra.

  // ── Vida na água: um timer cicla os 4 frames de onda. Com a fase de cada tile,
  // a superfície ondula em vez de piscar toda igual — mata o "azul seco".
  if (waterTiles.length > 0) {
    let waterFrame = 0;
    scene.time.addEvent({
      delay: 480,
      loop: true,
      callback: () => {
        waterFrame = (waterFrame + 1) % WATER_FRAMES.length;
        for (const { img, phase } of waterTiles) {
          img.setTexture(WATER_FRAMES[(waterFrame + phase) % WATER_FRAMES.length] ?? WATER_FRAMES[0]!);
        }
      },
    });
  }

  // ── Etiqueta de cada sala, discreta — sobre o chão, sob a gente.
  //
  // O sítio NÃO se decide aqui: vem de `ROOM_LABELS`, junto da planta, onde a
  // faixa que cada nome precisa é a mesma que a mobília tem de respeitar. Era
  // por serem duas decisões separadas que "SALA DE REUNIÃO" acabou atrás do
  // quadro branco — agora só há uma, e um teste guarda-a.
  for (const label of ROOM_LABELS) {
    scene.add
      .text(label.x, label.y, label.text.toUpperCase(), {
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: "13px",
        color: "#f2f6fb",
        stroke: "#10161d",
        strokeThickness: 4,
      })
      .setOrigin(0.5, 0.5)
      .setAlpha(0.72)
      .setResolution(2)
      .setDepth(DEPTH_ROOM_LABEL);
  }

  // ── E a boca de cada teletransporte, marcada com um poço de luz fria.
  //
  // SEM texto por cima: *"retire esse texto entrar no bunker"*. Havia uma legenda
  // flutuante em cada boca, e no jardim era ruído — a escotilha explica-se sozinha
  // (vê-se o aro de betão e as tampas de aço) e a legenda só tapava relva.
  //
  // O que ela resolvia continua resolvido, por duas peças que ficam: o halo, que
  // pulsa e cobre a área de alcance INTEIRA (o desenho a dizer até onde a tecla
  // funciona), e o aviso do canto, que só aparece de perto e aí sim escreve para
  // onde se vai. Era esse par que corrigia o "mesmo assim ele não saiu do bunker";
  // a legenda no mundo era a terceira cópia da mesma informação.
  for (const port of TELEPORTS) {
    const { x: cx, y: cy } = teleportCenter(port);
    const shaft = scene.add
      .image(cx, cy, GLOW_COOL)
      .setDepth(DEPTH_RUG + 4)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(0.34);
    // A marca cobre a ÁREA DE ALCANCE inteira: é o desenho a dizer, sem uma
    // linha de texto, exactamente até onde a tecla funciona.
    shaft.setDisplaySize((port.zone.w + 0.9) * TILE, (port.zone.h + 0.9) * TILE);
    scene.tweens.add({
      targets: shaft,
      alpha: 0.16,
      duration: 1700,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });
  }

  return doors;
}

/**
 * A call que o ESCRITÓRIO abriu, por servidor — e pela mesma razão que a posição
 * acima: fora do React, para sobreviver à remontagem.
 *
 * Era uma `useRef`, e uma ref morre com o componente. Carregar em «Discord»
 * desmonta o escritório inteiro (`AppShell`), portanto essa ref renascia a
 * `null` e a propriedade da call perdia-se de duas maneiras, ambas erradas:
 *
 * - à SAÍDA, o cleanup desligava a call **sempre**. Só que trocar de vista não
 *   é sair da sala — o avatar fica lá, a posição fica guardada, e voltava-se ao
 *   escritório dentro da sala e fora da chamada. Era este o *"eu clico no chat,
 *   volto, e saí da call"*.
 * - à VOLTA, ninguém sabia que a call era nossa, por isso sair para o corredor
 *   deixava de a desligar.
 *
 * Um mapa por servidor resolve os dois: a call pertence à SESSÃO no servidor,
 * não à montagem do componente.
 */
const callDoEscritorio: Map<Snowflake, Snowflake> = import.meta.hot?.data?.callDoEscritorio ?? new Map();
if (import.meta.hot?.data) import.meta.hot.data.callDoEscritorio = callDoEscritorio;

export function OfficeSpace({ serverId }: { serverId: Snowflake }) {
  const voiceSession = useStore((s) => s.voice);
  const voiceActions = useVoiceActions();
  // A VERDADE da ligação é a do MOTOR, não a da store: a store guarda a INTENÇÃO
  // ("pedi para entrar neste canal") e sobrevive a uma queda do media, por isso
  // dizia "Ligado" com o motor em baixo — um chip verde por cima de controlos
  // inertes, que é exactamente como uma call partida se disfarça de call boa.
  const voiceSnapshot = useVoiceSnapshot();
  const mountRef = useRef<HTMLDivElement>(null);
  const [room, setRoom] = useState<Room | null>(null);
  /**
   * O nome do sítio, para o cabeçalho — e é uma STRING e não uma sala porque
   * "Lá fora" e "Subsolo" não são salas nenhumas. Era essa a raiz do rótulo
   * errado: o cabeçalho só tinha `room`, e desenhava "Corredor" para tudo o que
   * viesse null, jardim e bunker incluídos. Quem manda nele é o `placeName`, do
   * `space.ts`, para o nome do sítio não ter duas versões.
   */
  const [place, setPlace] = useState("Corredor");
  /**
   * O lugar emprestado na barra fixa de cima, onde os controlos deste ecrã vão
   * viver (ver `TopBar`).
   *
   * Procurado num efeito e não durante o render porque durante o render o DOM
   * ainda é o do commit ANTERIOR — o `getElementById` de um primeiro render
   * devolveria nulo mesmo com a barra a ser montada no mesmo commit. Os efeitos
   * correm depois do DOM estar escrito, e nessa altura o lugar está lá.
   *
   * Uma vez só (`[]`) porque a barra é filha directa do `AppShell` e não depende
   * de nada que mude: nunca é desmontada enquanto a app viver, e é precisamente
   * por isso que ela existe. Se um dia passar a ser condicional, este efeito tem
   * de passar a depender dessa condição — senão fica-se com um nó órfão.
   */
  const [slotDaBarra, setSlotDaBarra] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setSlotDaBarra(document.getElementById(TOPBAR_SLOT_ID));
  }, []);
  /**
   * A call que se vê por cima do mundo — DERIVADA da sessão de voz, nunca uma
   * cópia dela.
   *
   * Era um `useState` alimentado só pelo `onRoomChange`, ou seja: o ecrã da
   * chamada só sabia das calls que o AVATAR abriu ao andar. Isso dava dois
   * sintomas que são o mesmo bug — *"a miniatura nunca mais aparece"*. Entrar
   * numa sala pela LISTA do modo chat não abria miniatura nenhuma (ninguém se
   * mexeu, logo ninguém avisou o React); e abrir o chat DESMONTA este componente
   * (`inOffice`, no `AppShell`), por isso ao voltar o estado renascia a `null` e
   * a deteção do spawn (`enter=false`) voltava a apagá-lo — com a call bem viva
   * por trás. Derivar mata os dois de uma vez: a miniatura passa a ser A CHAMADA,
   * e não a memória de quem a abriu.
   *
   * `serverId` no meio porque o escritório é de UM servidor: uma call noutro não
   * tem nada que abrir um ecrã aqui. `callMin` encolhe-a para um canto sem sair
   * da call — o mundo 2D é a vista dona e continua navegável por trás.
   *
   * O dono da call lê-se do MAPA DE CANAIS, não do campo `serverId` da sessão —
   * e é aqui que morre o resto do *"quando saio e entro da sala pelo modo chat, a
   * miniatura nunca mais aparece"*. Esse campo nasce optimista em `useVoice.join`
   * (`channels[channelId]?.serverId ?? null`): entrar num canal que ainda não
   * chegou à store grava **null** e o `=== serverId` fica falso PARA SEMPRE,
   * porque nada mais lhe volta a mexer. Dava exactamente o retrato dele: o sinal
   * "Em chamada" aceso (só olha para `voiceSession`) e o botão "Miniatura" em
   * lado nenhum — só o F5 a repunha. O mapa, esse, é reactivo: quando o canal
   * chegar, a miniatura aparece sozinha.
   *
   * Desconhecido ⇒ é desta casa. Preferir mostrar de menos custa uma call
   * invisível sem porta de volta; mostrar de mais custa um cartão a mais num
   * canto, que se fecha no ×.
   */
  const callServerId = useStore((s) =>
    s.voice ? (s.channels[s.voice.channelId]?.serverId ?? s.voice.serverId) : null,
  );
  const callId = voiceSession && (callServerId === null || callServerId === serverId) ? voiceSession.channelId : null;
  // Arranca MINIMIZADA (canto): entrar numa sala abre só PARTE do GreenVoice por
  // cima do mundo, nunca o ecrã inteiro — o escritório 2D continua a vista dona.
  // Na STORE e não aqui: a barra de cima passou a ser a porta de volta ao mundo
  // (*"é só a pessoa clicar em escritório novamente que ela volta pra lá"*), e
  // para isso tem de mexer nisto. Duas cópias sincronizadas por um efeito eram
  // duas verdades à espera de divergir.
  const callMin = useStore((s) => s.officeCallMin);
  const setCallMin = useStore((s) => s.setOfficeCallMin);
  // A miniatura pode ser FECHADA (o × da barra) sem se sair da chamada, e volta
  // pelo botão do cabeçalho. Sem esse par, o cartão era obrigatório: tapava um
  // canto do mundo e a única maneira de o tirar dali era desligar a call.
  const [callShown, setCallShown] = useState(true);

  /**
   * O ecrã da chamada TAPA o mundo. Uma pergunta só, feita num sítio só: decide
   * se a vista é ainda o escritório 2D ou já o GreenVoice por cima dele, e é
   * dela que dependem o cabeçalho da chamada, a barra de ajuda do rodapé e o
   * salto para o modo chat quando a chamada acaba em ecrã cheio.
   */
  const callEmEcraCheio = callId !== null && callShown && !callMin;

  /**
   * E quem está de fora precisa de saber: a barra de cima lê esta bandeira para
   * decidir o que a sua porta diz. Enquanto a chamada tapa o mundo, quem está no
   * escritório está a ver o GreenVoice — e o botão que dizia «Discord» estava a
   * apontar para onde a pessoa já estava.
   *
   * A limpeza no desmonte é a metade que não se vê: sair do escritório (ir ao
   * chat, trocar de servidor) deixava a bandeira levantada e a barra a oferecer
   * «Escritório» a partir de uma vista onde ele nem existe.
   */
  const setOfficeCallFull = useStore((s) => s.setOfficeCallFull);
  useEffect(() => {
    setOfficeCallFull(callEmEcraCheio);
    return () => setOfficeCallFull(false);
  }, [callEmEcraCheio, setOfficeCallFull]);

  /**
   * *"quando eu clico em expandir na miniatura e saiu da call, o discord fecha,
   * devemos manter ele aparecendo"* — e o Discord dele é o `office__callnav`, a
   * coluna que só existe DENTRO do ecrã cheio da chamada. Toda ela está
   * pendurada no `callId`; a call a acabar levava-a com ela e devolvia o mapa a
   * quem estava a trabalhar num painel de canais.
   *
   * O ecrã cheio é uma escolha de VISTA, não da chamada: quem o abriu deixou o
   * mundo 2D para trás de propósito. Então a chamada acaba e a vista fica —
   * `viewMode: "chat"`, que é a mesma barra de canais mais a conversa e a lista
   * de membros. Encolhida ou fechada não mexe em nada (o mapa já estava à
   * vista), e a decisão inteira, com a guarda da troca de servidor, vive no
   * `saiuDeEcraCheio` — aqui só ficam os efeitos.
   *
   * A leitura é do render ANTERIOR, guardada numa ref: quando o `callId` chega a
   * `null` já não há maneira de saber como é que ele estava no ecrã.
   */
  const setViewMode = useStore((s) => s.setViewMode);
  const ecraDaCall = useRef<EcraDaCall>({ servidor: serverId, call: callId, cheia: false });
  useEffect(() => {
    const antes = ecraDaCall.current;
    const agora: EcraDaCall = { servidor: serverId, call: callId, cheia: callEmEcraCheio };
    ecraDaCall.current = agora;
    if (saiuDeEcraCheio(antes, agora)) setViewMode("chat");
  }, [serverId, callId, callEmEcraCheio, setViewMode]);

  /**
   * O canal de voz DESTA sala, quando existe e o podemos abrir — a mesma regra
   * do `onRoomChange` (nome igual, sem distinguir maiúsculas, mais o teste de
   * `CONNECT`), aqui em versão reactiva para o botão de entrar.
   *
   * Devolve um id ou `null`, nunca um objecto: a store compara por identidade e
   * um objecto novo a cada leitura re-renderizava o escritório sem parar.
   */
  const salaVoz = useStore((s) => {
    if (!room) return null;
    const alvo = channelsOf(s, serverId).find(
      (c) => c.type === "VOICE" && c.name.toLowerCase() === room.name.toLowerCase(),
    );
    return alvo && can(selfPermissions(s, serverId, alvo), Permission.CONNECT) ? alvo.id : null;
  });

  /**
   * Estou DENTRO da sala e FORA da call dela — o estado que não tinha botão. É
   * também o *"mostra que estou na call e não estou"*: o cabeçalho lê a
   * `voiceSession` (a intenção) e o painel "quem está onde" lista-me na sala por
   * GEOMETRIA; nenhum dos dois olha para o motor. Ver `officeCall.ts`.
   */
  const foraDaCallDaSala = foraDaCall(salaVoz, { canal: voiceSession?.channelId ?? null, motor: voiceSnapshot.state });

  // ── PIP da chamada: movível + redimensionável (estilo Discord) ──
  // O cartão de canto tinha posição fixa por CSS e ZERO handlers — daí o "só fica
  // no canto, não movo nem redimensiono". Guardo posição/tamanho em px: null =
  // ancorado ao canto (default CSS); ao arrastar/redimensionar passa a `fixed`
  // (classe `--float`) e o inline `style` manda. Arrasta-se pela barra, o canto
  // redimensiona; os botões (Expandir, controlos) NÃO iniciam arrasto — o guard
  // `closest("button")` deixa o clique passar. Listeners no WINDOW (não no
  // elemento): o gesto segue o cursor mesmo saindo do cartão, e o <canvas> do
  // Phaser não os intercepta (o pointerdown foi na barra, não no jogo).
  const callScreenRef = useRef<HTMLDivElement>(null);
  const [callRect, setCallRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const callDrag = useRef<{
    mode: "move" | "resize";
    px: number;
    py: number;
    start: { x: number; y: number; w: number; h: number };
  } | null>(null);
  const beginCallDrag = (mode: "move" | "resize") => (e: ReactPointerEvent<HTMLElement>) => {
    if (!callMin) return; // só faz sentido no cartão de canto
    if ((e.target as HTMLElement).closest("button")) return; // deixa o botão clicar
    const r = callScreenRef.current?.getBoundingClientRect();
    // `fixed` ⇒ coordenadas do viewport: getBoundingClientRect mapeia 1:1.
    const start = callRect ?? (r ? { x: r.left, y: r.top, w: r.width, h: r.height } : { x: 24, y: 24, w: 300, h: 260 });
    e.preventDefault();
    e.stopPropagation();
    callDrag.current = { mode, px: e.clientX, py: e.clientY, start };
    const clampN = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);
    const onMove = (ev: PointerEvent) => {
      const d = callDrag.current;
      if (!d) return;
      const dx = ev.clientX - d.px;
      const dy = ev.clientY - d.py;
      if (d.mode === "move") {
        setCallRect({
          ...d.start,
          x: clampN(d.start.x + dx, 0, window.innerWidth - d.start.w),
          y: clampN(d.start.y + dy, 0, window.innerHeight - d.start.h),
        });
      } else {
        setCallRect({
          ...d.start,
          w: clampN(d.start.w + dx, 232, window.innerWidth - d.start.x),
          h: clampN(d.start.h + dy, 200, window.innerHeight - d.start.y),
        });
      }
    };
    const onUp = () => {
      callDrag.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };
  // À porta de QUE sala estamos (null = de nenhuma). Guarda-se o id, não um
  // booleano: em cima da soleira `room` é null (o vão fica na linha de parede
  // ENTRE as divisões) e era exactamente aí que o aviso desaparecia — no passo em
  // que se chega à porta. Vem do loop do Phaser, só na transição.
  const [doorRoomId, setDoorRoomId] = useState<string | null>(null);
  // A porta TRANCADA a que estamos encostados vindos DE FORA — a que se pode
  // bater. Simétrico exacto do `doorRoomId` (esse é o ferrolho, de dentro).
  const [knockRoomId, setKnockRoomId] = useState<string | null>(null);
  // O móvel ao alcance da mão (quadro, posto, televisão) — null quando não há
  // nenhum. É o que decide o que a tecla E faz e o que o aviso mostra.
  const [prop, setProp] = useState<PropTarget | null>(null);
  // A escotilha que espera pela TECLA, ou null — e é só isso que este estado é.
  // A tampa do jardim anuncia-se e espera, porque pisá-la de passagem engolia
  // quem só ia a andar. A saída lá de baixo já não espera por nada (sobe-se ao
  // chegar ao patamar); só se anuncia no instante em que está travada, para quem
  // acabou de descer poder voltar já.
  const [hatch, setHatch] = useState<Teleport | null>(null);
  // A bola de neve: se há neve para apanhar, se está a ser feita, se está na mão.
  const [bola, setBola] = useState<EstadoDaBola>("sem_neve");
  // A escala da câmara. Vive aqui só para o botão do canto poder mostrar quanto
  // é e para se saber quando desligar o "+" ou o "−" — quem manda nela é a cena.
  const [zoom, setZoom] = useState(CAMERA_ZOOM);
  // O quadro branco, aberto por E em cima do quadro da Sala de Reunião. Fica
  // MONTADO depois de aberto uma vez (`open` é só visibilidade): fechá-lo a
  // sério perdia os traços que continuam a chegar dos outros.
  const [boardOpen, setBoardOpen] = useState(false);
  const [boardMounted, setBoardMounted] = useState(false);
  // A mesa de jogos segue a mesma regra, e por um motivo mais forte ainda:
  // fechar o painel a meio de uma partida não pode ser desistir dela.
  const [gameOpen, setGameOpen] = useState(false);
  const [gameMounted, setGameMounted] = useState(false);
  // A arcade, idem — e aqui montar cedo tem uma segunda função: é o painel que
  // responde a quem chega e pede o volante. Quem o fechou continua a guardar o
  // lugar na fila de quem está a ver.
  const [arcadeOpen, setArcadeOpen] = useState(false);
  const [arcadeMounted, setArcadeMounted] = useState(false);
  // A tela de Bater Ponto, aberta pelo relógio de pé. Ao contrário dos jogos
  // não fica montada: não há nada a seguir enquanto está fechada, e reabrir lê
  // o ponto de novo ao painel — que é o que se quer.
  const [pontoOpen, setPontoOpen] = useState(false);
  // O mapa grande do minimapa (tecla M, ou um clique no disco).
  const [mapaAberto, setMapaAberto] = useState(false);
  // O painel lateral "quem está onde" e a caixa de texto do balão.
  const [whoOpen, setWhoOpen] = useState(false);
  const [sayOpen, setSayOpen] = useState(false);
  // Ecrã de toque: joystick, botões de chat/mapa, tocar no chão para ir, e
  // dois dedos para o zoom. A direcção do joystick vive num ref, lido pelo
  // ciclo do Phaser como as teclas.
  const toque = useIsTouch();
  // Para o ciclo do Phaser, que não relê props: há browsers que entregam o dedo
  // como rato (o emulador Android), e tocar no chão tem de andar na mesma.
  const toqueRef = useRef(toque);
  toqueRef.current = toque;
  const direccaoToque = useRef<Direccao>({ x: 0, y: 0 });
  // Estável: o chat regista um ouvinte de "carregar fora" que depende dela.
  const fecharChat = useCallback(() => setSayOpen(false), []);
  // Estamos numa conversa de corredor? Só serve à barra do canto — quem manda
  // nisto é a geometria, no loop.
  const [huddleOn, setHuddleOn] = useState(false);

  // A barra de ajuda do rodapé, arrumável. Quem já sabe os comandos lê-a uma vez
  // e depois ela é só uma faixa a comer altura ao mundo — mas apagá-la de vez
  // deixava sem resposta quem entra pela primeira vez. Fica a escolha, e fica
  // GUARDADA no dispositivo: arrumá-la a cada F5 era a mesma chatice outra vez.
  // O inicializador do `useState` corre uma vez só, e é a leitura que se quer —
  // um `useEffect` a repor o valor depois de montar dava um piscar da barra.
  const [hintHidden, setHintHidden] = useState(() => loadOfficePrefs().hintHidden);

  // A mesma coluna do modo chat, com a mesma escolha: recolhida, o ecrã da
  // chamada fica com os 190px dela. É de propósito que a preferência é UMA —
  // quem arruma a lista de salas quer o palco maior, e o palco é o mesmo dos
  // dois lados da porta.
  const navMini = useSidebarCollapsed();

  // Quem tem esta sala trancada agora (null = porta aberta). É o mesmo mapa que o
  // loop lê imperativamente; aqui é reactivo, para o cabeçalho e o aviso mudarem
  // no instante em que outra pessoa corre o ferrolho.
  const lockedBy = useStore((s) => (room ? (s.roomLocks[serverId]?.[room.id] ?? null) : null));
  const lockedByName = useStore((s) => {
    if (!lockedBy) return null;
    if (s.self?.id === lockedBy) return "ti";
    const u = s.users[lockedBy];
    return u ? (u.displayName ?? u.username) : "alguém";
  });
  // O estado da porta a que estamos encostados — pode não ser a sala do cabeçalho
  // (na soleira não se está em sala nenhuma), por isso é uma leitura à parte.
  const doorLocked = useStore((s) => (doorRoomId ? (s.roomLocks[serverId]?.[doorRoomId] ?? null) : null));
  const doorRoomName = ROOMS.find((r) => r.id === doorRoomId)?.name ?? "";

  // O estado da LIGAÇÃO, porque a tranca depende dela: quem manda no ferrolho é o
  // servidor (é ele que valida a posição e responde com `RoomLockUpdate`), logo
  // sem sessão viva a tecla E não tem para onde ir. Ver o efeito de re-entrada
  // mais abaixo — e o aviso, que passa a dizê-lo em vez de ficar calado.
  const connectionStatus = useStore((s) => s.connectionStatus);
  const linked = connectionStatus === "ready";

  /**
   * Onde o avatar está AGORA, fora do React (o loop escreve-lhe a 60fps sem
   * re-render). Serve para reentrar no espaço a dizer já a posição certa, em vez
   * de reaparecer no spawn ou de ficar invisível até ao passo seguinte.
   */
  // Nasce onde este escritório ficou da última vez (voltar do chat), ou no SPAWN
  // na primeira entrada da sessão. Cópia, não a referência do Map — o loop escreve
  // aqui a 60fps e não queremos mutar a memória guardada a cada frame.
  const posRef = useRef({ ...(corpoLembrado(serverId) ?? { x: SPAWN.x, y: SPAWN.y, facing: "down" as Facing }) });

  // A camada de voz vista de fora do React (o loop do Phaser vive lá fora):
  // guardo as acções numa ref para o cleanup do efeito lhes chegar, e o id do
  // canal que ESTE escritório abriu — só saímos de calls que fomos nós a abrir,
  // nunca de uma que o utilizador juntou à mão pela vista de chat.
  const voiceRef = useRef(voiceActions);
  voiceRef.current = voiceActions;
  /** A call que ESTE escritório abriu (ver `callDoEscritorio`), ou `null`. */
  const callDaSala = useCallback(() => callDoEscritorio.get(serverId) ?? null, [serverId]);
  const marcarCallDaSala = useCallback(
    (channelId: Snowflake | null) => {
      if (channelId === null) callDoEscritorio.delete(serverId);
      else callDoEscritorio.set(serverId, channelId);
    },
    [serverId],
  );

  // A deteção de sala corre no loop do Phaser (fora do React); comunica de volta
  // só quando MUDA de sala, através desta ref — nunca a 60fps.
  const onRoomChange = useRef<(r: Room | null, enter?: boolean) => void>(() => {});
  // Idem para o aviso do ferrolho: chega só quando se entra ou sai do alcance da
  // porta, nunca a cada frame.
  const onDoorPrompt = useRef<(roomId: string | null) => void>(() => {});
  onDoorPrompt.current = (roomId) => setDoorRoomId(roomId);
  // Idem para a porta a que se pode BATER e para o móvel ao alcance: o loop
  // chama-os só na transição, nunca a 60fps.
  const onKnockPrompt = useRef<(roomId: string | null) => void>(() => {});
  onKnockPrompt.current = (roomId) => setKnockRoomId(roomId);
  // O nome do sítio para o cabeçalho, à parte do `onRoomChange`: este só pinta
  // uma palavra, aquele abre e fecha chamadas.
  const onPlaceChange = useRef<(nome: string) => void>(() => {});
  onPlaceChange.current = (nome) => setPlace(nome);
  const onPropPrompt = useRef<(target: PropTarget | null) => void>(() => {});
  onPropPrompt.current = (target) => setProp(target);
  const onBolaEstado = useRef<(estado: EstadoDaBola) => void>(() => {});
  onBolaEstado.current = (estado) => setBola(estado);
  /** O mesmo que a tecla F: apanhar neve, ou atirar a bola a quem está à frente. */
  const bolaAcao = useRef<() => void>(() => {});
  const onHatchPrompt = useRef<(port: Teleport | null) => void>(() => {});
  onHatchPrompt.current = (port) => setHatch(port);
  // O zoom nos dois sentidos: a cena avisa quando muda (roda do rato, teclas), e
  // os botões do canto pedem-lhe uma escala nova. Só o `onZoom` re-renderiza.
  const onZoom = useRef<(z: number) => void>(() => {});
  onZoom.current = (z) => setZoom(z);
  const zoomCtl = useRef<(next: number) => void>(() => {});
  /** O salto em si, dado pela cena — o botão do aviso chama isto (ver `create`). */
  const useHatch = useRef<(port: Teleport) => void>(() => {});

  /**
   * Usar o móvel — o que a tecla E (e o botão do aviso) fazem com ele.
   *
   * Atalhos para coisas que já existem: o quadro branco abre o `Whiteboard` da
   * sala, as mesas de jogo abrem o jogo e o relógio de pé o ponto. Nenhum inventa
   * funcionalidade nova — é mobília a servir de porta.
   *
   * A televisão já NÃO está nesta lista: juntava à call de quem partilhava o
   * ecrã sem se entrar na sala. O ecrã vê-se entrando na sala (ver `PROP_ACTIONS`).
   * Os computadores também não: o canal da equipa abre-se no botão Discord.
   */
  const onPropUse = useRef<(target: PropTarget) => void>(() => {});
  onPropUse.current = (target) => {
    if (target.action === "whiteboard") {
      setBoardMounted(true); // montar antes de abrir: o quadro liga-se à call
      setBoardOpen(true);
      return;
    }

    if (target.action === "game") {
      setGameMounted(true); // montar antes de abrir: a mesa liga-se à call
      setGameOpen(true);
      return;
    }

    if (target.action === "rush") {
      setArcadeMounted(true); // idem: a arcade liga-se à call ao montar
      setArcadeOpen(true);
      return;
    }

    if (target.action === "clock") setPontoOpen(true);
  };

  /** Abrir a caixa do balão de texto (tecla T, ou o botão do rodapé). */
  const onSay = useRef<() => void>(() => {});
  onSay.current = () => setSayOpen(true);

  // As teclas estão a ser de outra coisa? O loop lê isto para não pôr o avatar
  // a andar com o "w" de uma palavra — nem a atravessar o escritório enquanto o
  // dono delas está a desviar-se de um cone. Ref e não state: é lido a 60fps.
  //
  // A arcade é o primeiro painel que usa as SETAS. O quadro e a mesa de jogos
  // são de rato, por isso durante meses bastou olhar para a caixa de texto —
  // e no dia em que a arcade entrou, conduzir passou a arrastar o avatar pela
  // sala ao mesmo tempo.
  // O ecrã CHEIO da chamada entra pela mesma porta: tapa o mundo por completo,
  // por isso o WASD não tem para onde olhar — andava-se às cegas, e agora que a
  // coluna das salas vive lá dentro há caixas de texto (renomear um canal) que
  // arrastavam o avatar a cada letra. O cartão de canto NÃO conta: aí o mundo
  // está à vista e andar enquanto se fala é metade da graça disto.
  //
  // E o QUADRO BRANCO entra pela porta óbvia, que faltava: ele tem uma
  // ferramenta de TEXTO, e escrever nela com o mundo à escuta era escrever com o
  // boneco a andar.
  //
  // Isto é a SEGUNDA metade da defesa, e a menor: quem impede o jogo de engolir
  // as letras é o `stopPropagation` da própria caixa (o Phaser ouve na window e
  // faz `preventDefault` a WASD/setas/E/T; o React ouve antes, na raiz). Este
  // gate trata do resto — a tecla que já estava EM BAIXO quando a caixa abriu,
  // cujo `keyup` o jogo nunca chega a ver, continuaria a empurrar o boneco.
  const teclasOcupadasRef = useRef(false);
  teclasOcupadasRef.current =
    sayOpen || arcadeOpen || boardOpen || pontoOpen || mapaAberto || (callId !== null && !callMin);

  // A chamada em ecrã cheio tapa o mundo e desmonta o minimapa: um mapa aberto
  // por baixo dela ficava a prender o teclado sem ninguém o ver.
  useEffect(() => {
    if (callId !== null && !callMin) setMapaAberto(false);
  }, [callId, callMin]);

  // Uma ligação privada suspende a call automática das salas. Quando acaba,
  // reconcilia-se com a sala onde se está: quem desliga dentro de Vendas volta à
  // call de Vendas sem ter de sair e voltar a entrar.
  const ligacaoEmCurso = useStore((s) => s.ligacao?.estado === "em_curso");
  const estavaEmLigacao = useRef(false);
  useEffect(() => {
    if (ligacaoEmCurso) {
      estavaEmLigacao.current = true;
      return;
    }
    if (!estavaEmLigacao.current) return;
    estavaEmLigacao.current = false;
    const { x, y } = posRef.current;
    onRoomChange.current(roomAt(x, y), true);
  }, [ligacaoEmCurso]);

  /**
   * Levar o avatar a um sítio — o que o painel "quem está onde" usa. O caminho
   * é calculado no loop (é lá que estão as posições), por isso isto é só o fio
   * que o React puxa; quem o ata é o efeito do Phaser.
   */
  const goTo = useRef<(userId: Snowflake) => void>(() => {});

  /** Levar o avatar a um PONTO do mapa — o que o minimapa usa. `false` = não há caminho. */
  const irPara = useRef<(x: number, y: number) => boolean>(() => false);

  /** O trajecto automático em curso, escrito pelo loop e lido pelo minimapa (a rota do GPS). */
  const rotaRef = useRef<Rota | null>(null);

  /**
   * Carregar numa PESSOA do mapa abre o cartão dela — o mesmo cartão do chat e
   * da lista de membros, e não um menu novo só para aqui (ver `UserCard`).
   *
   * O ponto guardado é da JANELA e é onde o cartão se agarra. Guarda-se num
   * estado só (e não em três) porque é isso que o torna um objecto ESTÁVEL: a
   * âncora do cartão é derivada dele, e uma âncora nova a cada render punha o
   * cartão a recalcular-se para sempre.
   */
  const [picked, setPicked] = useState<{ userId: Snowflake; x: number; y: number } | null>(null);
  const onPickPerson = useRef<(userId: Snowflake | null, x: number, y: number) => void>(() => {});
  onPickPerson.current = (userId, x, y) => {
    setPicked(userId ? { userId, x, y } : null);
  };
  const pickedUser = useStore((s) => (picked ? (s.users[picked.userId] ?? null) : null));
  // A âncora tem de ser o MESMO objecto entre renders — o cartão mede-se quando
  // ela muda, e um objecto novo a cada render era uma medição sem fim.
  const pickedAnchor = useMemo(() => (picked ? { x: picked.x, y: picked.y } : null), [picked]);
  const closePicked = useCallback(() => setPicked(null), []);

  /**
   * A âncora do huddle em que estamos (o menor id do grupo) — ou null. É a
   * IDENTIDADE da conversa: muda quando o grupo muda de forma, e é por comparar
   * com ela que se sabe se há alguma coisa a fazer.
   */
  const huddleAnchorRef = useRef<string | null>(null);
  /** Corta respostas de pedidos que já não interessam (saímos entretanto). */
  const huddleGenRef = useRef(0);

  /**
   * Entrar e sair da conversa de corredor — chamado pelo loop na TRANSIÇÃO.
   *
   * A regra do huddle é geográfica (ver `huddleZone`): fora de salas, perto de
   * alguém. Não há botão de entrar nem de sair porque não é preciso — anda-se.
   *
   * Duas cautelas que valem o comentário:
   *  - só se liga se NÃO houver call de sala (`voice === null`); a call de sala
   *    é uma decisão explícita e ganha sempre;
   *  - ao desligar, confirma-se outra vez que não há call de sala. Entrar numa
   *    sala tira-nos da zona de huddle E abre a call da equipa: sem esta guarda,
   *    o fim do huddle desligava a call que acabou de começar.
   */
  const onHuddle = useRef<(anchorId: string | null) => void>(() => {});
  onHuddle.current = (anchorId) => {
    // Numa ligação privada o corredor não liga nem desliga nada — senão sair do
    // pé de alguém desligava o motor, e com ele a ligação.
    if (useStore.getState().ligacao?.estado === "em_curso") {
      huddleAnchorRef.current = null;
      setHuddleOn(false);
      return;
    }
    if (anchorId === huddleAnchorRef.current) return;
    huddleGenRef.current += 1;
    const gen = huddleGenRef.current;
    huddleAnchorRef.current = anchorId;

    if (anchorId === null) {
      setHuddleOn(false);
      if (useStore.getState().voice === null) voiceEngine.disconnect();
      return;
    }
    if (useStore.getState().voice !== null) {
      huddleAnchorRef.current = null; // numa call de sala: o corredor não interrompe
      return;
    }

    setHuddleOn(true);
    void (async () => {
      try {
        const result = await api.voice.joinHuddle(serverId, anchorId);
        // Entre o pedido e a resposta pode ter-se andado: se a âncora já não é
        // esta, o token que chegou é de uma conversa que já não é a nossa.
        if (gen !== huddleGenRef.current) return;
        // A MESMA função do `join` de canal: entra-se mudo. Aqui é onde mais
        // importa — um huddle começa por se ANDAR até perto de alguém, não por
        // um clique, e um microfone que abrisse por si transmitia o que se
        // estivesse a dizer na secretária ao lado. E como o huddle não tem
        // `voiceState` na API (o `sync` desiste com `voice === null`), este
        // `micEnabled` não tem quem o corrija: é a única palavra sobre o assunto.
        await voiceEngine.connect(connectMuted(result));
      } catch {
        // Sem SFU ou sem permissão: desiste em silêncio e volta ao estado de
        // quem está só a passar. Um erro vermelho por passar ao lado de alguém
        // seria pior do que o próprio problema.
        if (gen !== huddleGenRef.current) return;
        huddleAnchorRef.current = null;
        setHuddleOn(false);
      }
    })();
  };

  /**
   * Entrar no espaço — a cada ligação NOVA, não uma vez na vida.
   *
   * O servidor guarda os espaços em que estamos na sessão do SOCKET, e ignora em
   * silêncio posições e trancos de quem não consta lá. Duas maneiras de nunca lá
   * constar: mandar o `EnterSpace` antes do socket abrir (`send()` deita fora o
   * que não vai por ligação aberta — e é o caso normal do F5 a cair direito no
   * escritório), ou ter entrado numa ligação que depois caiu e voltou, já com
   * outra sessão vazia do outro lado. Em ambos os casos andava-se pelo mapa e a
   * tecla E não fazia nada — sem erro nenhum, que é o pior dos casos.
   *
   * Daí depender do `connectionStatus`: sempre que a ligação fica de pé, voltamos
   * a entrar e dizemos onde estamos. `EnterSpace` é idempotente no servidor, por
   * isso repeti-lo não custa nada além do pacote.
   */
  useEffect(() => {
    if (!linked) return;
    const gw = gatewayClient();
    if (!gw) return;
    gw.enterSpace(serverId);
    const { x, y, facing } = posRef.current;
    gw.updatePosition(serverId, Math.round(x), Math.round(y), facing);
  }, [serverId, linked]);

  /**
   * Uma chamada NOVA traz a miniatura de volta.
   *
   * Fechar o cartão é um gesto sobre ESTA call ("agora quero o mapa todo"), não
   * uma preferência para sempre: sem este reposicionar, quem fechasse a
   * miniatura em Vendas entrava depois em TI e ficava sem ecrã nenhum, a olhar
   * para um botão do cabeçalho que teria de descobrir sozinho.
   */
  useEffect(() => {
    if (callId !== null) setCallShown(true);
  }, [callId]);

  /**
   * Mexer no ferrolho da porta a que estamos encostados — o mesmo que a tecla E
   * faz, aqui para quem anda de rato. Nunca se assume o resultado: o servidor
   * revalida a posição e é o `RoomLockUpdate` dele que muda o mundo.
   */
  const toggleLock = () => {
    if (!doorRoomId) return;
    const locked = useStore.getState().roomLocks[serverId]?.[doorRoomId] !== undefined;
    gatewayClient()?.lockRoom(serverId, doorRoomId, !locked);
  };

  onRoomChange.current = (r, enter = true) => {
    setRoom(r);

    // Numa ligação privada, a sala NÃO puxa para a call dela: a ligação é a
    // conversa desta pessoa, e anda-se com ela para onde se quiser. Quando a
    // ligação acaba, o efeito `ligacaoEmCurso` volta a passar por aqui.
    if (useStore.getState().ligacao?.estado === "em_curso") return;

    // Nascer numa sala NÃO abre a call. A deteção inicial do spawn chega com
    // enter=false: só actualiza o cabeçalho, para o mundo 2D ser a vista com que
    // se aterra ao F5 — nunca uma chamada. A call por cargo só abre quando o
    // jogador ENTRA numa sala a andar (enter=true). "Entrar" ≠ "nascer".
    //
    // Esquecer a call do escritório é tudo o que isto faz — nunca FECHAR o ecrã
    // dela. Era aqui que a miniatura morria ao voltar do chat: o componente
    // remonta, o spawn chega com enter=false e apagava um `callId` que era a
    // chamada a decorrer. Hoje o `callId` é derivado da sessão de voz e este
    // ramo já não lhe toca.
    //
    // O que faz é REAVER a propriedade da call, em vez de a deitar fora: voltar
    // do Discord remonta isto e a sala é redetectada com enter=false, e se a
    // chamada que está a tocar é a que este escritório abriu, ela continua a ser
    // dele — senão sair para o corredor deixava de a desligar. Se entretanto
    // mudou (juntou-se a outra à mão, pela vista de chat), a propriedade caduca:
    // não se desligam calls que não fomos nós a abrir.
    if (!enter) {
      marcarCallDaSala(donaAoVoltar(callDaSala(), useStore.getState().voice?.channelId ?? null));
      return;
    }

    // A call por cargo: reconcilio a call ABERTA-PELO-ESCRITÓRIO com a sala atual.
    // Cada sala de cargo (Vendas, TI, Sala de Reunião…) casa com o canal de VOZ
    // de nome igual: entrar nela junta a essa call e desbloqueia partilha de ecrã
    // & companhia; mudar de sala salta para a call nova (um novo `join` expulsa a
    // anterior); voltar ao corredor sai da call. O corredor é zona neutra.
    const state = useStore.getState();
    const target =
      r && channelsOf(state, serverId).find((c) => c.type === "VOICE" && c.name.toLowerCase() === r.name.toLowerCase());
    // Só entramos se de facto PODEMOS ligar — o mesmo teste que a API corre; senão
    // o avatar "entrava" na sala e o servidor respondia 403 ao pedir a call.
    const desired = target && can(selfPermissions(state, serverId, target), Permission.CONNECT) ? target.id : null;
    // O motor, não a store: a sessão fica a apontar para o canal mesmo com a
    // ligação em baixo (`idle`/`failed`). Ver `planoDeSala` em `officeCall.ts`.
    const voz = { canal: state.voice?.channelId ?? null, motor: voiceEngine.getSnapshot().state };
    const plano = planoDeSala(desired, voz, callDaSala());

    if (desired) {
      // Entrar numa sala com call ENCERRA o huddle sem o desligar à mão: o
      // `join` refaz a ligação de raiz (o motor desmonta a anterior primeiro).
      // Limpar a âncora AQUI, e não deixar o loop tratar disso 200ms depois, é
      // o que impede o fim do huddle de matar a call que acabou de nascer.
      huddleAnchorRef.current = null;
      huddleGenRef.current += 1;
      setHuddleOn(false);
    }

    marcarCallDaSala(plano.dona);
    if (plano.accao === "entrar") void voiceActions.join(plano.canal);
    else if (plano.accao === "sair") void voiceActions.leave(); // corredor: o ecrã da call segue a call
  };

  useEffect(() => {
    let game: PhaserT.Game | undefined;
    let disposed = false;
    /** O botão direito abre o menu do browser ("guardar imagem…") e esse menu
     *  ENGOLE o `keyup` da tecla de andar: quando se fecha, o Phaser ainda a
     *  julga em baixo e o boneco anda sozinho. Largar o foco (alt-tab) faz o
     *  mesmo. Guardamos aqui o "solta tudo" — declarado no scope do effect para
     *  o `create` o ligar e o cleanup o tirar do `window`. */
    let releaseHeldKeys: (() => void) | null = null;

    void (async () => {
      const Phaser = (await import("phaser")).default;
      const mount = mountRef.current;
      if (disposed || !mount) return;

      // Estado do mundo, partilhado entre create/update por closure. Fora do
      // React: um `set` aqui a 60fps mataria a aba.
      let scene: PhaserT.Scene | undefined;
      let playerAv: Avatar | undefined;
      let doors: ExteriorDoor[] = [];
      let slidingDoors: SlidingDoor[] = [];
      /** Reaproveitado a cada frame: as posições de toda a gente, para as portas
       *  automáticas. Reciclar o array evita 60 alocações por segundo por nada. */
      const bodies: { x: number; y: number }[] = [];
      /** O mesmo, mas com id e "está a andar?" — a entrada de quem reparte os
       *  lugares. Também reciclado; a lista muda de conteúdo, não de identidade. */
      const seatBodies: SeatBody[] = [];
      let cursors: PhaserT.Types.Input.Keyboard.CursorKeys | undefined;
      let wasd: { W: Key; A: Key; S: Key; D: Key } | undefined;
      let keyE: Key | undefined;
      // Onde este avatar aterra: o sítio lembrado (voltar do chat) ou o SPAWN na
      // primeira entrada. O mesmo ponto para tudo o que nasce com a cena — o
      // avatar, a câmara, a sala do cabeçalho — para não haver dois "início".
      const start = corpoLembrado(serverId) ?? { x: SPAWN.x, y: SPAWN.y, facing: "down" as Facing };
      let lastX = start.x;
      let lastY = start.y;
      let lastFacing: Facing = start.facing;
      let lastSentAt = 0;
      let currentRoomId: string | null = roomAt(start.x, start.y)?.id ?? null;
      /**
       * O nome do sítio, para o cabeçalho. É acompanhado à PARTE do `currentRoomId`
       * de propósito: sair do corredor para o jardim não muda sala nenhuma (as duas
       * dão `roomAt` null) e não pode disparar o `onRoomChange`, que é quem abre e
       * fecha a call por cargo. Um rótulo a mudar nunca pode mexer numa chamada.
       * Nasce vazio para o primeiro frame o publicar, seja onde for que se aterre.
       */
      let currentPlace = "";
      /** À porta de que sala estamos? Só se comunica ao React na TRANSIÇÃO. */
      let doorRoomNow: string | null = null;
      /** A última correcção de posição já aplicada — compara-se por IDENTIDADE do
       *  objecto, que é o que distingue "uma correcção nova" de "a mesma outra vez". */
      let lastCorrection: unknown;
      /** A call em que estávamos no frame anterior. É a MUDANÇA dela que manda o
       *  boneco trocar de sala quando se entra numa call pela lista do chat.
       *  Nasce a null de propósito: quem remonta o escritório já dentro de uma
       *  call vê a transição no primeiro frame, e vai parar à sala certa. */
      let lastVoiceChannel: Snowflake | null = null;
      const remotes = new Map<string, Remote>();

      // ── A camada social. Os desenhos que aparecem e desaparecem com as
      // pessoas (balões, círculo da conversa) mais o letreiro da televisão.
      // Cada um recebe a lista COMPLETA uma vez por frame e trata da sua pool
      // — ver `officeSocial.ts`.
      let sayLayer: SayLayer | undefined;
      let huddleLayer: HuddleLayer | undefined;
      let tvBadge: TvBadge | undefined;
      let keyT: Key | undefined;
      let keyF: Key | undefined;
      let usarBola: (clique: { x: number; y: number; id: string | null } | null) => boolean = () => false;
      /** Zoom pelo teclado, para quem não tem roda: mais perto, mais longe, repor. */
      let keyZoomIn: Key | undefined;
      let keyZoomOut: Key | undefined;
      let keyZoomReset: Key | undefined;
      /** O nosso id — o loop precisa dele para se distinguir dos remotos. */
      let selfId = "self";

      // Arrays reaproveitados: o que se desenha muda todos os frames, mas alocar
      // listas novas por frame não. Mesma razão do `bodies` das portas.
      const sayItems: SayItem[] = [];
      const rings: HuddleRing[] = [];
      const huddleBodies: HuddleBody[] = [];

      /**
       * O huddle recalcula-se a 5Hz, não a 60: `huddleGroups` é O(n²) e aloca
       * (filtra, ordena, faz union-find). O que corre a 60fps é só a POSIÇÃO do
       * círculo, a partir dos membros já conhecidos — o desenho acompanha os pés
       * de toda a gente sem refazer o grafo.
       */
      let huddleTick = 0;
      /** Os grupos da última varredura. Guarda-se a lista INTEIRA e não só o meu:
       *  o círculo de uma conversa alheia também se vê (a azul), que é o que faz
       *  o corredor parecer habitado a quem passa. */
      let huddleCache: HuddleGroup[] = [];
      /** Estado anterior dos avisos de E — só se fala ao React na transição. */
      let propNow: string | null = null;
      let knockRoomNow: string | null = null;
      let hatchNow: string | null = null;
      /**
       * O FECHO do salto automático: a boca em que estamos por termos ATERRADO
       * nela, e que por isso não nos pode voltar a atirar.
       *
       * A chegada da descida cai no meio da área de subida de propósito
       * (`space.ts`), e a subida é automática — sem isto, descer era descer e
       * voltar no mesmo frame, para sempre. Limpa-se assim que o corpo sai da
       * área: a partir daí, entrar nela é um pedido de saída como qualquer outro.
       */
      let hatchLatch: string | null = null;

      /**
       * O caminho que o avatar está a percorrer sozinho ("ir ter com alguém").
       * É uma lista de pontos; o passo seguinte entra no MESMO cálculo de
       * movimento das teclas, por isso colide com as paredes e com as portas
       * trancadas exactamente da mesma maneira.
       */
      let path: { x: number; y: number }[] | null = null;
      let pathIdx = 0;
      /**
       * O salto pela escotilha. Nasce no `create` (precisa da câmara da cena) e
       * é chamado do `update` e do botão do aviso — daí viver aqui fora, no
       * único sítio que os três alcançam.
       */
      let jump: (port: Teleport) => void = () => {};
      /** Pousar o boneco noutro sítio do mapa sem o pôr a andar até lá — o que a
       *  mudança de call usa. Nasce no `create` como o `jump`, pela mesma razão:
       *  precisa da câmara da cena. */
      let placeAt: (x: number, y: number) => void = () => {};
      /** Idem para o zoom: nasce no `create`, usam-no o `update` e os botões. */
      let applyZoom: (next: number, animate?: boolean) => void = () => {};
      /** A escala PEDIDA (a câmara pode ainda estar a caminho dela). */
      let zoomTarget = CAMERA_ZOOM;
      /** A câmara largou o avatar por um arrasto (pan) e fica onde a deixaram até
       *  ao próximo passo? Vive aqui porque o `create` a solta e o `update` volta
       *  a colá-la ao boneco. */
      let camDetached = false;
      /**
       * A troca de andar na escada — nasce no `create` porque mexe nas câmaras.
       *
       * O 1.º andar não tem câmara própria: é a MESMA câmara principal que passa
       * a olhar para a faixa de cima, com o scroll somado do mesmo desvio que o
       * corpo — e é por isso que nada mexe no ecrã. O que muda é o que se vê, e
       * para isso há três câmaras de apoio, sempre por esta ordem de desenho:
       *   1. `baixo` — o rés-do-chão, POR BAIXO do 1.º andar. Enquanto se está
       *      lá em cima é ela que mostra o jardim à volta do telhado;
       *   2. (a principal);
       *   3. `cima`  — o 1.º andar POR CIMA do rés-do-chão, só durante a descida,
       *      a desvanecer-se;
       *   4. `eu`    — só o meu boneco, durante a troca: sem ela, o próprio boneco
       *      desvanecia-se com o andar em que já não está.
       */
      let subirOuDescer: (dest: { x: number; y: number }, agora: number) => void = () => {};
      /** Põe as câmaras de apoio no sítio certo e decide quais se vêem. Corre a cada frame. */
      let vistaDoAndar: (agora: number) => void = () => {};
      /** Anti-encravamento: quem não avança durante ~0,6s desiste do caminho. */
      let stuckMs = 0;
      let stuckX = 0;
      let stuckY = 0;

      /** As trancas, para quem precisa delas fora do frame (o `goTo`). */
      const lockedNow = (roomId: string): boolean => useStore.getState().roomLocks[serverId]?.[roomId] !== undefined;

      const config: PhaserT.Types.Core.GameConfig = {
        type: Phaser.AUTO,
        parent: mount,
        width: mount.clientWidth || MAP_WIDTH,
        height: mount.clientHeight || MAP_HEIGHT,
        backgroundColor: "#0f151b",
        banner: false,
        // pixelArt = nearest-neighbor, sem suavização: as bordas ficam nítidas
        // mesmo com o zoom da câmara — e é ISTO que dá a nitidez, não o
        // roundPixels.
        //
        // roundPixels ficou de FORA de propósito, ao contrário do que o
        // comentário antigo dizia. A câmara segue o avatar com lerp (0.15) e o
        // zoom é fraccionário (1.75 por omissão, passos de 1.18 — nunca inteiro).
        // Nesse cenário, arredondar a posição final para pixel inteiro a cada
        // frame faz um passo render 2 px e o seguinte 1 px: o boneco anda a
        // coxear. Sem roundPixels o movimento é contínuo e as texturas continuam
        // nítidas (quem trata disso é o pixelArt). Se algum dia o zoom passar a
        // ser só inteiro, aí sim roundPixels volta a compensar.
        pixelArt: true,
        scale: { mode: Phaser.Scale.RESIZE, autoCenter: Phaser.Scale.CENTER_BOTH },
        scene: {
          // O v1 fabrica as suas texturas em código e por isso nunca precisou de
          // um `preload`. A vegetação e os móveis do campus são ficheiros — e
          // ficheiros chegam quando chegam. É o `preload` que garante que o
          // `create` só corre com eles já em casa.
          //
          // A cache tem de ser servida ANTES dos quatro `preloadOffice*`: é ela
          // que regista as imagens que montagens anteriores já descodificaram, e
          // o loader do Phaser salta sozinho os ficheiros cuja chave já exista
          // (`File.hasCacheConflict`). Sem esta linha, cada remontagem — e o Fast
          // Refresh faz uma por ficheiro guardado — voltava a pedir os ~70 PNG,
          // e bastava um falhar para a peça cair no desenho do v1 em silêncio.
          preload(this: PhaserT.Scene) {
            servirArteEmCache(this);
            preloadOfficeFlora(this);
            preloadOfficeFurniture(this);
            preloadOfficeChar(this);
            preloadOfficeCharFemale(this);
          },
          create(this: PhaserT.Scene) {
            scene = this;
            // Aqui e não mais abaixo: já com os ficheiros em casa e ainda antes
            // de o mundo desenhar a primeira textura de canvas, que é o que
            // separa "ficheiro que chegou" de "arte gerada em código".
            guardarArteDaCena(this);
            doors = drawWorld(this, Phaser);
            new BolasDeNeve(this, (estado) => onBolaEstado.current(estado));
            /**
             * F (ou o botão do aviso, ou um clique com a bola na mão). Sem bola,
             * apanha neve; com bola, atira-a — ao ponto clicado, ou a quem está à
             * frente e ao alcance, ou uns passos em frente se não houver ninguém.
             */
            usarBola = (clique) => {
              const bolas = bolasDaCena(this);
              if (!bolas || !playerAv) return false;
              const eu = playerAv.container;
              if (bolas.estado === "pode" && !clique) return bolas.apanhar(this.time.now, eu);
              if (bolas.estado !== "na_mao") return false;
              let destino: { x: number; y: number };
              let alvoId: string | null;
              if (clique) {
                destino = clique;
                alvoId = clique.id;
              } else {
                const pessoas = [...remotes].map(([id, r]) => ({ id, x: r.container.x, y: r.container.y }));
                const alvo = alvoAFrente(eu, playerAv.dir, pessoas);
                destino = alvo ?? pontoAFrente(eu, playerAv.dir, DISTANCIA_SEM_ALVO);
                alvoId = alvo?.id ?? null;
              }
              bolas.largar();
              gatewayClient()?.bolaDeNeve(serverId, destino.x, destino.y, alvoId);
              return true;
            };
            bolaAcao.current = () => void usarBola(null);
            slidingDoors = drawSlidingDoors(this, Phaser);
            // Uma correcção que já lá estivesse (de um escritório anterior nesta
            // mesma sessão) não é nossa — regista-se como "já aplicada" para não
            // teletransportar ninguém no primeiro frame.
            lastCorrection = useStore.getState().spaceCorrection[serverId];

            applyCameraBand(this.cameras.main, start.y);
            this.cameras.main.setZoom(CAMERA_ZOOM);

            /**
             * O zoom, com um único caminho de entrada.
             *
             * Roda do rato, teclas +/− e os botões do canto acabam todos aqui:
             * um só sítio a limitar a escala e a avisar o React, em vez de três
             * cópias da mesma aritmética a divergirem com o tempo. O `zoomTo`
             * anima em vez de saltar — a câmara segue o avatar com lerp e um
             * degrau seco de escala lia-se como um solavanco.
             *
             * Os limites são da câmara, não do mundo — mas quem os centra é o
             * `bandaCamara`, não o Phaser: à conta dele, uma vista mais larga
             * do que a faixa encosta o mundo ao canto esquerdo.
             *
             * Os degraus contam-se a partir de `zoomTarget` e não de `cam.zoom`:
             * a meio de uma animação a câmara ainda não lá chegou, e três
             * cliques seguidos no "+" andavam menos do que três cliques lentos.
             */
            applyZoom = (next: number, animate = true) => {
              const cam = this.cameras.main;
              const z = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next));
              if (Math.abs(z - zoomTarget) < 0.001) return;
              zoomTarget = z;
              if (animate) cam.zoomTo(z, 140, "Sine.easeOut");
              else cam.setZoom(z);
              onZoom.current(z);
              // Afastou o zoom? A mão aberta avisa que se pode agarrar e arrastar
              // o mapa; ao voltar ao zoom de origem a câmara volta a colar-se e o
              // cursor deixa de o sugerir. Não se mexe a meio de um arrasto.
              if (!camDetached) this.input.setDefaultCursor(z < CAMERA_ZOOM - 0.001 ? "grab" : "");
            };
            zoomCtl.current = (next) => applyZoom(next);

            // A roda do rato é o gesto que toda a gente tenta primeiro. O passo é
            // multiplicativo (ver `ZOOM_STEP`) para o salto ser igual em toda a
            // escala; `dy` só interessa no sinal — a amplitude varia de rato para
            // rato e de trackpad para trackpad, e usá-la dava saltos absurdos.
            this.input.on("wheel", (_p: unknown, _o: unknown, _dx: number, dy: number) => {
              if (dy === 0) return;
              applyZoom(zoomTarget * (dy > 0 ? 1 / ZOOM_STEP : ZOOM_STEP), false);
            });

            // ── Arrastar para deslocar o mapa (pan), estilo jogo isométrico ──
            // Com o zoom afastado vê-se mais campus do que aquilo que a câmara,
            // colada ao avatar, mostra. Um arrasto sobre o MUNDO (os painéis são
            // DOM por cima e não chegam aqui) LARGA o avatar e empurra o scroll
            // pelo delta do ponteiro convertido para pixels de mundo (÷ zoom). A
            // câmara volta a segui-lo ao primeiro passo (ver o `update`) — olha-se
            // à volta como num RTS e o controlo do boneco recupera a vista. Os
            // limites são os mesmos do zoom (`applyCameraBand` prende o scroll à
            // faixa a cada frame), por isso nunca se arrasta para fora do mapa. Só
            // com o zoom afastado: ao zoom de origem a câmara fica colada, que é o
            // que se quer a andar de perto — e é exactamente "quando tiro o zoom".
            // Dois dedos ao mesmo tempo: o Phaser só conta um por omissão.
            this.input.addPointer(1);
            /** O zoom de pinça: distância inicial entre os dedos e o zoom de então. */
            let pinca: { d0: number; z0: number } | null = null;
            let panning = false;
            let panPx = 0;
            let panPy = 0;
            let panScrollX = 0;
            let panScrollY = 0;
            let panDist = 0;
            const endPan = () => {
              panning = false;
              this.input.setDefaultCursor(zoomTarget < CAMERA_ZOOM - 0.001 ? "grab" : "");
            };
            // ── Carregar numa PESSOA: a mesa de acções dela ──
            // Vive ao lado do pan e não dentro dele porque o pan só existe com o
            // zoom afastado — e carregar num boneco tem de funcionar sempre. O
            // que os dois partilham é a regra do arrasto: um ponteiro que ANDOU
            // estava a arrastar o mapa, não a escolher ninguém.
            let pickPx = 0;
            let pickPy = 0;
            let pickDist = 0;
            let picking = false;
            this.input.on("pointerdown", (p: PhaserT.Input.Pointer) => {
              picking = true;
              pickDist = 0;
              pickPx = p.x;
              pickPy = p.y;
              if (zoomTarget >= CAMERA_ZOOM - 0.001) return; // colado ao avatar de perto
              const cam = this.cameras.main;
              panning = true;
              panDist = 0;
              panPx = p.x;
              panPy = p.y;
              panScrollX = cam.scrollX;
              panScrollY = cam.scrollY;
            });
            this.input.on("pointermove", (p: PhaserT.Input.Pointer) => {
              const dedos = this.input.manager.pointers.filter((q) => q.isDown && q.wasTouch);
              if (dedos.length >= 2) {
                const [a, b] = dedos as [PhaserT.Input.Pointer, PhaserT.Input.Pointer];
                const d = Math.hypot(a.x - b.x, a.y - b.y);
                if (!pinca) pinca = { d0: Math.max(1, d), z0: zoomTarget };
                else applyZoom(pinca.z0 * (d / pinca.d0), false);
                // Uma pinça não é um toque num sítio nem um arrasto do mapa.
                picking = false;
                panning = false;
                return;
              }
              pinca = null;
              if (picking) pickDist = Math.max(pickDist, Math.hypot(p.x - pickPx, p.y - pickPy));
              if (!panning) return;
              if (!p.isDown) {
                endPan(); // largou-se fora da tela e o `pointerup` não chegou
                return;
              }
              const cam = this.cameras.main;
              panDist = Math.max(panDist, Math.hypot(p.x - panPx, p.y - panPy));
              // Um toque seco não rouba a câmara: só a partir de ~4px conta como
              // arrasto e é aí que se larga o avatar.
              if (!camDetached && panDist > 4) {
                cam.stopFollow();
                camDetached = true;
                this.input.setDefaultCursor("grabbing");
              }
              if (camDetached) {
                cam.setScroll(panScrollX - (p.x - panPx) / cam.zoom, panScrollY - (p.y - panPy) / cam.zoom);
              }
            });
            this.input.on("pointerup", (p: PhaserT.Input.Pointer) => {
              endPan();
              if (!picking) return;
              picking = false;
              if (pickDist > 4) return; // arrastou o mapa, não escolheu ninguém

              // Quem está debaixo do dedo. O sprite tem origem (0.5, 1) — o `y`
              // do contentor são os PÉS — e é por isso que a caixa cresce para
              // CIMA. Empatam-se pelos pés mais baixos: num escritório em
              // perspectiva, quem está mais à frente tapa quem está atrás, e
              // ganhar o clique é a mesma coisa que estar por cima.
              const world = this.cameras.main.getWorldPoint(p.x, p.y);
              let hitId: string | null = null;
              let hitFeet = Number.NEGATIVE_INFINITY;
              for (const [userId, r] of remotes) {
                const cx = r.container.x + r.sprite.x;
                const feet = r.container.y + r.sprite.y;
                const halfW = r.sprite.displayWidth / 2 + 4;
                if (world.x < cx - halfW || world.x > cx + halfW) continue;
                if (world.y < feet - r.sprite.displayHeight - 6 || world.y > feet + 4) continue;
                if (feet <= hitFeet) continue;
                hitFeet = feet;
                hitId = userId;
              }
              if (bolasDaCena(this)?.estado === "na_mao") {
                const r = hitId ? remotes.get(hitId) : undefined;
                usarBola(
                  r && hitId ? { x: r.container.x, y: r.container.y, id: hitId } : { x: world.x, y: world.y, id: null },
                );
                return;
              }
              if (!hitId) {
                onPickPerson.current(null, 0, 0); // carregou no chão: fecha o que estiver aberto
                // Num ecrã de toque, tocar no chão é ir até lá — o mesmo piloto
                // automático do minimapa, com as mesmas paredes e portas.
                if (p.wasTouch || toqueRef.current) {
                  const alvo = destinoMaisProximo(world.x, world.y);
                  if (alvo) irPara.current(alvo.x, alvo.y);
                }
                return;
              }
              // O cartão vive no `body`, não na tela: precisa de coordenadas da
              // JANELA. O evento do rato já as traz; sem ele (toque sintético),
              // soma-se a origem do canvas às coordenadas locais.
              const ev = p.event as MouseEvent | undefined;
              const rect = this.game.canvas.getBoundingClientRect();
              const sx = ev && typeof ev.clientX === "number" ? ev.clientX : rect.left + p.x;
              const sy = ev && typeof ev.clientY === "number" ? ev.clientY : rect.top + p.y;
              onPickPerson.current(hitId, sx, sy);
            });

            const self = useStore.getState().self;
            const name = self ? (self.displayName ?? self.username) : "Eu";
            const id = self ? self.id : "self";
            selfId = id;
            playerAv = makeAvatar(this, start.x, start.y, id, name, self?.gender ?? "MALE");
            playerAv.dir = start.facing; // vira-o para onde estava; o loop repinta a textura
            this.cameras.main.startFollow(playerAv.container, true, 0.15, 0.15);

            // ── As câmaras de apoio do 1.º andar (ver `subirOuDescer`).
            {
              const principal = this.cameras.main;
              const nova = (nome: string) =>
                this.cameras.add(0, 0, principal.width, principal.height, false, nome).setVisible(false);
              const camBaixo = nova("andar-de-baixo");
              const camCima = nova("andar-de-cima");
              const camEu = nova("so-eu");
              // O Phaser desenha as câmaras pela ordem da lista: a de baixo vai
              // para a frente da principal, as outras duas ficam depois dela.
              const lista = this.cameras.cameras;
              lista.splice(lista.indexOf(camBaixo), 1);
              lista.unshift(camBaixo);

              // O que as câmaras de apoio seguem: o corpo com o desvio do andar.
              // Seguem com o MESMO lerp da principal, e por isso fazem a mesma
              // conta no mesmo frame — copiar o scroll dela chegava um frame
              // atrasado (a de baixo desenha antes de a principal andar).
              const sombraBaixo = { x: 0, y: 0 };
              const sombraCima = { x: 0, y: 0 };
              let seguem: PhaserT.GameObjects.Container | null = null;
              let faixaVista = Number.NaN;
              let fade: { subir: boolean; desde: number } | null = null;

              const alinhar = () => {
                camBaixo.setScroll(principal.scrollX, principal.scrollY - ANDAR_PX);
                camCima.setScroll(principal.scrollX, principal.scrollY + ANDAR_PX);
                camEu.setScroll(principal.scrollX, principal.scrollY);
              };

              subirOuDescer = (dest, agora) => {
                if (!playerAv) return;
                const dy = dest.y - playerAv.container.y;
                playerAv.container.x = dest.x;
                playerAv.container.y = dest.y;
                // Por ESTA ordem: o `setBounds` do Phaser prende o scroll no próprio
                // instante. Com os limites do andar novo aplicados primeiro, o scroll
                // ainda do andar velho era atirado para a borda e só depois somado —
                // e a vista saltava para o fundo do mapa durante a troca.
                principal.setScroll(principal.scrollX, principal.scrollY + dy);
                applyCameraBand(principal, dest.y);
                // Um caminho automático que passava pela escada tem, a seguir, um
                // ponto no andar de onde se veio: salta-se, senão o boneco voltava
                // para trás a deslizar através do mapa.
                if (path) {
                  const faixa = faixaDe(dest.y).y;
                  while (pathIdx < path.length && faixaDe(path[pathIdx]!.y).y !== faixa) pathIdx += 1;
                  if (pathIdx >= path.length) path = null;
                }
                alinhar();
                faixaVista = faixaDe(dest.y).y;
                // A câmara do boneco só vê o boneco: marca-se tudo o resto para ela
                // ignorar. O que nascer durante a troca aparece nas duas no mesmo
                // sítio, o que não se nota em 260 ms.
                for (const obj of this.children.getChildren()) {
                  if (obj === playerAv.container) obj.cameraFilter &= ~camEu.id;
                  else obj.cameraFilter |= camEu.id;
                }
                fade = { subir: dy > 0, desde: agora };
              };

              vistaDoAndar = (agora) => {
                if (!playerAv) return;
                const corpo = playerAv.container;
                sombraBaixo.x = corpo.x;
                sombraBaixo.y = corpo.y - ANDAR_PX;
                sombraCima.x = corpo.x;
                sombraCima.y = corpo.y + ANDAR_PX;

                // Mudou de faixa sem ser pela escada (escotilha, call pela lista,
                // correcção do servidor): as de apoio saltam com a principal.
                const faixa = faixaDe(corpo.y).y;
                if (faixa !== faixaVista) {
                  faixaVista = faixa;
                  alinhar();
                }

                for (const cam of [camBaixo, camCima, camEu]) {
                  if (cam.width !== principal.width || cam.height !== principal.height)
                    cam.setSize(principal.width, principal.height);
                  cam.setZoom(principal.zoom);
                }
                applyCameraBand(camBaixo, sombraBaixo.y);
                applyCameraBand(camCima, sombraCima.y);
                applyCameraBand(camEu, corpo.y);

                // A mão largou a câmara (pan)? As de apoio copiam o scroll em vez de
                // seguirem — o da principal já foi escrito pelo rato antes deste frame.
                const alvo = camDetached ? null : corpo;
                if (alvo !== seguem) {
                  seguem = alvo;
                  if (alvo) {
                    camBaixo.startFollow(sombraBaixo, true, 0.15, 0.15);
                    camCima.startFollow(sombraCima, true, 0.15, 0.15);
                    camEu.startFollow(alvo, true, 0.15, 0.15);
                  } else {
                    for (const cam of [camBaixo, camCima, camEu]) cam.stopFollow();
                  }
                }
                if (camDetached) alinhar();

                let p = 1;
                if (fade) {
                  p = Math.min(1, (agora - fade.desde) / FADE_DO_ANDAR_MS);
                  if (p >= 1) fade = null;
                }
                camBaixo.setVisible(corpo.y >= UPSTAIRS_TOP);
                camCima.setVisible(fade !== null && !fade.subir);
                camEu.setVisible(fade !== null);
                // Nunca 0: uma câmara a alfa 0 não é desenhada NEM actualizada, e
                // ficava um frame atrás das outras.
                principal.setAlpha(fade?.subir ? Math.max(0.001, p) : 1);
                camCima.setAlpha(fade && !fade.subir ? Math.max(0.001, 1 - p) : 1);
              };
            }

            // A camada social nasce com a cena e morre com ela (o `game.destroy`
            // leva tudo o que está na cena, incluindo as pools).
            sayLayer = createSayLayer(this);
            huddleLayer = createHuddleLayer(this, Phaser);
            const tv = PROPS.find((p) => p.kind === "tv");
            if (tv) {
              const spec = PROP_SPECS.tv;
              tvBadge = createTvBadge(this, (tv.col + spec.w / 2) * TILE, (tv.row + spec.h) * TILE);
            }

            const kb = this.input.keyboard;
            if (kb) {
              cursors = kb.createCursorKeys();
              wasd = kb.addKeys("W,A,S,D") as { W: Key; A: Key; S: Key; D: Key };
              keyE = kb.addKey("E"); // o ferrolho: chega-se à porta e carrega-se em E
              keyT = kb.addKey("T"); // T de texto: abre o balão de fala
              keyF = kb.addKey("F"); // F de floco: apanha e atira bolas de neve
              // Zoom sem rato. PLUS/MINUS são as teclas da linha de cima (onde
              // dizem + e −); o `0` repõe a escala de origem, como em qualquer
              // mapa. Sem SHIFT: `+` e `=` partilham a mesma tecla física.
              keyZoomIn = kb.addKey(Phaser.Input.Keyboard.KeyCodes.PLUS);
              keyZoomOut = kb.addKey(Phaser.Input.Keyboard.KeyCodes.MINUS);
              keyZoomReset = kb.addKey(Phaser.Input.Keyboard.KeyCodes.ZERO);
              // Menu de contexto (botão direito) ou perda de foco = keyup perdido.
              // `resetKeys` põe TODAS as teclas do jogo de novo em cima, por isso
              // ao fechar o menu o avatar não fica a andar sozinho — recomeça só
              // se a tecla for mesmo carregada outra vez. `window` (e não o canvas)
              // porque o menu pode abrir sobre qualquer parte da página.
              releaseHeldKeys = () => kb.resetKeys();
              window.addEventListener("contextmenu", releaseHeldKeys);
              window.addEventListener("blur", releaseHeldKeys);
            }

            /**
             * "Ir ter com alguém": o caminho é calculado UMA vez, aqui, sobre as
             * paredes e as portas trancadas de agora — e não é recalculado a
             * meio. Se alguém trancar uma porta pelo caminho, o avatar bate nela
             * e o anti-encravamento desiste, que é o que uma pessoa faria.
             */
            goTo.current = (userId) => {
              if (!playerAv) return;
              const target = remotes.get(userId)?.container;
              if (!target) return;
              const from = { x: playerAv.container.x, y: playerAv.container.y };
              const route = pathToPerson(from, { x: target.x, y: target.y }, lockedNow);
              if (!route || route.length === 0) return;
              path = route;
              pathIdx = 0;
              stuckMs = 0;
              stuckX = from.x;
              stuckY = from.y;
            };

            /**
             * "Ir a um sítio" (o minimapa): o mesmo piloto automático, para um
             * ponto em vez de uma pessoa. As mesmas paredes e portas trancadas.
             */
            irPara.current = (x, y) => {
              if (!playerAv) return false;
              const from = { x: playerAv.container.x, y: playerAv.container.y };
              const route = findPath(from, { x, y }, lockedNow);
              if (!route || route.length === 0) return false;
              path = route;
              pathIdx = 0;
              stuckMs = 0;
              stuckX = from.x;
              stuckY = from.y;
              // Já, e não só no próximo frame: o minimapa limpa o destino quando
              // não vê rota nenhuma, e desenha antes de o loop voltar a correr.
              rotaRef.current = { path: route, idx: 0 };
              return true;
            };

            /**
             * O salto pela escotilha — o único do mapa, e com uma regra por
             * direcção (`port.auto`).
             *
             * Foi automático nos dois sentidos e falhava nos dois ao mesmo tempo.
             * Em cima, a boca era um tile de relva à vista igual aos outros e
             * engolia quem só ia a passar ("está muito longe da porta e já está
             * abrindo sozinho"). Em baixo, a saída era um tile de betão entre doze
             * idênticos, e quem lá estava não a encontrava ("mesmo assim ele não
             * saiu do bunker"). Passou a ser tudo à tecla E — o que arrumou a
             * primeira metade e deixou a segunda a meio: *"na hora de sair do
             * bunker, quero que não precise apertar E"*.
             *
             * Hoje: DESCER pede a tecla (a tampa está num caminho), SUBIR não (o
             * patamar é um beco onde ninguém passa por acaso). O `hatchLatch`
             * abaixo é o que impede que aterrar seja partir outra vez.
             */
            jump = (port: Teleport) => {
              if (!playerAv) return;
              const dest = teleportDestination(port);
              playerAv.container.x = dest.x;
              playerAv.container.y = dest.y;
              // Aterrar não é chegar: se a chegada cai dentro de uma boca, ela
              // fica travada até sairmos de lá pelo nosso pé.
              hatchLatch = teleportNear(dest.x, dest.y)?.id ?? null;
              path = null; // um trajecto automático arrastaria de volta pela boca
              applyCameraBand(this.cameras.main, dest.y);
              // A câmara segue com lerp: sem este empurrão, um salto de 1100px
              // varria o mapa inteiro em panorâmica em vez de mudar de sítio.
              this.cameras.main.centerOn(dest.x, dest.y);
            };
            useHatch.current = jump;

            /**
             * Mudar de sala PELA LISTA (modo chat) tem de mexer o boneco.
             *
             * *"quando eu tô no modo discord e entro numa sala de lá, o meu
             * bonequinho tem que sair da sala que ele estava antes e ir para a
             * nova"* — e tinha razão: o mapa dizia que a pessoa estava em Vendas
             * enquanto a voz dela saía de TI. O avatar É onde se está.
             *
             * Pousa-se, não se anda. O gesto foi um clique numa lista, não um
             * passo — e um trajecto automático atravessaria salas pelo caminho,
             * cada uma a puxar a call para si (`onRoomChange` dispara em cada
             * transição). Mesma mecânica da correcção do servidor e do salto
             * pela escotilha: escreve-se a posição e empurra-se a câmara.
             */
            placeAt = (x, y) => {
              if (!playerAv) return;
              playerAv.container.x = x;
              playerAv.container.y = y;
              hatchLatch = teleportNear(x, y)?.id ?? null; // pousar também não é chegar
              path = null; // um trajecto a meio arrastaria o boneco de volta
              applyCameraBand(this.cameras.main, y);
              this.cameras.main.centerOn(x, y);
            };

            // NÃO se entra no espaço aqui. A cena nasce quase sempre ANTES da
            // ligação estar aberta (é o F5 a cair directamente no escritório) e
            // `send()` deita fora em silêncio o que se manda com o socket fechado:
            // o servidor nunca nos registava no espaço e depois ignorava, sem uma
            // palavra, TODAS as posições e TODOS os trancos. Quem entra é o efeito
            // ligado ao `connectionStatus`, que volta a entrar a cada ligação nova.
            posRef.current = { x: start.x, y: start.y, facing: playerAv.dir };
            lastSentAt = this.time.now;
            onRoomChange.current(roomAt(start.x, start.y), false);
          },

          update(this: PhaserT.Scene, time: number, delta: number) {
            if (!playerAv) return;

            // Trocou de personagem? O boneco escolhe a arte UMA vez, ao nascer —
            // sem isto, escolher no ecrã de entrada (ou nas definições) não se
            // via até recarregar a página, e ficava a parecer que não resultou.
            //
            // Nasce outro no mesmo sítio, virado para o mesmo lado, e a câmara
            // volta a colar-se-lhe: o contentor antigo morre e o `startFollow`
            // ficaria a seguir um objecto destruído.
            {
              const eu = useStore.getState().self;
              const genero = eu?.gender ?? "MALE";
              if (selfId && mudouDePersonagem(playerAv, selfId, genero)) {
                const antigo = playerAv;
                const nome = eu ? (eu.displayName ?? eu.username) : "Eu";
                playerAv = makeAvatar(this, antigo.container.x, antigo.container.y, selfId, nome, genero);
                playerAv.dir = antigo.dir;
                if (antigo.seat) setSeated(playerAv, antigo.seat);
                antigo.container.destroy();
                this.cameras.main.startFollow(playerAv.container, true, 0.15, 0.15);
              }
            }

            const container = playerAv.container;

            // Os limites da câmara dependem do campo de visão, e o campo de
            // visão muda por dois caminhos que não avisam ninguém: o zoom (que
            // ANIMA, portanto passa por dezenas de valores intermédios) e o
            // redimensionar da janela. Reaplicar aqui é a única forma de os
            // apanhar aos dois — e é barato: são quatro contas e um `setTo`.
            applyCameraBand(this.cameras.main, container.y);

            // As trancas lidas UMA vez por frame (o store é imperativo aqui, como
            // tudo o resto neste loop) e usadas para três coisas: bater na porta,
            // desenhar a folha, e saber o que a tecla E vai fazer.
            const locks = useStore.getState().roomLocks[serverId] ?? {};
            const isLocked = (roomId: string): boolean => locks[roomId] !== undefined;

            // O servidor recusou-nos um passo? Volta-se para onde ele diz. É raro
            // (só quando alguém tranca no mesmo instante em que atravessamos) mas
            // sem isto ficávamos a andar numa sala onde já não estamos para os
            // outros — invisíveis, com tudo a parecer bem.
            const correction = useStore.getState().spaceCorrection[serverId];
            if (correction && correction !== lastCorrection) {
              lastCorrection = correction;
              container.x = correction.x;
              container.y = correction.y;
              playerAv.dir = correction.facing;
              lastX = correction.x;
              lastY = correction.y;
              lastFacing = correction.facing;
            }

            // Entrou-se numa call pela LISTA (modo chat, ou a barra de voz)? Então
            // o boneco muda de sala — a call é que manda onde se está. É a MESMA
            // decisão que o telemóvel já usava, e as duas partes dela são puras e
            // testadas (`presenceRoomId` + `roomMoveSpot`, em `useOfficePresence`);
            // aqui só se aplica o resultado ao avatar em vez de a um
            // `UpdatePosition`.
            //
            // Aqui no loop e não num `useEffect` de propósito: voltar do chat
            // REMONTA este componente, e um efeito corria antes de o Phaser ter
            // acabado de arrancar — o `placeAt` ainda seria o vazio do arranque, e
            // quem voltasse ao escritório já dentro de uma call ficava na sala
            // errada, que é exactamente o caso do Kawã. O `lastVoiceChannel` nasce
            // a `null` para que o primeiro frame veja a call como transição.
            const storeNow = useStore.getState();
            const voiceNow = storeNow.voice && storeNow.voice.serverId === serverId ? storeNow.voice.channelId : null;
            if (voiceNow !== lastVoiceChannel) {
              lastVoiceChannel = voiceNow;
              const spot = roomMoveSpot(
                roomAt(container.x, container.y)?.id ?? null,
                presenceRoomId(serverId, storeNow.voice, storeNow.channels),
              );
              if (spot) placeAt(spot.x, spot.y);
            }

            // A escrever no balão? Então o teclado é da caixa de texto e não do
            // avatar. O `stopPropagation` do input já impede o Phaser de ver as
            // teclas; isto trata do resto — uma tecla que tenha ficado em baixo
            // no instante em que a caixa abriu continuaria a empurrar o boneco.
            const typing = teclasOcupadasRef.current;

            let ix = typing
              ? 0
              : (cursors?.right.isDown || wasd?.D.isDown ? 1 : 0) - (cursors?.left.isDown || wasd?.A.isDown ? 1 : 0);
            let iy = typing
              ? 0
              : (cursors?.down.isDown || wasd?.S.isDown ? 1 : 0) - (cursors?.up.isDown || wasd?.W.isDown ? 1 : 0);
            // O joystick de toque é mais uma entrada, com as teclas a mandar se
            // as duas estiverem a ser usadas.
            if (ix === 0 && iy === 0 && !typing) {
              ix = direccaoToque.current.x;
              iy = direccaoToque.current.y;
            }

            // ── O caminho automático ("ir ter com alguém").
            // Os pontos entram no MESMO cálculo das teclas — só mudam a direcção,
            // nunca a forma de andar. É por isso que um avatar em piloto
            // automático continua a bater nas paredes e nas portas trancadas
            // como qualquer outro: não há um segundo motor de movimento.
            if (ix !== 0 || iy !== 0)
              path = null; // mexer nas teclas manda sempre
            else if (path) {
              const step = (SPEED * delta) / 1000;
              const wp = path[pathIdx];
              if (!wp) {
                path = null;
              } else if (Math.hypot(wp.x - container.x, wp.y - container.y) <= Math.max(3, step)) {
                // Chegou à inflexão: encaixa nela e segue. Sem o encaixe, o erro
                // de cada canto somava-se ao seguinte e o trajecto derivava.
                container.x = wp.x;
                container.y = wp.y;
                pathIdx += 1;
                if (pathIdx >= path.length) path = null;
              } else {
                ix = wp.x - container.x;
                iy = wp.y - container.y;
              }

              // Encravado (porta que fechou, móvel, alguém a tapar o vão)?
              // Desiste. Insistir era um avatar a esfregar-se numa parede para
              // sempre — e ninguém percebe porquê.
              stuckMs += delta;
              if (stuckMs >= 600) {
                if (Math.hypot(container.x - stuckX, container.y - stuckY) < 2) path = null;
                stuckMs = 0;
                stuckX = container.x;
                stuckY = container.y;
              }
            }

            const moving = ix !== 0 || iy !== 0;

            // A câmara foi largada para olhar à volta (pan)? O primeiro passo do
            // avatar traz-la de volta e volta a segui-lo — o controlo do boneco
            // recupera sempre a vista, como em qualquer jogo de câmara solta.
            if (camDetached && moving) {
              this.cameras.main.startFollow(container, true, 0.15, 0.15);
              camDetached = false;
              this.input.setDefaultCursor(zoomTarget < CAMERA_ZOOM - 0.001 ? "grab" : "");
            }

            // De quem é cada lugar, ANTES de dar o passo. Um assento não é um
            // comando nem uma mensagem: é geometria, e a dona da geometria é a
            // lista de corpos que já circula. Cada cliente faz esta conta com a
            // mesma entrada e chega ao mesmo dono — por isso ninguém precisa de
            // perguntar ao servidor de quem é a cadeira.
            seatBodies.length = 0;
            seatBodies.push({ id: selfId, x: container.x, y: container.y, moving });
            for (const [id, remote] of remotes)
              seatBodies.push({ id, x: remote.container.x, y: remote.container.y, moving: remote.moving });
            seatOwnership = seatOwners(seatBodies);

            if (moving) {
              const len = Math.hypot(ix, iy);
              const dist = (SPEED * delta) / 1000;
              const vx = (ix / len) * dist;
              const vy = (iy / len) * dist;

              // Colisão por eixo = deslizar ao longo da parede em vez de colar.
              // Uma porta trancada conta como parede, com a MESMA `canEnter` que
              // o servidor corre — só que aqui bate-se nela a 60fps, com o corpo
              // do avatar (a margem), em vez de se ser corrigido pela rede. O
              // cliente trava sempre ANTES do servidor, e é por isso que um passo
              // legítimo nunca chega a ser recusado.
              //
              // Um lugar OCUPADO conta como parede, e é essa a correcção do bug
              // de dois bonecos na mesma cadeira: já não se chega lá. Não é o
              // `solid` do móvel a mudar — a cadeira vazia continua a ser chão,
              // senão nunca se lá chegava; o que ganha corpo é o instante em que
              // alguém está lá sentado. O `seatBlocks` recebe também de ONDE se
              // vem, porque há cantos deste mapa (a faixa da Copa, a ponta do
              // sofá da TI) a que só se chega por cima de uma cadeira: quem lá
              // esteja atravessa-a a SAIR, e por isso ninguém fica fechado.
              const blocked = (tx: number, ty: number): boolean => {
                const from = { x: container.x, y: container.y };
                const to = { x: tx, y: ty };
                return (
                  !isWalkable(tx, ty) || !canEnter(from, to, isLocked) || seatBlocks(from, to, seatOwnership, selfId)
                );
              };
              if (vx !== 0 && !blocked(container.x + vx + Math.sign(vx) * COLLIDE_MARGIN, container.y))
                container.x += vx;
              if (vy !== 0 && !blocked(container.x, container.y + vy + Math.sign(vy) * COLLIDE_MARGIN))
                container.y += vy;

              playerAv.dir = Math.abs(ix) >= Math.abs(iy) ? (ix < 0 ? "left" : "right") : iy < 0 ? "up" : "down";
            }

            // ── A escada: pisar o degrau certo troca de andar sem mexer no ecrã.
            // A regra (e a folga que impede voltar logo) vive em `space.ts`.
            const andar = trocaDeAndar(container.x, container.y);
            if (andar) subirOuDescer(andar, time);

            // Sentar: pisar um assento e parar CHEGA — não há tecla nenhuma. Ao
            // sentar, o corpo encaixa no centro do lugar (senão ficava a cavalo
            // no braço do sofá); ao dar o primeiro passo, levanta-se sozinho.
            // E só se senta quem é DONO do lugar: o passo já não deixa entrar numa
            // cadeira tomada, mas uma posição pode chegar pela rede com o pé já
            // dentro — nesse instante fica-se de pé em cima dela, não sentado por
            // cima de alguém.
            const pisado = moving ? null : seatAt(container.x, container.y);
            const seat = pisado && seatOwnership.get(seatKey(pisado)) !== selfId ? null : pisado;
            if (setSeated(playerAv, seat) && seat) {
              const anchor = seatAnchor(seat);
              container.x = anchor.x;
              container.y = anchor.y;
            }
            stepAnim(playerAv, moving, delta);
            container.setDepth(playerAv.seat ? seatedDepth(playerAv.seat) : container.y);

            // Toda a gente que está no mundo — o avatar local mais os remotos.
            // Uma porta automática que só reagisse a NÓS seria um truque de
            // câmara: o colega chegava ao vão, a folha ficava fechada, e ele
            // atravessava-a à vista de todos (o vão é pisável; o "fechado" é só
            // desenho). Cada cliente calcula o mesmo a partir das mesmas
            // posições, por isso todos vêem a porta abrir para quem lá chega.
            bodies.length = 0;
            bodies.push(container);
            for (const remote of remotes.values()) bodies.push(remote.container);

            // Portas exteriores e escotilhas: abrem quando QUALQUER corpo se
            // aproxima do vão e fecham quando o último se afasta. `setExtDoorOpen`
            // só dispara o tween na transição (guarda de estado), por isso corre a
            // 60fps sem re-animar.
            for (const door of doors) {
              const near = bodies.some((body) => Math.hypot(body.x - door.cx, body.y - door.cy) < door.range);
              setExtDoorOpen(this, door, near);
            }

            // ── As portas de correr das salas, sobre a MESMA lista de corpos.
            for (const door of slidingDoors) {
              setDoorLocked(this, door, isLocked(door.roomId));
              const shouldOpen = doorShouldOpen(door, bodies);
              setSlideOpen(this, door, shouldOpen);
              // O cadeado só faz sentido com a porta fechada: aberta, não há folha
              // onde o pendurar. `setSignShown` guarda o estado (nada re-anima).
              setSignShown(this, door, door.locked && !shouldOpen);
            }

            // Mudou de sala? Dispara a call por cargo (só na transição, não a 60fps).
            // Pisar a soleira NÃO conta como entrar — quem manda aqui é `roomAt`.
            const nowRoom = roomAt(container.x, container.y);
            const nowRoomId = nowRoom?.id ?? null;
            if (nowRoomId !== currentRoomId) {
              currentRoomId = nowRoomId;
              onRoomChange.current(nowRoom);
            }

            // E o rótulo, à parte: muda ao passar a porta da rua, que não é uma
            // mudança de sala. Compara-se a string porque é ela o estado — dois
            // sítios com o mesmo nome são o mesmo sítio para quem lê a barra.
            const nowPlace = placeName(container.x, container.y);
            if (nowPlace !== currentPlace) {
              currentPlace = nowPlace;
              onPlaceChange.current(nowPlace);
            }

            // O ferrolho: mexe-se-lhe à mão, encostado à porta — incluindo em cima
            // da soleira, que é onde a mão lhe chega mesmo (`lockableRoomAt` é a
            // MESMA função que o gateway usa para validar, logo nunca há aviso que
            // o servidor recuse). O aviso vai ao React só na transição; a tecla E é
            // lida em JustDown, para um toque não alternar a tranca 60×/s.
            const doorRoom = lockableRoomAt(container.x, container.y);
            if (doorRoom !== doorRoomNow) {
              doorRoomNow = doorRoom;
              onDoorPrompt.current(doorRoom);
            }

            // Bater à porta é o SIMÉTRICO do ferrolho: só de fora, só numa sala
            // trancada. Nunca coincide com o aviso do ferrolho (esse exige estar
            // lá dentro), por isso não há prioridade a decidir entre os dois.
            const knockRoom = knockableRoomAt(container.x, container.y, isLocked);
            if (knockRoom !== knockRoomNow) {
              knockRoomNow = knockRoom;
              onKnockPrompt.current(knockRoom);
            }

            // O móvel ao alcance da mão. Compara-se pela POSIÇÃO e não pelo
            // objecto: `interactivePropAt` devolve um objecto novo a cada frame,
            // logo comparar por identidade avisava o React sessenta vezes por
            // segundo que "mudou" de móvel sem nada ter mudado.
            const propHere = interactivePropAt(container.x, container.y);
            const propId = propHere ? `${propHere.col},${propHere.row}` : null;
            if (propId !== propNow) {
              propNow = propId;
              onPropPrompt.current(propHere);
            }

            // A escotilha ao alcance dos pés. Mesma disciplina dos outros avisos:
            // compara-se pelo ID, e só se fala ao React quando ele muda.
            let hatchHere = teleportNear(container.x, container.y);
            // Saímos da boca onde aterrámos? Então ela volta a valer. (Basta um
            // tile fora — o patamar do bunker só tem uma porta, e é por ela que
            // se vai e se volta.)
            if (hatchHere?.id !== hatchLatch) hatchLatch = null;
            // *"só de chegar no topo ele já sai sozinho"*: a boca de subida salta
            // sem tecla — mas só a quem CHEGOU, nunca a quem acabou de aterrar.
            if (hatchHere?.auto && hatchHere.id !== hatchLatch) {
              jump(hatchHere);
              hatchHere = teleportNear(container.x, container.y);
            }
            // Avisa-se o que ainda espera pela tecla: a tampa do jardim sempre, e
            // o patamar enquanto está travado — quem desceu por engano tem ali o
            // caminho de volta sem ter de dar a volta ao vestíbulo.
            const hatchPrompt = hatchHere && (!hatchHere.auto || hatchHere.id === hatchLatch) ? hatchHere : null;
            if ((hatchPrompt?.id ?? null) !== hatchNow) {
              hatchNow = hatchPrompt?.id ?? null;
              onHatchPrompt.current(hatchPrompt);
            }

            // Uma tecla, quatro significados, por ordem do que está mais à mão:
            // o móvel, a escotilha aos pés, a porta a que se bate, o ferrolho.
            // Quem está encostado a um quadro branco quer o quadro, não a porta
            // atrás de si — e quem está EM CIMA da tampa quer descer, que é a
            // única coisa que ali há para fazer.
            if (keyE && !typing && Phaser.Input.Keyboard.JustDown(keyE)) {
              if (propHere) onPropUse.current(propHere);
              else if (hatchHere) jump(hatchHere);
              else if (knockRoom) gatewayClient()?.knock(serverId, knockRoom);
              else if (doorRoom) gatewayClient()?.lockRoom(serverId, doorRoom, !isLocked(doorRoom));
            }

            // T de texto — falar sem microfone, para quem o não tem ligado.
            if (keyT && !typing && Phaser.Input.Keyboard.JustDown(keyT)) onSay.current();
            if (keyF && !typing && Phaser.Input.Keyboard.JustDown(keyF)) usarBola(null);

            // Zoom pelo teclado. `!typing` como todas as outras: quem está a
            // escrever "+50%" no balão não quer a câmara a mexer-se.
            if (!typing) {
              if (keyZoomIn && Phaser.Input.Keyboard.JustDown(keyZoomIn)) applyZoom(zoomTarget * ZOOM_STEP);
              if (keyZoomOut && Phaser.Input.Keyboard.JustDown(keyZoomOut)) applyZoom(zoomTarget / ZOOM_STEP);
              if (keyZoomReset && Phaser.Input.Keyboard.JustDown(keyZoomReset)) applyZoom(CAMERA_ZOOM);
            }

            // Onde estamos, para quem vive fora do loop: é daqui que a re-entrada
            // tira a posição a anunciar quando a ligação volta, para reaparecermos
            // onde estamos e não no spawn.
            posRef.current.x = container.x;
            posRef.current.y = container.y;
            posRef.current.facing = playerAv.dir;
            rotaRef.current = path ? { path, idx: pathIdx } : null;

            // Envia a 10Hz, e só quando algo mudou de facto: um cliente parado
            // não gasta um pacote por frame.
            const changed =
              Math.round(container.x) !== Math.round(lastX) ||
              Math.round(container.y) !== Math.round(lastY) ||
              playerAv.dir !== lastFacing;
            if (changed && time - lastSentAt >= POSITION_SYNC_MS) {
              gatewayClient()?.updatePosition(serverId, Math.round(container.x), Math.round(container.y), playerAv.dir);
              lastSentAt = time;
              lastX = container.x;
              lastY = container.y;
              lastFacing = playerAv.dir;
            }

            vistaDoAndar(time);

            if (scene) reconcileRemotes(scene, serverId, remotes, delta, SEATING);
            if (scene) helicopteroDaCena(scene)?.update(time, delta, ceoPresente(useStore.getState(), serverId));
            if (scene) {
              const pes = [{ id: selfId, x: container.x, y: container.y }];
              for (const [id, r] of remotes) pes.push({ id, x: r.container.x, y: r.container.y });
              const clima = climaDaCena(scene);
              clima?.update(time, delta, pes);
              vidaDaCena(scene)?.update(time, pes);
              const bolas = bolasDaCena(scene);
              if (bolas && playerAv) {
                const neve = clima?.neveNoChao ?? 0;
                if (neve < 0.05) bolas.semNeve();
                const pos = new Map(pes.map((p) => [p.id, p]));
                bolas.update(time, {
                  eu: { x: container.x, y: container.y, dir: playerAv.dir },
                  podeApanhar: neve > 0.35 && aoAr(Math.floor(container.x / TILE), Math.floor(container.y / TILE)),
                  posicao: (id) => pos.get(id) ?? null,
                  bolas: useStore.getState().spaceBolas[serverId] ?? [],
                });
              }
            }

            // ── A camada social, uma passagem por frame, DEPOIS de os remotos
            // já estarem na posição deste frame. Tudo o que se segue lê o store
            // imperativamente e escreve nos objectos do Phaser: são coisas que
            // ou acompanham pés a 60fps (círculos, balões) ou expiram sozinhas
            // (os balões). Pelo React seria um render por frame.
            const now = Date.now();
            const store = useStore.getState();

            // Os MEUS selos e o MEU tremor: o `reconcileRemotes` trata dos outros
            // e não me conhece — o avatar local nunca entra no `remotes`. O meu
            // estado de presença vem do `self`, que é onde ele se escreve primeiro.
            if (scene) {
              const mine = store.users[selfId]?.status ?? store.self?.status;
              // O meu microfone vem do MOTOR, não do eco do servidor: num huddle
              // de corredor não existe `voiceState` nenhum (a API não o cria), e
              // sem isto o meu boneco era o único do mapa a nunca aparecer mudo.
              setMarks(scene, playerAv, marksFor(ownVoiceMarks(store.voiceStates[selfId], engineVoice()), mine));
            }
            playerAv.sprite.setX(pokeShake(store.spacePokes[serverId]?.[selfId]?.at, now));

            /** Onde estão os pés de alguém — eu ou um remoto. */
            const feetOf = (userId: string): { x: number; y: number } | null => {
              if (userId === selfId) return { x: container.x, y: container.y };
              const r = remotes.get(userId);
              return r ? { x: r.container.x, y: r.container.y } : null;
            };

            // Conversa de corredor: o GRAFO a 5Hz (é O(n²) e aloca), os CÍRCULOS a
            // cada frame a partir dos membros já conhecidos — senão o desenho
            // ficava 200ms atrás dos pés e via-se a arrastar.
            huddleTick -= delta;
            if (huddleTick <= 0) {
              huddleTick = HUDDLE_SCAN_MS;
              huddleBodies.length = 0;
              huddleBodies.push({ userId: selfId, x: container.x, y: container.y });
              for (const [id, r] of remotes) huddleBodies.push({ userId: id, x: r.container.x, y: r.container.y });
              huddleCache = huddleGroups(huddleBodies);
              onHuddle.current(huddleOf(huddleCache, selfId)?.id ?? null);
            }

            rings.length = 0;
            for (const group of huddleCache) {
              let sx = 0;
              let sy = 0;
              let n = 0;
              let mine = false;
              for (const id of group.memberIds) {
                const at = feetOf(id);
                if (!at) continue;
                sx += at.x;
                sy += at.y;
                n += 1;
                if (id === selfId) mine = true;
              }
              if (n < 2) continue;
              const cx = sx / n;
              const cy = sy / n;
              let spread = 0;
              for (const id of group.memberIds) {
                const at = feetOf(id);
                if (!at) continue;
                spread = Math.max(spread, Math.hypot(at.x - cx, at.y - cy));
              }
              rings.push({ id: group.id, x: cx, y: cy, spread, mine });
            }
            huddleLayer?.render(rings, now);

            // Balões de texto. O TTL é comparado AQUI e não no store porque é o
            // desenho que o precisa — ninguém varre `spaceSays`, os balões velhos
            // simplesmente deixam de ser desenhados. Só se lê o que está perto: um
            // balão do outro lado do mapa não se leria de qualquer maneira.
            sayItems.length = 0;
            const says = store.spaceSays[serverId];
            if (says) {
              for (const [userId, said] of Object.entries(says)) {
                if (now - said.at >= SAY_TTL_MS) continue;
                const at = feetOf(userId);
                if (!at) continue;
                if (Math.hypot(at.x - container.x, at.y - container.y) > SAY_RANGE) continue;
                sayItems.push({ id: userId, x: at.x, y: at.y, text: said.text, at: said.at });
              }
            }
            sayLayer?.render(sayItems, now);

            // O letreiro da televisão: quem estiver a partilhar o ecrã neste
            // espaço acende-a. É a única funcionalidade do escritório que se lê
            // sem carregar em nada — vê-se ao passar, que é o objectivo.
            const presenting = Object.values(store.voiceStates).find((v) => v.serverId === serverId && v.screenSharing);
            const channelName = presenting
              ? (channelsOf(store, serverId).find((c) => c.id === presenting.channelId)?.name ?? "apresentação")
              : null;
            tvBadge?.render(channelName, now);
          },
        },
      };

      game = new Phaser.Game(config);
    })();

    return () => {
      disposed = true;
      // Guardamos onde ficámos ANTES de sair: abrir o chat desmonta isto, e quem
      // volta tem de reaparecer aqui e não na Recepção. `posRef.current` traz a
      // última posição escrita pelo loop (ou a de entrada, se nunca se andou).
      lembrarCorpo(serverId, posRef.current);
      // Sair do mapa avisa o servidor (despawn) antes de deitar abaixo o jogo — a
      // ordem inversa deixaria o socket a mandar posições de um mundo já
      // destruído. Mas SÓ quando ninguém fica a segurar o corpo.
      //
      // Carregar em «Discord» a meio de uma call na TI desmonta isto exactamente
      // como trocar de servidor, e o despejo do mapa era o *"vou pro discord e o
      // meu personagem some do escritório"*: continuo na sala, a falar com quem
      // lá está, e para eles deixei de existir. Com um agravante que não se vê no
      // ecrã — o `leaveSpace` do servidor LIBERTA a tranca da sala quando sai o
      // último ocupante, portanto ir ao chat abria a porta de uma reunião que
      // continua a decorrer.
      //
      // Quem fica com o corpo a seguir é o `useOfficePresence`, e é ele que o
      // tira do mapa quando a call acabar.
      const estado = useStore.getState();
      const fica = corpoNoMapa({
        mesmoServidor: estado.activeServerId === serverId,
        // Se a vista ainda é "office" o escritório vai REMONTAR (uma troca de
        // servidor) e o corpo volta a ser dele; carregar em «Discord» já pôs
        // isto a "chat" antes de o cleanup correr.
        escritorio: estado.viewMode === "office",
        // Este componente nunca monta em telemóvel (`AppShell`: `!phone`).
        phone: false,
        salaDaCall: presenceRoomId(estado.activeServerId, estado.voice, estado.channels),
      });
      if (!fica) gatewayClient()?.leaveSpace(serverId);
      // Fechar o escritório também sai da call por cargo — mas só se foi ELE a
      // abri-la; uma call juntada pela vista de chat continua a tocar.
      //
      // E só se o escritório fechou MESMO. Carregar em «Discord» desmonta isto
      // exactamente da mesma maneira, e isso não é sair da sala: o avatar fica
      // lá dentro, a posição fica guardada duas linhas acima, e desligar-lhe a
      // call era pô-lo mudo numa sala onde ele continua a estar — que é o
      // *"clico no chat, volto, e já não estou na call"*. Continuamos neste
      // servidor ⇒ a vista mudou, a sala não; a call fica e a propriedade dela
      // também (o mapa vive fora do React de propósito). Trocar de servidor ou
      // sair da conta é que é fechar o escritório, e aí sim desliga-se.
      const fecho = planoDeFecho(
        callDaSala(),
        useStore.getState().voice?.channelId ?? null,
        useStore.getState().activeServerId === serverId,
      );
      marcarCallDaSala(fecho.dona);
      if (fecho.desligar) void voiceRef.current.leave();
      // A conversa de corredor não passa pelo `voice` do store (não é um canal do
      // servidor, é uma sala efémera do SFU), por isso tem de ser desligada à mão
      // — sair do escritório é afastar-se de toda a gente.
      if (huddleAnchorRef.current !== null) {
        huddleAnchorRef.current = null;
        huddleGenRef.current += 1;
        setHuddleOn(false);
        voiceEngine.disconnect();
      }
      // Trocar de servidor recomeça a vista da call do zero: encolhida a um canto
      // e visível. O `callId` já não se limpa aqui — é derivado da sessão de voz,
      // que compara o `serverId`, por isso a call do servidor anterior desaparece
      // sozinha do ecrã sem ninguém a apagar.
      setCallMin(true);
      setCallShown(true);
      setDoorRoomId(null);
      if (releaseHeldKeys) {
        window.removeEventListener("contextmenu", releaseHeldKeys);
        window.removeEventListener("blur", releaseHeldKeys);
      }
      game?.destroy(true);
    };
  }, [serverId]);

  return (
    <section className={`office${toque ? " office--toque" : ""}`}>
      {/* O escritório já não tem cabeçalho próprio: o que era dele passou a ser
          pendurado na barra fixa lá de cima (ver `TopBar`). O nome do servidor
          era duplicado e ficou lá; o resto — a sala onde o avatar está, "quem
          está onde", o estado da chamada e a miniatura — vem por portal, com o
          estado a ficar exactamente onde estava. Levantá-lo para a store para
          poder ser desenhado noutro sítio seria transformar um detalhe de
          desenho em estado global: o `callMin` daqui manda no `teclasOcupadas`,
          e o WASD não pertence a ninguém fora deste componente. */}
      {slotDaBarra
        ? createPortal(
            <>
              {room ? (
                <span className={`office__room${lockedBy ? " office__room--locked" : ""}`}>
                  {lockedBy ? <IconLock size={12} /> : null}
                  {room.name}
                  {lockedBy ? <span className="office__lockwho">trancada por {lockedByName}</span> : null}
                </span>
              ) : (
                <span className="office__room office__room--hall">{place}</span>
              )}
              <div className="office__headright">
                {/* "Quem está onde" — a planta em lista. Serve para o gesto que o
                    mapa sozinho não dá: ver a empresa toda de uma vez e ir ter
                    com alguém. */}
                <button
                  type="button"
                  className={`office__gv${whoOpen ? " is-on" : ""}`}
                  onClick={() => setWhoOpen((v) => !v)}
                  title="Quem está onde"
                >
                  <IconMembers size={15} />
                  <span>Quem está onde</span>
                </button>
                {/* O `voiceSession` sozinho não chega para acender isto: é a
                    INTENÇÃO, e ela sobrevive ao motor: a sessão fica pendurada a
                    apontar para o canal com a ligação já em `idle`. Era o
                    *"mostra que estou na call ali no botão de quem está onde,
                    porém não estou"* — um sinal de chamada aceso ao lado da
                    lista, por uma chamada que não existe. `failed` continua à
                    vista de propósito: aí houve mesmo uma tentativa e a pessoa
                    tem de saber que ela morreu; `idle` é motor parado, e motor
                    parado é chamada nenhuma. */}
                {/* …e some quando a chamada está em ECRÃ CHEIO: um letreiro a
                    dizer "Em chamada" por cima da chamada que se está a ver é
                    ruído. Enquanto o mundo se vê, ele é a única prova de que a
                    voz continua a andar algures. */}
                {voiceSession && voiceSnapshot.state !== "idle" && !callEmEcraCheio ? (
                  <span className={`office__call${voiceSnapshot.state === "connected" ? " office__call--live" : ""}`}>
                    {/* "Em chamada" é a palavra deste sítio; o resto vem da regra única. */}
                    {voiceSnapshot.state === "connected" ? "Em chamada" : connectionLabel(voiceSnapshot.state).text}
                  </span>
                ) : null}
                {/* Chamar a miniatura de volta.
                    Sem isto, fechar o cartão era uma porta só de saída: o × tapava-se
                    e a chamada continuava viva algures, sem nada no ecrã a dizer por
                    onde se voltava lá. Fica ao lado do sinal "Em chamada" porque é
                    sobre a MESMA chamada — e só existe enquanto ela existir; um botão
                    que não tem miniatura nenhuma para abrir só ocuparia a barra. */}
                {/* *"o botão de miniatura só vai aparecer quando estiver no
                    escritório"* — e é a mesma regra do letreiro: em ecrã cheio
                    não há miniatura nenhuma para chamar, há uma chamada a tapar
                    o mundo. A porta de volta ali é a da barra («Escritório»). */}
                {callId && !callEmEcraCheio ? (
                  <button
                    type="button"
                    className={`office__gv${callShown ? " is-on" : ""}`}
                    onClick={() => {
                      // Reabrir traz sempre o cartão de canto, nunca o ecrã inteiro: o
                      // mundo 2D é a vista dona e quem carrega aqui quer a chamada à
                      // vista, não por cima de tudo.
                      setCallShown((v) => !v);
                      setCallMin(true);
                    }}
                    title={callShown ? "Esconder a miniatura da chamada" : "Mostrar a miniatura da chamada"}
                  >
                    <IconShrink size={15} />
                    <span>Miniatura</span>
                  </button>
                ) : null}
              </div>
            </>,
            slotDaBarra,
          )
        : null}
      <div className="office__stagewrap">
        {/* O Phaser é DONO deste div (anexa-lhe o canvas): fica sem filhos React
            de propósito, senão o reconciliador removia-lhe o canvas num render.
            O ecrã da call é IRMÃO, sobreposto pelo wrapper posicionado. */}
        <div className="office__stage" ref={mountRef} />
        {/* A mesa de acções de uma PESSOA. É o cartão de perfil de sempre — o
            mesmo do chat e da lista de membros — ancorado ao ponto do ecrã onde
            se carregou, porque um avatar numa tela não é um elemento do DOM a
            que agarrar. Ganha aqui o que só aqui faz sentido (chamar a atenção)
            sem que o escritório tenha um menu próprio a divergir do outro. */}
        {pickedUser && pickedAnchor ? <UserCard user={pickedUser} anchor={pickedAnchor} onClose={closePicked} /> : null}
        {/* Os avisos do sítio onde se está de pé, numa COLUNA só, à direita do
            minimapa e empilhada de baixo para cima. Eram caixas soltas, cada uma a
            uma altura fixa — e bastava a dica do ferrolho ter duas linhas para o
            aviso do móvel lhe cair em cima. Numa coluna não há alturas a acertar. */}
        <div className={`office__acoes${callEmEcraCheio ? " is-escondido" : ""}`}>
          {/* O ferrolho. Só aparece com o avatar encostado a uma porta — é uma porta,
            não um menu: de longe não há nada para carregar. Aparece também em cima
            da soleira, que é onde a mão chega mesmo ao ferrolho. O botão é o mesmo
            gesto para quem anda de rato; a tecla E faz o mesmo pedido. */}
          {doorRoomId ? (
            <div className="office__door">
              <button type="button" className="office__doorbtn" onClick={toggleLock} disabled={!linked}>
                <kbd>E</kbd>
                <IconLock size={14} />
                <span>{doorLocked ? "Destrancar" : "Trancar"}</span>
                <span className="office__doorroom">{doorRoomName}</span>
              </button>
              {/* Quem manda no ferrolho é o servidor. Sem ligação, o pedido não vai a
                lado nenhum — e mais vale dizê-lo do que deixar a tecla E parecer
                estragada. */}
              <span className="office__doorhint">
                {!linked
                  ? "Sem ligação ao servidor — a tranca volta assim que ligar."
                  : doorLocked
                    ? "A porta fica fechada a quem vem de fora — quem está dentro sai sempre."
                    : "A porta abre sozinha a quem chega. Tranca-a e deixa de abrir."}
              </span>
            </div>
          ) : null}
          {/* Bater à porta: o simétrico do ferrolho, do lado de fora. Só aparece
            numa porta TRANCADA que não seja a da sala onde estamos. */}
          {knockRoomId ? (
            <div className="office__door">
              <button
                type="button"
                className="office__doorbtn"
                onClick={() => gatewayClient()?.knock(serverId, knockRoomId)}
                disabled={!linked}
              >
                <kbd>E</kbd>
                <IconLock size={14} />
                <span>Bater à porta</span>
                <span className="office__doorroom">{ROOMS.find((r) => r.id === knockRoomId)?.name ?? ""}</span>
              </button>
              <span className="office__doorhint">
                {linked
                  ? "Quem estiver lá dentro vê quem bateu e decide se abre."
                  : "Sem ligação ao servidor — ninguém ouve a batida."}
              </span>
            </div>
          ) : null}
          {/* O móvel ao alcance da mão. Mesmo gesto do ferrolho: aparece por se
            estar em cima dele, some-se por se dar dois passos. */}
          {/* A bola de neve: só quando há neve debaixo dos pés, ou uma bola na mão. */}
          {bola !== "sem_neve" ? (
            <div className="office__door office__door--bola">
              <button
                type="button"
                className="office__doorbtn"
                disabled={bola === "a_apanhar"}
                onClick={() => bolaAcao.current()}
              >
                <kbd>F</kbd>
                <span>
                  {bola === "na_mao"
                    ? "Atirar a bola de neve"
                    : bola === "a_apanhar"
                      ? "A fazer a bola…"
                      : "Apanhar neve"}
                </span>
              </button>
              {bola === "na_mao" ? (
                <span className="office__doorhint">
                  {toque
                    ? "Ou toca em alguém (ou num sítio) para atirar."
                    : "Ou clica em alguém (ou num sítio) para atirar."}
                </span>
              ) : null}
            </div>
          ) : null}
          {prop ? (
            <div className="office__door office__door--prop">
              <button type="button" className="office__doorbtn" onClick={() => onPropUse.current(prop)}>
                <kbd>E</kbd>
                <span>{PROP_LABELS[prop.action]}</span>
                <span className="office__doorroom">{ROOMS.find((r) => r.id === prop.roomId)?.name ?? ""}</span>
              </button>
            </div>
          ) : null}
          {/* A escotilha. O aviso É a correcção: enquanto o salto era pisado não
            havia nada a mostrar — caía-se sem aviso em cima, e em baixo não havia
            sinal nenhum de que dali se saía. Agora chega-se perto, lê-se para onde
            se vai, e só se salta a pedido. */}
          {hatch ? (
            <div className="office__door office__door--hatch">
              <button type="button" className="office__doorbtn" onClick={() => useHatch.current(hatch)}>
                <kbd>E</kbd>
                <span>{hatch.label}</span>
              </button>
            </div>
          ) : null}
          {huddleOn ? <HuddleBar /> : null}
        </div>
        {/* O zoom. A roda do rato e as teclas +/−/0 fazem o mesmo — isto é o
            gesto VISÍVEL, para quem não adivinha atalhos. Afastar até ao fim
            mostra o escritório todo; o número no meio repõe a escala de origem. */}
        <div className="office__zoom">
          <button
            type="button"
            className="office__zoombtn"
            onClick={() => zoomCtl.current(zoom / ZOOM_STEP)}
            disabled={zoom <= ZOOM_MIN + 0.001}
            title="Afastar (tecla −)"
            aria-label="Afastar a câmara"
          >
            −
          </button>
          <button
            type="button"
            className="office__zoomlevel"
            onClick={() => zoomCtl.current(CAMERA_ZOOM)}
            title="Repor o zoom (tecla 0)"
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            type="button"
            className="office__zoombtn"
            onClick={() => zoomCtl.current(zoom * ZOOM_STEP)}
            disabled={zoom >= ZOOM_MAX - 0.001}
            title="Aproximar (tecla +)"
            aria-label="Aproximar a câmara"
          >
            +
          </button>
        </div>
        {/* O minimapa (estilo GTA). Some com a chamada em ecrã cheio, que tapa o
            mundo — um mapa do que não se vê é só ruído. */}
        {callId !== null && !callMin ? null : (
          <OfficeMinimap
            serverId={serverId}
            posRef={posRef}
            rotaRef={rotaRef}
            aberto={mapaAberto}
            ocupado={sayOpen || arcadeOpen || boardOpen || pontoOpen}
            onAbrir={setMapaAberto}
            onIrPara={(x, y) => irPara.current(x, y)}
            onIrTerCom={(userId) => goTo.current(userId)}
          />
        )}
        {/* Quem bateu à porta da sala onde estamos (e o estado das nossas batidas).
            O espaço do escritório É o servidor nu, por isso passa-se `serverId` —
            como no `SayComposer` mais abaixo, o que muda é o nome do argumento. */}
        <KnockPanel spaceId={serverId} insideRoomId={room?.id ?? null} />
        {whoOpen ? (
          <WhoPanel
            serverId={serverId}
            selfRoomId={room?.id ?? null}
            onGoTo={(userId) => {
              goTo.current(userId);
              setWhoOpen(false);
            }}
            onClose={() => setWhoOpen(false)}
          />
        ) : null}
        {/* O espaço do escritório É o servidor nu, por isso passa-se `serverId` —
            o nome do argumento é que é outro, não o valor. */}
        {sayOpen ? <OfficeChat spaceId={serverId} onClose={fecharChat} /> : null}
        {toque &&
        !callEmEcraCheio &&
        !sayOpen &&
        !mapaAberto &&
        !boardOpen &&
        !gameOpen &&
        !arcadeOpen &&
        !pontoOpen &&
        !whoOpen ? (
          <OfficeTouchControls
            direccaoRef={direccaoToque}
            onChat={() => setSayOpen(true)}
            onMapa={() => setMapaAberto(true)}
          />
        ) : null}
        {/* `callShown` esconde o CARTÃO, não desliga a chamada — quem carrega no ×
            quer o mapa todo, não sair da sala. A voz continua, o botão "Miniatura"
            do cabeçalho fica aceso ao contrário, e traz o cartão de volta. */}
        {callId && callShown ? (
          <div
            ref={callScreenRef}
            className={`office__callscreen${callMin ? " office__callscreen--min" : ""}${
              callMin && callRect ? " office__callscreen--float" : ""
            }`}
            style={
              callMin && callRect
                ? { left: callRect.x, top: callRect.y, width: callRect.w, height: callRect.h }
                : undefined
            }
          >
            {/* A coluna das salas volta ao ecrã cheio da chamada. Dentro do
                escritório o `AppShell` só monta a rail e o mundo — a barra de
                canais fica de fora, e enquanto o mundo se vê isso está certo
                (o mundo É a navegação: anda-se até à sala). Mas o ecrã cheio da
                chamada TAPA o mundo, e aí ficava-se sem as duas: nem mundo, nem
                lista. Para saber quem está onde, ou para mudar de sala, era
                preciso sair da chamada.

                É a MESMA `ChannelSidebar` do modo chat — não uma cópia. Ela já
                lista cada sala de voz com quem lá está: avatar, nome e os sinais
                de silenciado / a partilhar ecrã / com câmara, que é exactamente
                o que o Discord mostra. Não havia nada a construir, só a montar.

                Só no ecrã cheio: no cartão de canto não há largura para uma
                coluna, e lá o mundo continua visível por trás. */}
            {!callMin ? (
              <aside className={`office__callnav${navMini ? " nav-mini" : ""}`}>
                <ChannelSidebar />
                <VoiceBar />
                <UserPanel />
              </aside>
            ) : null}
            <div className="office__callmain">
              {/* O estado da ligação, VISÍVEL: sem isto, uma call que não liga (sem
                API/SFU a correr) mostra só botões cinzentos e parece partida. O
                dot muda de cor e a etiqueta diz porquê os controlos estão inertes.
                "connecting" = a ligar, "connected" = pronto, `error` = sem SFU. */}
              {callMin
                ? (() => {
                    // Um estado por cada coisa que pode estar a acontecer ao MEDIA —
                    // "a recuperar" não é "ligado" nem é "sem ligação", e é justamente
                    // durante essa recuperação que os controlos estão inertes.
                    const { tone: cs, text: label } = connectionLabel(voiceSnapshot.state);
                    return (
                      <div className="office__callbar" data-callstate={cs} onPointerDown={beginCallDrag("move")}>
                        <span className="office__calltag" title={voiceSnapshot.error ?? voiceSession?.error ?? label}>
                          <span className="office__calldot" />
                          {room?.name ?? "Chamada"}
                          <span className="office__callstate">{label}</span>
                        </span>
                        <button type="button" className="office__callbtn" onClick={() => setCallMin(false)}>
                          Expandir
                        </button>
                        {/* Arrumar o cartão SEM desligar. Era o que faltava para o
                          botão do cabeçalho ter sentido — e o que faltava a quem
                          só queria ver o canto do mapa que o cartão tapava. */}
                        <button
                          type="button"
                          className="office__callbtn office__callbtn--x"
                          onClick={() => setCallShown(false)}
                          title="Esconder a miniatura (a chamada continua)"
                          aria-label="Esconder a miniatura"
                        >
                          <IconClose size={13} />
                        </button>
                      </div>
                    );
                  })()
                : /*
                   * Em ECRÃ CHEIO não há barra NEM botões — o pedido foi *"vamos
                   * tirar esse header (…) pois queremos deixar a tela maior"*, e
                   * depois *"retire o botão de reduzir e do x, pois é só a pessoa
                   * clicar em escritório novamente que ela volta pra lá"*. O palco
                   * fica com o ecrã todo, do topo ao rodapé.
                   *
                   * Nada disto se perdeu, mudou de dono:
                   *  - VOLTAR ao mundo é a porta da barra de cima, que enquanto a
                   *    chamada tapa o escritório passa a dizer «Escritório» (é o
                   *    `officeCallFull` que lho diz). Uma porta só, sempre no mesmo
                   *    pixel, em vez de duas a fazer o mesmo lado a lado;
                   *  - ESCONDER a chamada continua no «Miniatura» da mesma barra;
                   *  - o NOME da sala e o estado da ligação estão na coluna da
                   *    esquerda (`VoiceBar`), que só existe neste modo; e uma
                   *    chamada que falha avisa pelo `stage__error` do próprio palco.
                   *
                   * O cabeçalho, em ecrã cheio, era ainda por cima uma pega que não
                   * pegava: o `beginCallDrag` devolve cedo quando não está minimizado.
                   */
                  null}
              <div className="office__callbody">
                {/* PIP: aqui o teclado é do jogo (WASD anda). Sem os atalhos de
                    tecla crua do VoiceStage — o `D` de andar à direita ensurdecia
                    a chamada e o micro/phones ligavam-se sozinhos a cada passo. */}
                <VoiceStage channelId={callId} shortcuts={false} toolsInBar={!callMin} />
              </div>
            </div>
            {/* Canto de redimensionar — só no cartão minimizado (em ecrã cheio o
                PIP enche a área e não faz sentido). */}
            {callMin ? (
              <div className="office__callresize" onPointerDown={beginCallDrag("resize")} title="Redimensionar" />
            ) : null}
          </div>
        ) : null}
        {/* Estou na sala, fora da call dela: a porta de volta.
            Mora no canto da miniatura porque é o canto da chamada — e sobe por
            cima dela (`--acima`) quando o cartão está lá ancorado, que é o caso
            de já se estar NOUTRA call. Arrastado para outro sítio, o cartão
            deixa o canto livre e o botão fica onde sempre esteve. */}
        {/* Numa ligação privada não se oferece a call da sala: entrar nela cortava a
            ligação a meio (o motor de voz só tem uma sala de cada vez). */}
        {foraDaCallDaSala &&
        salaVoz !== null &&
        !ligacaoEmCurso &&
        !callEmEcraCheio &&
        !(toque && (whoOpen || sayOpen)) ? (
          <button
            type="button"
            className={`office__joincall${callId && callShown && callMin && !callRect ? " office__joincall--acima" : ""}`}
            onClick={() => {
              // Marcar ANTES de entrar: é isto que faz a call ser do escritório,
              // e é a propriedade que depois a desliga ao sair para o corredor.
              marcarCallDaSala(salaVoz);
              void voiceActions.join(salaVoz);
            }}
            title={room ? `Entrar na chamada de ${room.name}` : "Entrar na chamada desta sala"}
          >
            <IconHeadphones size={15} />
            <span>Participar da chamada de voz</span>
          </button>
        ) : null}
      </div>
      {/* O quadro branco fica MONTADO depois da primeira abertura: fechá-lo não
          pode apagar o que lá está desenhado, e é ele que continua a receber os
          traços dos outros enquanto está escondido. */}
      {boardMounted ? (
        <Whiteboard open={boardOpen} active={voiceSnapshot.state === "connected"} onClose={() => setBoardOpen(false)} />
      ) : null}
      {/* A mesa fica montada pela mesma razão, ainda mais forte: escondida
          continua a seguir as jogadas, por isso fechar o painel é levantar os
          olhos do tabuleiro — não é abandonar a partida. */}
      {gameMounted ? (
        <GameTable open={gameOpen} active={voiceSnapshot.state === "connected"} onClose={() => setGameOpen(false)} />
      ) : null}
      {/* E a arcade por fim a mesma razão: a corrida continua a andar sem o
          painel aberto, e é este componente que responde a quem chega à fila. */}
      {arcadeMounted ? (
        <Arcade open={arcadeOpen} active={voiceSnapshot.state === "connected"} onClose={() => setArcadeOpen(false)} />
      ) : null}
      {pontoOpen ? <PontoModal onClose={() => setPontoOpen(false)} /> : null}
      {/*
       * A ajuda dos comandos existe para quem está a ANDAR pelo mundo, e por
       * isso vai-se embora quando o mundo não se vê: em ecrã cheio da chamada
       * ela ficava por baixo do GreenVoice a ensinar WASD a quem não tem para
       * onde andar — *"quando estiver no modo discord, retire essa parte;
       * apenas vai aparecer quando estiver no office 2d 100%"*. Custava uma
       * faixa inteira ao palco em troca de instruções para o que está tapado.
       *
       * Com o cartão de canto (`callMin`) ela FICA: aí o mundo continua à vista
       * e continua a andar-se nele, que é exactamente quando a ajuda serve.
       *
       * A seta fica SEMPRE no mesmo canto e só roda: arrumada, a barra encolhe
       * à altura dela e continua a ser o que se carrega para a trazer de volta.
       * Uma pega que desaparece com o que arrumou obriga a adivinhar onde ela
       * foi parar — e ninguém adivinha duas vezes.
       */}
      {callEmEcraCheio ? null : (
        <footer className={`office__hint${hintHidden ? " office__hint--fechada" : ""}`}>
          <span hidden={hintHidden || !toque}>
            Arrasta o círculo para andar, ou toca no chão para ir até lá. Toca nos avisos para usar portas e objetos, e
            junta dois dedos para o zoom.
          </span>
          <span hidden={hintHidden || toque}>
            Anda com WASD ou as setas. Entra na sala do teu cargo — o ecrã da chamada da equipa abre num canto, por cima
            do mundo. Junto à porta, <kbd>E</kbd> tranca a sala por dentro (ou bate, se estiver trancada); em cima de um
            quadro, relógio de ponto ou jogo, <kbd>E</kbd> usa-o. <kbd>T</kbd> abre o chat do escritório. Com neve,{" "}
            <kbd>F</kbd> apanha e atira bolas de neve.
          </span>
          <button
            type="button"
            className="office__hintbtn"
            onClick={(e) => {
              const proximo = !hintHidden;
              setHintHidden(proximo);
              saveOfficePrefs({ hintHidden: proximo });
              // Devolver o teclado ao mundo: com o foco preso no botão, o Enter
              // seguinte tornava a abrir a barra em vez de chegar ao jogo.
              e.currentTarget.blur();
            }}
            title={hintHidden ? "Mostrar a ajuda dos comandos" : "Arrumar a ajuda dos comandos"}
            aria-label={hintHidden ? "Mostrar a ajuda dos comandos" : "Arrumar a ajuda dos comandos"}
            aria-expanded={!hintHidden}
          >
            <IconChevron size={14} />
          </button>
        </footer>
      )}
    </section>
  );
}
